import React, { useState, useEffect, useRef, useMemo, useDeferredValue } from 'react';
import { CameraPreview } from '@capacitor-community/camera-preview';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import initialCustomersData from './data/customersData.json';
import {
  Building2, Users, MapPin, Camera, RefreshCw, LogOut, ShieldCheck,
  Search, CheckCircle2, AlertTriangle, AlertCircle, Menu, FileText, Download, User, Lock,
  Plus, PlusCircle, Trash2, ChevronRight, ArrowLeft, Send, Check, Eye, EyeOff,
  Navigation, ExternalLink, Compass, X, CloudUpload, XCircle, Zap, Upload, Crosshair, Volume2,
  LayoutDashboard, UserCheck, FileSpreadsheet, CheckSquare, BarChart3, ToggleLeft, ToggleRight, Key, Filter, FileCode, ArrowUpDown, Play, Clock, TrendingUp, ChevronDown, Sun, Moon, Info, Edit, Save
} from 'lucide-react';

const compressBase64 = (base64Str, maxWidth = 1400, quality = 0.80) => {
  return new Promise((resolve) => {
    if (!base64Str) return resolve(base64Str);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;
      if (w > maxWidth) {
        h = Math.round((h * maxWidth) / w);
        w = maxWidth;
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(base64Str);
    img.src = base64Str.startsWith('data:') ? base64Str : `data:image/jpeg;base64,${base64Str}`;
  });
};

const compressImage = async (file, maxWidth = 1400, quality = 0.80) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        if (w > maxWidth) {
          h = Math.round((h * maxWidth) / w);
          w = maxWidth;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name || "survey.jpg", { type: 'image/jpeg' }));
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = (error) => reject(error);
  });
};



function distributeCustomersEvenlyByUlp(customersList, usersList) {
    if (!Array.isArray(customersList) || customersList.length === 0) return customersList;

    const surveyors = Array.isArray(usersList)
        ? usersList.filter(u => u && u.role === 'surveyor' && u.status !== 'nonaktif')
        : [];

    const getUnitKey = (c) => {
        const u = String(c.ulp || c.kab || c.desa || c.kec || '').toLowerCase();
        if (u.includes('dobo') || u.includes('aru')) return 'dobo';
        if (u.includes('elat') || u.includes('kei besar')) return 'elat';
        return 'tual';
    };

    const officersByUnit = {
        dobo: surveyors.filter(s => {
            if (!s) return false;
            const u = String(s.ulp || '').toLowerCase();
            const usr = String(s.username || '').toLowerCase();
            return u.includes('dobo') || u.includes('aru') || usr.includes('dobo');
        }),
        elat: surveyors.filter(s => {
            if (!s) return false;
            const u = String(s.ulp || '').toLowerCase();
            const usr = String(s.username || '').toLowerCase();
            return u.includes('elat') || u.includes('kei besar') || usr.includes('elat');
        }),
        tual: surveyors.filter(s => {
            if (!s) return false;
            const u = String(s.ulp || '').toLowerCase();
            const usr = String(s.username || '').toLowerCase();
            return u.includes('tual') || usr === 'petugas1' || usr === 'petugas2';
        })
    };

    if (officersByUnit.dobo.length === 0) officersByUnit.dobo = [{ username: 'petugas_dobo' }, { username: 'petugas_dobo2' }];
    if (officersByUnit.elat.length === 0) officersByUnit.elat = [{ username: 'petugas_elat' }];
    if (officersByUnit.tual.length === 0) officersByUnit.tual = [{ username: 'petugas1' }, { username: 'petugas2' }];

    const roundRobinIndex = { dobo: 0, elat: 0, tual: 0 };

    return customersList.map(c => {
        if (!c) return c;

        // EVENLY REDISTRIBUTE ALL PENDING TOs FOR ALL PETUGAS IN THAT UNIT!
        if (!c.status || c.status === 'pending') {
            const unitKey = getUnitKey(c);
            const unitOfficers = (officersByUnit[unitKey] && officersByUnit[unitKey].length > 0)
                ? officersByUnit[unitKey]
                : [{ username: 'petugas1' }];

            const idx = roundRobinIndex[unitKey] % unitOfficers.length;
            roundRobinIndex[unitKey]++;

            const assignedOfficer = (unitOfficers[idx] && unitOfficers[idx].username) ? unitOfficers[idx].username : 'petugas1';
            const correctUlp = unitKey === 'dobo' ? 'ULP Dobo' : (unitKey === 'elat' ? 'ULP Elat' : 'Tual Kota');

            return {
                ...c,
                ulp: c.ulp && c.ulp !== 'Tual Kota' ? c.ulp : correctUlp,
                assignedTo: assignedOfficer
            };
        }
        return c; // Keep surveyed/validated items 100% untouched
    });
}





// Fix Leaflet Marker Icons safely
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  });
} catch (e) {
  console.warn("Leaflet icon setup warning:", e);
}

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

function StatusBadge({ status }) {
  switch (status) {
    case 'pending': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-yellow-100 text-[#FE7A15] rounded-md"><Clock size={10} className="mr-1"/> Menunggu Survey</span>;
    case 'surveyed': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-[#218DAE]/20 text-[#218DAE] rounded-md"><AlertCircle size={10} className="mr-1"/> Siap Sync</span>;
    case 'synced': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-[#FE7A15]/10 text-[#FE7A15] rounded-md border border-[#FE7A15]/20"><CheckCircle2 size={10} className="mr-1"/> Evaluasi Admin</span>;
    case 'sudah_divalidasi': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-md"><CheckCircle2 size={10} className="mr-1"/> Tervalidasi</span>;
    case 'menunggu_validasi': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-blue-100 text-blue-700 rounded-md"><RefreshCw size={10} className="mr-1"/> Review Admin</span>;
    case 'rejected': return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-red-100 text-red-700 rounded-md"><XCircle size={10} className="mr-1"/> Ditolak</span>;
    default: return <span className="flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md">Baru</span>;
  }
}

function JenisToBadge({ jenis }) {
  switch (jenis) {
    case 'perbaikan_data': return <span className="inline-block px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-md text-[9px] font-black uppercase tracking-wider">Perbaikan Data</span>;
    case 'id_double': return <span className="inline-block px-2.5 py-0.5 bg-rose-100 text-rose-700 rounded-md text-[9px] font-black uppercase tracking-wider">ID Pelanggan Double</span>;
    case 'pelanggan_baru':
    default: return <span className="inline-block px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-[9px] font-black uppercase tracking-wider">Pelanggan Baru</span>;
  }
}

const initialUsers = [
  { id: 1, username: 'up3tual', pass: 'P@ssw0rd', role: 'admin', name: 'Administrator UP3 Tual', ulp: 'Tual Kota', status: 'aktif' },
  { id: 2, username: 'manager', pass: 'P@ssw0rd', role: 'manager', name: 'Manager UP3 Tual', ulp: 'Tual Kota', status: 'aktif' },
  { id: 3, username: 'validator', pass: 'P@ssw0rd', role: 'validator', name: 'Petugas Validasi', ulp: 'Tual Kota', status: 'aktif' },
  { id: 4, username: 'petugas1', pass: 'P@ssw0rd', role: 'surveyor', name: 'Petugas Lapangan Utama', ulp: 'Tual Kota', status: 'aktif' },
  { id: 5, username: 'valen_tual', pass: 'P@ssw0rd', role: 'surveyor', name: 'VALEN JANWARIN', ulp: 'Tual Kota', status: 'aktif' },
  { id: 6, username: 'bravo_tual', pass: 'P@ssw0rd', role: 'surveyor', name: 'BRAVO FARNEUBUN', ulp: 'Tual Kota', status: 'aktif' },
  { id: 7, username: 'petrus_tual', pass: 'P@ssw0rd', role: 'surveyor', name: 'PETRUS JANWARIN', ulp: 'Tual Kota', status: 'aktif' },
  { id: 8, username: 'brian_tual', pass: 'P@ssw0rd', role: 'surveyor', name: 'BRIAN TALAUD', ulp: 'Tual Kota', status: 'aktif' },
  { id: 9, username: 'elvis_dobo', pass: 'P@ssw0rd', role: 'surveyor', name: 'ELVIS MATULESSY', ulp: 'ULP Dobo', status: 'aktif' },
  { id: 10, username: 'michael_dobo', pass: 'P@ssw0rd', role: 'surveyor', name: 'MICHAEL SELFANAY', ulp: 'ULP Dobo', status: 'aktif' },
  { id: 11, username: 'erik_dobo', pass: 'P@ssw0rd', role: 'surveyor', name: 'ERIK KAIDEL', ulp: 'ULP Dobo', status: 'aktif' },
  { id: 12, username: 'roni_dobo', pass: 'P@ssw0rd', role: 'surveyor', name: 'RONI LAUWANTO', ulp: 'ULP Dobo', status: 'aktif' }
];

const initialCustomers = initialCustomersData;

function isUserAdmin(u) {
  return u && (u.role === 'admin' || u.role === 'manager' || u.role === 'validator');
}


function isCustomerAssignedToSurveyor(c, surveyor) {
  if (!c || !surveyor) return false;

  // Admin / Manager / Validator sees all TOs
  if (surveyor.role === 'admin' || surveyor.role === 'manager' || surveyor.role === 'validator') {
    return true;
  }

  const surveyorUser = String(surveyor.username || '').toLowerCase().trim();
  const assignedUser = String(c.assignedTo || '').toLowerCase().trim();

  // 1. Direct username match
  if (assignedUser && assignedUser === surveyorUser) {
    return true;
  }

  // 2. Match ULP Unit: Allow surveyor to see all TOs belonging to their ULP Unit
  const sUlp = (surveyor.ulp || '').toLowerCase().replace('ulp', '').trim();
  const cUlp = (c.ulp || c.kab || c.desa || c.kec || '').toLowerCase().replace('ulp', '').trim();

  if (sUlp.includes('dobo') || sUlp.includes('aru') || surveyorUser.includes('dobo')) {
    return cUlp.includes('dobo') || cUlp.includes('aru') || assignedUser.includes('dobo');
  }
  if (sUlp.includes('elat') || sUlp.includes('kei besar') || surveyorUser.includes('elat')) {
    return cUlp.includes('elat') || cUlp.includes('kei besar') || assignedUser.includes('elat');
  }
  if (sUlp.includes('tual') || surveyorUser.includes('tual') || surveyorUser.includes('petugas')) {
    return !cUlp.includes('dobo') && !cUlp.includes('aru') && !cUlp.includes('elat');
  }

  if (sUlp && cUlp) {
    return sUlp.includes(cUlp) || cUlp.includes(sUlp);
  }

  return true;
}

function getAssignedSurveyorByUlp(ulpVal, usersList = []) {
  if (!ulpVal) return 'petugas1';
  const u = String(ulpVal).toLowerCase().trim();

  if (Array.isArray(usersList) && usersList.length > 0) {
    const matchedUser = usersList.find(usr => {
      if (!usr || usr.role === 'admin' || usr.role === 'manager' || usr.role === 'validator') return false;
      const usrUlp = String(usr.ulp || '').toLowerCase().trim();
      return usrUlp && (usrUlp.includes(u) || u.includes(usrUlp));
    });
    if (matchedUser && matchedUser.username) {
      return matchedUser.username;
    }
  }

  if (u.includes('dobo')) return 'petugas_dobo';
  if (u.includes('elat')) return 'petugas_elat';
  return 'petugas1';
}

function isRejectedForCurrentUser(c, user) {
  if (!c || !user) return false;
  if (c.status !== 'rejected') return false;

  if (user.role === 'admin' || user.role === 'manager' || user.role === 'validator') {
    return true;
  }

  const currentUsername = String(user.username || '').toLowerCase().trim();
  const surveyorUser = String(c.surveyorUsername || (c.surveyData && c.surveyData.surveyorUsername) || '').toLowerCase().trim();
  const assignedUser = String(c.assignedTo || '').toLowerCase().trim();

  if (surveyorUser) {
    return surveyorUser === currentUsername;
  }
  if (assignedUser) {
    return assignedUser === currentUsername;
  }
  return isCustomerAssignedToSurveyor(c, user);
}

function isCompletedByCurrentUser(c, user) {
  if (!c || !user) return false;
  const isDone = (c.status && c.status !== 'pending' && c.status !== 'rejected') || !!c.surveyData || !!c.tgl_survey;
  if (!isDone) return false;

  if (user.role === 'admin' || user.role === 'manager' || user.role === 'validator') {
    return true;
  }

  const currentUsername = String(user.username || '').toLowerCase().trim();
  const surveyorUser = String(c.surveyorUsername || (c.surveyData && c.surveyData.surveyorUsername) || '').toLowerCase().trim();
  const assignedUser = String(c.assignedTo || '').toLowerCase().trim();

  if (surveyorUser) {
    return surveyorUser === currentUsername;
  }
  if (assignedUser) {
    return assignedUser === currentUsername;
  }
  return isCustomerAssignedToSurveyor(c, user);
}

const secureStorage = {
  getItem: (key) => {
    try {
      const val = localStorage.getItem(key);
      if (!val) return null;
      return JSON.parse(val);
    } catch (e) {
      return null;
    }
  },
  setItem: (key, val) => {
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch (e) {
      console.warn("secureStorage.setItem error:", e);
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
  }
};

const GEOFENCE_RADIUS_METERS = 50;

function getPhotoUrl(rawUrl, backendUrlStr) {
  if (!rawUrl || typeof rawUrl !== 'string' || rawUrl.trim() === '' || rawUrl === 'null' || rawUrl === 'undefined') return null;
  let url = rawUrl.trim();

  // If url is a local IP/port from another device, convert to active server domain
  if (url.includes('/api/photos/')) {
    const filename = url.split('/api/photos/').pop();
    const host = backendUrlStr || 'https://api.mapping-pel.com';
    return `${host}/api/photos/${filename}`;
  }

  if (url.startsWith('data:image') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  const host = backendUrlStr || 'https://api.mapping-pel.com';
  return `${host}${cleanPath}`;
}

function ImageZoomMagnifier({ src, alt, kwhNum, onApprove, onReject, backendUrlStr }) {
  const [showLens, setShowLens] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, px: 50, py: 50 });
  const [zoomLevel, setZoomLevel] = useState(5);
  const [fullscreen, setFullscreen] = useState(false);
  const [imgError, setImgError] = useState(false);

  const containerRef = useRef(null);
  const fsRef = useRef(null);
  const [showFsLens, setShowFsLens] = useState(false);
  const [fsPos, setFsPos] = useState({ x: 0, y: 0, px: 50, py: 50 });

  const finalSrc = getPhotoUrl(src, backendUrlStr);

  const handleMove = (clientX, clientY, ref, setPositionFn) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const px = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const py = Math.max(0, Math.min(100, (y / rect.height) * 100));

    setPositionFn({ x, y, px, py });
  };

  if (!finalSrc || imgError) {
    return (
      <div className="h-64 w-full bg-slate-50 rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-slate-200 p-6 text-center space-y-2">
        <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-black shadow-2xs">
          <Camera size={20} />
        </div>
        <h4 className="text-xs font-black uppercase text-slate-700">Foto Survei Belum Tersedia</h4>
        <p className="text-[10px] text-slate-400 font-bold max-w-xs">
          Pelanggan ini belum disurvei oleh petugas lapangan atau file foto belum diunggah dari HP.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Top Zoom Level Controls */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center space-x-1.5">
          <span className="text-[10px] font-black uppercase text-slate-400">Zoom Kaca Pembesar:</span>
          {[3, 5, 8, 10].map((lvl) => (
            <button
              key={lvl}
              type="button"
              onClick={() => setZoomLevel(lvl)}
              className={`px-2 py-0.5 rounded-md text-[10px] font-black transition ${
                zoomLevel === lvl ? 'bg-[#218DAE] text-white shadow-xs' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              {lvl}x
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="text-[10px] font-black text-[#218DAE] hover:underline flex items-center space-x-1 bg-cyan-50 px-2 py-1 rounded-md border border-[#218DAE]/30"
        >
          <span>🔍 Pop-Up Kaca Pembesar</span>
        </button>
      </div>

      {/* Main Card Image Container */}
      <div
        ref={containerRef}
        onMouseEnter={() => setShowLens(true)}
        onMouseLeave={() => setShowLens(false)}
        onMouseMove={(e) => handleMove(e.clientX, e.clientY, containerRef, setPos)}
        onTouchStart={() => setShowLens(true)}
        onTouchEnd={() => setShowLens(false)}
        onTouchMove={(e) => e.touches[0] && handleMove(e.touches[0].clientX, e.touches[0].clientY, containerRef, setPos)}
        onClick={() => setFullscreen(true)}
        className="h-64 w-full bg-slate-950 rounded-2xl overflow-hidden relative border-2 border-slate-300 cursor-zoom-in group shadow-md"
      >
        <img
          src={finalSrc}
          alt={alt}
          onError={() => setImgError(true)}
          className="w-full h-full object-contain pointer-events-none select-none"
        />

        {/* Magnifying Loupe Glass Lens */}
        {showLens && (
          <div
            className="absolute w-52 h-52 rounded-full border-4 border-[#218DAE] shadow-2xl overflow-hidden pointer-events-none z-30 bg-slate-900"
            style={{
              top: `${pos.y - 104}px`,
              left: `${pos.x - 104}px`,
              backgroundImage: `url(${finalSrc})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${zoomLevel * 100}%`,
              backgroundPosition: `${pos.px}% ${pos.py}%`
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-40">
              <div className="w-full h-[1px] bg-cyan-400" />
              <div className="h-full w-[1px] bg-cyan-400 absolute" />
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-black/80 text-cyan-300 font-mono text-[9px] font-black rounded-full uppercase tracking-wider border border-cyan-500/40">
              {zoomLevel}X Kaca Pembesar
            </div>
          </div>
        )}

        {!showLens && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-2 text-center text-white text-[10px] font-bold opacity-90 group-hover:opacity-100 transition pointer-events-none">
            <span>🔎 Klik Foto untuk Pop-Up Kaca Pembesar ({zoomLevel}x)</span>
          </div>
        )}
      </div>

      {/* DEDICATED FULLSCREEN POP-UP WITH LOUPE LENS & KWH NUMBER COMPARISON */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-4 overflow-y-auto"
          onClick={() => setFullscreen(false)}
        >
          <div
            className="relative max-w-4xl w-full bg-slate-900 rounded-3xl p-6 border border-slate-700 shadow-2xl space-y-4 my-auto text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Target KWh Number */}
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-black uppercase text-cyan-400 flex items-center space-x-2">
                  <span>🔎 Pop-Up Kaca Pembesar Inspeksi Foto KWh Meter</span>
                </h3>
                {kwhNum && (
                  <div className="mt-1 bg-rose-950/80 border border-rose-500/50 p-2.5 rounded-xl flex items-center space-x-3">
                    <span className="text-[10px] font-black uppercase text-rose-300 tracking-wider">TARGET NO. KWH DATABASE:</span>
                    <span className="text-xl font-black text-rose-400 font-mono tracking-widest">{kwhNum}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setFullscreen(false)}
                className="p-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Level Zoom Selector in Pop-Up */}
            <div className="flex items-center justify-between bg-slate-950/80 p-3 rounded-2xl border border-slate-800">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-black uppercase text-slate-400">Atur Pembesaran:</span>
                {[3, 5, 8, 10].map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => setZoomLevel(lvl)}
                    className={`px-3 py-1 rounded-xl text-xs font-black transition ${
                      zoomLevel === lvl ? 'bg-[#218DAE] text-white shadow-md' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {lvl}x Zoom
                  </button>
                ))}
              </div>
              <span className="text-[10px] font-bold text-cyan-400 italic hidden sm:inline">
                Arahkan kursor / jari ke foto di bawah untuk Kaca Pembesar 🔍
              </span>
            </div>

            {/* Pop-Up Loupe Lens Image Container */}
            <div
              ref={fsRef}
              onMouseEnter={() => setShowFsLens(true)}
              onMouseLeave={() => setShowFsLens(false)}
              onMouseMove={(e) => handleMove(e.clientX, e.clientY, fsRef, setFsPos)}
              onTouchStart={() => setShowFsLens(true)}
              onTouchEnd={() => setShowFsLens(false)}
              onTouchMove={(e) => e.touches[0] && handleMove(e.touches[0].clientX, e.touches[0].clientY, fsRef, setFsPos)}
              className="h-[55vh] w-full bg-slate-950 rounded-2xl overflow-hidden relative border border-slate-700 cursor-crosshair shadow-inner flex items-center justify-center"
            >
              <img src={finalSrc} alt={alt} className="max-w-full max-h-full object-contain pointer-events-none select-none" />

              {/* Pop-Up Circular Loupe Glass Lens */}
              {showFsLens && (
                <div
                  className="absolute w-64 h-64 rounded-full border-4 border-cyan-400 shadow-2xl overflow-hidden pointer-events-none z-30 bg-slate-950"
                  style={{
                    top: `${fsPos.y - 128}px`,
                    left: `${fsPos.x - 128}px`,
                    backgroundImage: `url(${finalSrc})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: `${zoomLevel * 100}%`,
                    backgroundPosition: `${fsPos.px}% ${fsPos.py}%`
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center opacity-40">
                    <div className="w-full h-[1px] bg-cyan-400" />
                    <div className="h-full w-[1px] bg-cyan-400 absolute" />
                  </div>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/90 text-cyan-300 font-mono text-[10px] font-black rounded-full uppercase tracking-wider border border-cyan-500/50 shadow-md">
                    Kaca Pembesar {zoomLevel}X
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions in Pop-Up */}
            {(onApprove || onReject) && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                <span className="text-xs font-bold text-slate-400">Validasi Langsung dari Pop-Up:</span>
                <div className="flex space-x-2">
                  {onReject && (
                    <button
                      type="button"
                      onClick={() => {
                        setFullscreen(false);
                        onReject();
                      }}
                      className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase shadow-md transition"
                    >
                      Tolak
                    </button>
                  )}
                  {onApprove && (
                    <button
                      type="button"
                      onClick={() => {
                        setFullscreen(false);
                        onApprove();
                      }}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase shadow-md transition"
                    >
                      Setuju & Validasi
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function filterOutDeleted(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const rawSavedDeleted = secureStorage.getItem('pln_deleted_ids') || [];
  const savedDeleted = Array.isArray(rawSavedDeleted) ? rawSavedDeleted.map(d => String(d).trim()) : [];
  if (savedDeleted.length === 0) return list;

  return list.filter(c => {
    if (!c) return false;
    const cId = String(c.id || '').trim();
    const cDil = String(c.dil || '').trim();
    const isDeleted = savedDeleted.some(d => d && (d === cId || d === cDil));
    if (!isDeleted) return true;

    // If isDeleted is true, ONLY keep if status is ALREADY VALIDATED ('sudah_divalidasi')
    const isTrulyValidated = c.status === 'sudah_divalidasi';
    return isTrulyValidated;
  });
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const isAdmin = isUserAdmin(currentUser);
  const [currentView, setCurrentView] = useState('login');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [toast, setToast] = useState(null);

  const [darkMode, setDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [users, setUsers] = useState(initialUsers);
  const [customers, setCustomers] = useState(() => {
    const saved = secureStorage.getItem('pln_customers_db');
    if (Array.isArray(saved) && saved.length > 10) {
      const filtered = saved.filter(c => {
        if (!c || c.role) return false;
        const nameUpper = String(c.name || '').toUpperCase();
        if (nameUpper.includes('PETUGAS LAPANGAN') || nameUpper.includes('ADMINISTRATOR') || nameUpper.includes('VALIDATOR') || nameUpper.includes('MANAGER')) {
          return false;
        }
        return true;
      });
      if (filtered.length > 10) return filterOutDeleted(distributeCustomersEvenlyByUlp(filtered, initialUsers));
    }
    return filterOutDeleted(distributeCustomersEvenlyByUlp(initialCustomersData || [], initialUsers));
  });
  const [adminTab, setAdminTab] = useState('dashboard');
  const [eventLogs, setEventLogs] = useState([]);
  const [importDilSubTab, setImportDilSubTab] = useState('upload');
  const [importFilterDate, setImportFilterDate] = useState('');
  const [importFilterDesa, setImportFilterDesa] = useState('Semua Desa');
  const [importSearchQuery, setImportSearchQuery] = useState('');
  const [importPage, setImportPage] = useState(1);
  const [valPage, setValPage] = useState(1);


  const [csvData, setCsvData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [validationSearchQuery, setValidationSearchQuery] = useState('');
  const [valFilterStatus, setValFilterStatus] = useState('ALL');
  const [valFilterPetugas, setValFilterPetugas] = useState('ALL');
  const [valFilterStartDate, setValFilterStartDate] = useState('');
  const [valFilterEndDate, setValFilterEndDate] = useState('');
  const [valFilterDesa, setValFilterDesa] = useState('ALL');
  const [valFilterUnit, setValFilterUnit] = useState('ALL');

  const handleResetValidationFilter = () => {
    setValFilterStatus('ALL');
    setValFilterPetugas('ALL');
    setValFilterStartDate('');
    setValFilterEndDate('');
    setValFilterDesa('ALL');
    setValFilterUnit('ALL');
    setValidationSearchQuery('');
    fetchBackendData(true, true);
    showToast('🔄 Filter validasi di-reset & data dimuat ulang!');
  };

  const getFilteredValidationCustomers = () => {
    let list = customers.filter(c => {
      if (!c || c.role) return false;
      const nameUpper = String(c.name || '').toUpperCase();
      if (nameUpper.includes('PETUGAS LAPANGAN') || nameUpper.includes('ADMINISTRATOR') || nameUpper.includes('VALIDATOR') || nameUpper.includes('MANAGER')) {
        return false;
      }
      return (c.status && c.status !== 'pending') || c.surveyData || c.tgl_survey;
    });

    if (valFilterStatus !== 'ALL') {
      if (valFilterStatus === 'menunggu_validasi') {
        list = list.filter(c => c.status === 'menunggu_validasi' || c.status === 'surveyed' || c.status === 'synced');
      } else {
        list = list.filter(c => c.status === valFilterStatus);
      }
    }

    if (valFilterPetugas !== 'ALL') {
      list = list.filter(c => (c.assignedTo === valFilterPetugas || c.surveyorUsername === valFilterPetugas));
    }

    if (valFilterStartDate) {
      list = list.filter(c => c.tgl_survey && c.tgl_survey.split(' ')[0] >= valFilterStartDate);
    }

    if (valFilterEndDate) {
      list = list.filter(c => c.tgl_survey && c.tgl_survey.split(' ')[0] <= valFilterEndDate);
    }

    if (valFilterDesa !== 'ALL') {
      list = list.filter(c => c.desa === valFilterDesa);
    }

    if (valFilterUnit !== 'ALL') {
      list = list.filter(c => (c.ulp || '').toLowerCase().includes(valFilterUnit.toLowerCase()));
    }

    if (deferredValidationQuery.trim()) {
      const q = deferredValidationQuery.toLowerCase().trim();
      list = list.filter(c =>
        (c.dil && c.dil.toLowerCase().includes(q)) ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.alamat && c.alamat.toLowerCase().includes(q)) ||
        (c.kwh_meter_awal && c.kwh_meter_awal.toLowerCase().includes(q))
      );
    }

    return list;
  };

  const [photoStep, setPhotoStep] = useState('kwh');
  const [kwhPhotoUri, setKwhPhotoUri] = useState(null);
  const [housePhotoUri, setHousePhotoUri] = useState(null);
  const [surveyMatch, setSurveyMatch] = useState(true);
  const [surveyNote, setSurveyNote] = useState('');
  const [surveyMode, setSurveyMode] = useState('form');
  const [surveyorDesaFilter, setSurveyorDesaFilter] = useState('ALL');
  const [visibleSurveyorLimit, setVisibleSurveyorLimit] = useState(30);
  const [isSyncing, setIsSyncing] = useState(false);
  const lastValidPendingCount = useRef(0);
  const [surveyorSearchQuery, setSurveyorSearchQuery] = useState('');
  const deferredValidationQuery = useDeferredValue(validationSearchQuery);
  const deferredImportQuery = useDeferredValue(importSearchQuery);
  const deferredSurveyorQuery = useDeferredValue(surveyorSearchQuery);
  const [surveyorTab, setSurveyorTab] = useState('semua');
  const [isNearestFilter, setIsNearestFilter] = useState(false);

  const [cameraStream, setCameraStream] = useState(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState(null);
  const [showCameraFlash, setShowCameraFlash] = useState(false);
  const [currentPos, setCurrentPos] = useState({ lat: -5.6200, lng: 132.7400 });

  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [deletedIds, setDeletedIds] = useState(() => secureStorage.getItem('pln_deleted_ids') || []);
  const [editCustomerModal, setEditCustomerModal] = useState({ show: false, customer: null });
  const [editCustomerForm, setEditCustomerForm] = useState({ name: '', dil: '', daya: '', tarif: 'PRABAYAR', alamat: '', desa: '', ulp: 'Tual Kota' });
  const [showSuratTugasMobileModal, setShowSuratTugasMobileModal] = useState(false);
  const [showSurveyedModal, setShowSurveyedModal] = useState(false);
  const [suratTugasDoc, setSuratTugasDoc] = useState(() => {
    return secureStorage.getItem('pln_surat_tugas') || { name: 'Surat_Tugas_Resmi_UP3_Tual_2026.pdf', date: '2026-06-01', status: 'Aktif Berstempel' };
  });

  const [userModal, setUserModal] = useState({ show: false, mode: 'add', user: null });
  const [userForm, setUserForm] = useState({ name: '', username: '', role: 'surveyor', ulp: 'Tual Kota', pass: 'P@ssw0rd' });

  const [serverIpModal, setServerIpModal] = useState(false);
  const [addCustomerModal, setAddCustomerModal] = useState(false);
  const [addCustomerForm, setAddCustomerForm] = useState({
    dil: '',
    name: '',
    kwh_meter_awal: '',
    daya: '900',
    tarif: 'R1MT',
    alamat: '',
    desa: 'KOLSER',
    ulp: 'Tual Kota',
    lat: -5.6241,
    lng: 132.7470,
    jenis_to: 'pelanggan_baru'
  });
  const [inputServerIp, setInputServerIp] = useState(secureStorage.getItem('pln_custom_server_ip') || 'https://api.mapping-pel.com');

  const [mapComparisonModal, setMapComparisonModal] = useState({ show: false, customer: null });
  const [photoComparisonModal, setPhotoComparisonModal] = useState({ show: false, customer: null });

  const [rejectModal, setRejectModal] = useState({ show: false, customerId: null, customerName: '' });
  const [rejectReasonText, setRejectReasonText] = useState('');

  const videoRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const customServerIp = secureStorage.getItem('pln_custom_server_ip') || '';
  const isNative = window.Capacitor?.isNativePlatform();
  const [activeWorkingUrl, setActiveWorkingUrl] = useState(() => secureStorage.getItem('pln_working_server_url') || '');

  const getApiCandidates = () => {
    const customIp = secureStorage.getItem('pln_custom_server_ip') || inputServerIp || '';
    const formattedCustom = customIp
      ? (customIp.startsWith('http') ? customIp : `http://${customIp}:3005`).replace(/\/$/, '')
      : '';

    const candidates = [];

    // Local Express Server at port 3005 (Primary Server for Laptop & Local Wi-Fi)
    candidates.push(
      'http://localhost:3005',
      'http://127.0.0.1:3005',
      'http://10.23.131.39:3005',
      'http://192.168.137.1:3005'
    );

    if (activeWorkingUrl) {
      candidates.push(activeWorkingUrl);
    }

    if (formattedCustom) {
      candidates.push(formattedCustom);
    }

    if (!isNative) {
      candidates.push('');
    }

    // Official production Cloudflare domains
    candidates.push(
      'https://api.mapping-pel.com',
      'https://mapping-pel.com'
    );

    return [...new Set(candidates.filter(c => c !== undefined && c !== null))];
  };

  const activeServerHost = customServerIp
    ? (customServerIp.startsWith('http') ? customServerIp : `http://${customServerIp}:3005`)
    : 'https://api.mapping-pel.com';

  const backendUrl = isNative ? activeServerHost : (window.location.origin.includes('localhost') || window.location.origin.includes('127.0.0.1') ? 'https://api.mapping-pel.com' : '');

  const fetchWithTimeout = async (url, timeoutMs = 3000, options = {}) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(id);
      return res;
    } catch (e) {
      clearTimeout(id);
      throw e;
    }
  };

  const postToApiCandidates = async (endpointPath, bodyObj, method = 'POST') => {
    const candidates = getApiCandidates();
    let success = false;
    let resultData = null;

    for (const base of candidates) {
      try {
        const url = base ? `${base}${endpointPath}` : endpointPath;
        const res = await fetchWithTimeout(url, 20000, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bodyObj)
        });
        if (res && res.ok) {
          if (!activeWorkingUrl || base === activeWorkingUrl) {
            setActiveWorkingUrl(base);
            secureStorage.setItem('pln_working_server_url', base);
          }
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            try {
              resultData = await res.json();
            } catch (jsonErr) {}
          }
          success = true;
        }
      } catch (e) {}
    }
    return { success, data: resultData };
  };

  const deleteFromApiCandidates = async (endpointPath) => {
    const candidates = getApiCandidates();
    let success = false;

    for (const base of candidates) {
      try {
        const url = base ? `${base}${endpointPath}` : endpointPath;
        const res = await fetchWithTimeout(url, 10000, { method: 'DELETE' });
        if (res && res.ok) {
          if (!activeWorkingUrl || base === activeWorkingUrl) {
            setActiveWorkingUrl(base);
            secureStorage.setItem('pln_working_server_url', base);
          }
          success = true;
        }
      } catch (e) {}
    }
    return success;
  };

  const handleSuratTugasUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      showToast(`⏳ Meng-upload & menyinkronkan "${file.name}"...`);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const isImg = file.type.includes('image');
        const docInfo = {
          name: file.name,
          type: isImg ? 'image' : 'pdf',
          url: event.target.result,
          date: new Date().toISOString().split('T')[0],
          status: 'Aktif Berstempel'
        };
        setSuratTugasDoc(docInfo);
        secureStorage.setItem('pln_surat_tugas', docInfo);

        try {
          const res = await postToApiCandidates('/api/surat-tugas', docInfo);
          if (res && res.data && res.data.docInfo) {
            setSuratTugasDoc(res.data.docInfo);
            secureStorage.setItem('pln_surat_tugas', res.data.docInfo);
          }
          showToast(`✅ Surat Tugas "${file.name}" BERHASIL DI-UPLOAD & TERSINKRONISASI KE HP PETUGAS!`);
        } catch (err) {
          showToast(`✅ Surat Tugas "${file.name}" tersimpan di memori lokal.`);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const [serverDiagnosticList, setServerDiagnosticList] = useState([]);
  const [isTestingServers, setIsTestingServers] = useState(false);

  const runServerDiagnostics = async () => {
    setIsTestingServers(true);
    const candidates = getApiCandidates();
    const results = [];

    for (const base of candidates) {
      const url = base ? `${base}/api/customers` : '/api/customers';
      const startTime = Date.now();
      let status = 'OFFLINE';
      let latency = 0;
      let count = 0;
      let ok = false;

      try {
        const res = await fetchWithTimeout(url, 2500);
        latency = Date.now() - startTime;
        if (res.ok) {
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await res.json();
            if (Array.isArray(data)) {
              count = data.length;
              status = `ONLINE (${count} TO)`;
              ok = true;
            } else {
              status = 'Bukan Format JSON';
            }
          } else {
            status = 'Bukan JSON (HTML)';
          }
        } else {
          status = `HTTP ${res.status}`;
        }
      } catch (e) {
        latency = Date.now() - startTime;
        status = 'TIMEOUT / Unreachable';
      }

      results.push({
        base: base || '',
        displayUrl: base || 'http://localhost (Relative)',
        ok,
        status,
        latency,
        count
      });
    }

    setServerDiagnosticList(results);
    setIsTestingServers(false);
  };

  const handleTestAndSaveServerIp = async (e) => {
    e?.preventDefault();
    const cleanIp = inputServerIp.trim().replace('http://', '').replace(':3005', '').replace('/', '');
    if (!cleanIp) return;
    showToast('Menguji koneksi server...');
    try {
      const res = await fetchWithTimeout(`http://${cleanIp}:3005/api/customers`, 3000);
      if (res.ok) {
        secureStorage.setItem('pln_custom_server_ip', cleanIp);
        showToast(`✅ Berhasil terhubung ke Server Laptop (${cleanIp})!`);
        setServerIpModal(false);
        fetchBackendData(true, true);
      } else {
        showToast('⚠️ Server merespon tapi ada error.');
      }
    } catch (err) {
      showToast(`⚠️ Gagal terhubung ke http://${cleanIp}:3005. Periksa IP / Wi-Fi.`);
    }
  };

  const fetchEventLogs = async () => {
    try {
      const candidates = getApiCandidates();
      for (const base of candidates) {
        try {
          const url = base ? `${base}/api/event-logs` : '/api/event-logs';
          const res = await fetchWithTimeout(url, 3000);
          if (res && res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) {
              setEventLogs(data);
              break;
            }
          }
        } catch (e) {}
      }
    } catch (e) {}
  };

  const fetchBackendData = async (silent = false, force = false) => {
    const candidates = getApiCandidates();
    let successCust = false;

    for (const baseUrl of candidates) {
      try {
        const url = baseUrl ? `${baseUrl}/api/customers` : '/api/customers';
        const resCust = await fetchWithTimeout(url, 2500);
        if (resCust.ok) {
          if (baseUrl !== activeWorkingUrl) {
            setActiveWorkingUrl(baseUrl);
            secureStorage.setItem('pln_working_server_url', baseUrl);
          }
          const contentType = resCust.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) continue;
          let dataCustRaw = [];
          try {
            dataCustRaw = await resCust.json();
          } catch (jsonErr) {
            continue;
          }

          if (Array.isArray(dataCustRaw)) {
            const savedLocal = secureStorage.getItem('pln_customers_db');
            if (dataCustRaw.length < 10) {
              console.warn("⚠️ Server merespon kurang dari 10 data. Memulihkan database master...");
              const masterFallback = (Array.isArray(savedLocal) && savedLocal.length > 10) ? savedLocal : (initialCustomersData || []);
              if (masterFallback.length > 0) {
                const cleanFallback = filterOutDeleted(masterFallback);
                pushCustomersToBackend(cleanFallback);
                setCustomers(cleanFallback);
                secureStorage.setItem('pln_customers_db', cleanFallback);
                successCust = true;
                return;
              }
            }
          }

          if (Array.isArray(dataCustRaw) && dataCustRaw.length > 0) {
            const dataCust = filterOutDeleted(dataCustRaw);

            const currentLocal = secureStorage.getItem('pln_customers_db') || customers || [];

            const serverMap = new Map();
            dataCust.forEach(s => {
              if (s) {
                const key = String(s.dil || s.id || '').trim();
                if (key) serverMap.set(key, s);
              }
            });

            const localMap = new Map();
            if (Array.isArray(currentLocal)) {
              currentLocal.forEach(l => {
                if (l) {
                  const key = String(l.dil || l.id || '').trim();
                  if (key) localMap.set(key, l);
                }
              });
            }

            const mergedCustMap = new Map();

            let newToCount = 0;

            serverMap.forEach((serverItem, key) => {
              const localItem = localMap.get(key);

              if (!localItem) {
                mergedCustMap.set(key, serverItem);
                if (!serverItem.status || serverItem.status === 'pending') {
                  newToCount++;
                }
              } else {
                if (serverItem.status === 'sudah_divalidasi' || serverItem.status === 'rejected') {
                  mergedCustMap.set(key, {
                    ...localItem,
                    ...serverItem,
                    surveyData: {
                      ...(localItem.surveyData || {}),
                      ...(serverItem.surveyData || {})
                    }
                  });
                } else if (localItem.status === 'menunggu_validasi' && serverItem.status === 'pending') {
                  mergedCustMap.set(key, localItem);
                  postToApiCandidates('/api/customers/update', localItem);
                } else if (localItem.status === 'menunggu_validasi' && serverItem.status === 'menunggu_validasi') {
                  mergedCustMap.set(key, {
                    ...localItem,
                    ...serverItem,
                    surveyData: {
                      ...(localItem.surveyData || {}),
                      ...(serverItem.surveyData || {})
                    }
                  });
                } else {
                  mergedCustMap.set(key, { ...localItem, ...serverItem });
                }
              }
            });

            // Build customer list:
            // For Web browser on any laptop (!isNative): Server dataset dataCust is 100% Single Source of Truth!
            // For Mobile App (Android): Merge local unsynced offline surveys
            let finalCustList = [];
            if (!isNative) {
              finalCustList = dataCust;
            } else {
              const localUnsyncedSurveys = Array.isArray(currentLocal)
                ? currentLocal.filter(l => l && l.status === 'menunggu_validasi')
                : [];

              finalCustList = dataCust.map(s => {
                const localSurvey = localUnsyncedSurveys.find(l => String(l.id) === String(s.id) || (l.dil && String(l.dil) === String(s.dil)));
                if (localSurvey && (!s.status || s.status === 'pending')) {
                  postToApiCandidates('/api/customers/update', localSurvey);
                  return localSurvey;
                }
                return s;
              });

              const localNewTOs = Array.isArray(currentLocal)
                ? currentLocal.filter(l => l && l.isLocalNew && !dataCust.some(s => String(s.id) === String(l.id) || (l.dil && String(l.dil) === String(s.dil))))
                : [];

              if (localNewTOs.length > 0) {
                finalCustList.push(...localNewTOs);
              }
            }

            if (newToCount > 0 && !silent && isNative) {
              showToast(`📥 Terdeteksi ${newToCount} TO baru ditambahkan ke HP!`);
            }

            setCustomers(finalCustList);
            secureStorage.setItem('pln_customers_db', finalCustList);
            successCust = true;
          }
        }

        const resUser = await fetchWithTimeout(`${baseUrl}/api/users`, 3000);
        if (resUser.ok) {
          const dataUser = await resUser.json();
          if (Array.isArray(dataUser) && dataUser.length > 0) {
            setUsers(prevUsers => {
              if (Array.isArray(prevUsers) && prevUsers.length === dataUser.length) {
                return prevUsers;
              }
              secureStorage.setItem('pln_users_db', dataUser);
              return dataUser;
            });
          }
        }

        try {
          const resSurat = await fetchWithTimeout(`${baseUrl}/api/surat-tugas`, 3000);
          if (resSurat.ok) {
            const dataSurat = await resSurat.json();
            if (dataSurat) {
              setSuratTugasDoc(dataSurat);
              secureStorage.setItem('pln_surat_tugas', dataSurat);
            }
          }
        } catch (e) {}

        if (successCust) {
          return;
        }
      } catch (e) {
        // Silent poll
      }
    }

    if (!silent) {
      showToast('⚠️ Gagal terhubung ke server. Menggunakan data lokal (Offline).');
    }
    const savedCusts = secureStorage.getItem('pln_customers_db');
    if (savedCusts && Array.isArray(savedCusts) && savedCusts.length > 10) {
      setCustomers(filterOutDeleted(savedCusts));
    } else if (Array.isArray(initialCustomersData) && initialCustomersData.length > 10) {
      const cleanInitial = filterOutDeleted(distributeCustomersEvenlyByUlp(initialCustomersData, initialUsers));
      setCustomers(cleanInitial);
      secureStorage.setItem('pln_customers_db', cleanInitial);
    }
    const savedUsers = secureStorage.getItem('pln_users_db');
    if (savedUsers && Array.isArray(savedUsers) && savedUsers.length > 0) setUsers(savedUsers);
  };

  const pushUsersToBackend = async (updatedList) => {
    await postToApiCandidates('/api/users', updatedList);
  };

  const pushCustomersToBackend = async (updatedList) => {
    if (!Array.isArray(updatedList) || updatedList.length < 10) {
      console.warn("⚠️ Refusing to push empty or corrupted customer list to server!");
      return;
    }
    await postToApiCandidates('/api/customers', updatedList);
  };

  useEffect(() => {
    try {
      const savedUser = secureStorage.getItem('pln_current_user');
      if (savedUser) {
        setCurrentUser(savedUser);
        setCurrentView(isUserAdmin(savedUser) ? 'admin_dashboard' : 'surveyor_dashboard');
      }
    } catch (e) {}

    fetchBackendData(true, true);

    const pollInterval = setInterval(() => {
      fetchBackendData(true, true);
    }, 3000);

    const handleAppFocus = () => {
      fetchBackendData(true, true);
    };

    window.addEventListener('focus', handleAppFocus);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBackendData(true, true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Init GPS Tracking immediately
    if (navigator.geolocation) {
      let lastGpsLat = 0;
      let lastGpsLng = 0;

      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const newLat = pos.coords.latitude;
          const newLng = pos.coords.longitude;
          // Throttle state updates: Only update if position moved > 3 meters
          const moved = calculateDistance(lastGpsLat, lastGpsLng, newLat, newLng);
          if (lastGpsLat === 0 || moved > 3) {
            lastGpsLat = newLat;
            lastGpsLng = newLng;
            setCurrentPos({ lat: newLat, lng: newLng });
          }
        },
        (err) => console.log('GPS Error:', err),
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 8000 }
      );
      return () => {
        clearInterval(pollInterval);
        navigator.geolocation.clearWatch(watchId);
      };
    }

    return () => clearInterval(pollInterval);
  }, []);

  useEffect(() => {
    if (surveyMode === 'camera' && cameraStream && typeof cameraStream !== 'boolean') {
      const videoElement = document.getElementById('live-camera-video');
      if (videoElement && videoElement.srcObject !== cameraStream) {
        videoElement.srcObject = cameraStream;
        videoElement.play().catch(e => console.log("Video play error:", e));
      }
    }
  }, [surveyMode, cameraStream]);

  const handleOpenAddUser = () => {
    setUserForm({ name: '', username: '', role: 'surveyor', ulp: 'Tual Kota', pass: 'P@ssw0rd' });
    setUserModal({ show: true, mode: 'add', user: null });
  };

  const handleOpenEditUser = (u) => {
    setUserForm({ name: u.name, username: u.username, role: u.role, ulp: u.ulp || 'Tual Kota', pass: u.pass || 'P@ssw0rd' });
    setUserModal({ show: true, mode: 'edit', user: u });
  };

  const handleSaveUserModal = (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.username) {
      showToast('Nama dan Username wajib diisi!');
      return;
    }
    const cleanUsername = userForm.username.trim().toLowerCase().replace('@', '');
    if (userModal.mode === 'add') {
      const newUser = {
        id: Date.now(),
        name: userForm.name.trim(),
        username: cleanUsername,
        role: userForm.role,
        ulp: userForm.ulp,
        pass: userForm.pass,
        status: 'aktif'
      };
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      secureStorage.setItem('pln_users_db', updatedUsers);
      pushUsersToBackend(updatedUsers);
      showToast('✅ Berhasil menambah pengguna baru!');
    } else {
      const updatedUsers = users.map(u => u.id === userModal.user.id ? {
        ...u,
        name: userForm.name.trim(),
        username: cleanUsername,
        role: userForm.role,
        ulp: userForm.ulp,
        pass: userForm.pass
      } : u);
      setUsers(updatedUsers);
      secureStorage.setItem('pln_users_db', updatedUsers);
      pushUsersToBackend(updatedUsers);
      showToast('✅ Berhasil memperbarui pengguna!');
    }
    setUserModal({ show: false, mode: 'add', user: null });
  };

  const handleDeleteUser = (id) => {
    if (confirm('Yakin ingin menghapus pengguna ini?')) {
      const updatedUsers = users.filter(u => u.id !== id);
      setUsers(updatedUsers);
      secureStorage.setItem('pln_users_db', updatedUsers);
      pushUsersToBackend(updatedUsers);
      showToast('🗑️ Pengguna berhasil dihapus.');
    }
  };

  const handleOpenEditCustomer = (c) => {
    setEditCustomerForm({
      name: c.name || '',
      dil: c.dil || '',
      daya: c.daya || '',
      tarif: c.tarif || 'PRABAYAR',
      alamat: c.alamat || '',
      desa: c.desa || '',
      ulp: c.ulp || 'Tual Kota'
    });
    setEditCustomerModal({ show: true, customer: c });
  };

  
  const handleSaveNewCustomer = async (e) => {
    e.preventDefault();
    if (!addCustomerForm.dil || !addCustomerForm.name) {
      showToast('⚠️ IDPEL dan Nama Pelanggan wajib diisi!');
      return;
    }

    const assigned = getAssignedSurveyorByUlp(addCustomerForm.ulp, users);
    const newCust = {
      id: Date.now(),
      dil: String(addCustomerForm.dil).trim(),
      name: String(addCustomerForm.name).trim().toUpperCase(),
      kwh_meter_awal: String(addCustomerForm.kwh_meter_awal || '').trim(),
      daya: String(addCustomerForm.daya || '900').trim(),
      tarif: String(addCustomerForm.tarif || 'R1MT').trim().toUpperCase(),
      alamat: String(addCustomerForm.alamat || '').trim().toUpperCase(),
      desa: String(addCustomerForm.desa || 'KOLSER').trim().toUpperCase(),
      kec: 'Tual',
      kab: 'Maluku Tenggara',
      ulp: addCustomerForm.ulp || 'Tual Kota',
      lat: parseFloat(addCustomerForm.lat) || -5.6241,
      lng: parseFloat(addCustomerForm.lng) || 132.7470,
      jenis_to: addCustomerForm.jenis_to || 'pelanggan_baru',
      assignedTo: assigned,
      status: 'pending',
      tgl_input: new Date().toISOString().split('T')[0]
    };

    // Check duplicate DIL
    const existingDils = customers.map(c => String(c.dil || c.id));
    if (existingDils.includes(String(newCust.dil))) {
      showToast('⚠️ IDPEL / DIL tersebut sudah ada di database!');
      return;
    }

    // Un-blacklist new customer DIL locally
    const currentDeleted = secureStorage.getItem('pln_deleted_ids') || deletedIds || [];
    const cleanDeleted = Array.isArray(currentDeleted)
      ? currentDeleted.filter(d => String(d).trim() !== String(newCust.dil).trim() && String(d).trim() !== String(newCust.id).trim())
      : [];
    setDeletedIds(cleanDeleted);
    secureStorage.setItem('pln_deleted_ids', cleanDeleted);

    const updated = [newCust, ...customers];
    setCustomers(updated);
    secureStorage.setItem('pln_customers_db', updated);
    await pushCustomersToBackend(updated);
    await fetchBackendData(true, true);

    setAddCustomerModal(false);
    setAddCustomerForm({
      dil: '',
      name: '',
      kwh_meter_awal: '',
      daya: '900',
      tarif: 'R1MT',
      alamat: '',
      desa: 'KOLSER',
      ulp: 'Tual Kota',
      lat: -5.6241,
      lng: 132.7470,
      jenis_to: 'pelanggan_baru'
    });

    showToast(`✅ 1 TO baru berhasil ditambahkan & otomatis di-assign ke @${assigned}!`);
  };

  const handleSaveCustomerEdit = (e) => {
    e.preventDefault();
    const updated = customers.map(c => c.id === editCustomerModal.customer.id ? {
      ...c,
      name: editCustomerForm.name,
      dil: editCustomerForm.dil,
      daya: editCustomerForm.daya,
      tarif: editCustomerForm.tarif,
      alamat: editCustomerForm.alamat,
      desa: editCustomerForm.desa,
      ulp: editCustomerForm.ulp
    } : c);
    setCustomers(updated);
    secureStorage.setItem('pln_customers_db', updated);
    pushCustomersToBackend(updated);
    showToast('✅ Berhasil memperbarui data pelanggan!');
    setEditCustomerModal({ show: false, customer: null });
  };

  const [selectedValidationIds, setSelectedValidationIds] = useState([]);

  const handleDeleteCustomer = async (cInput) => {
    if (!cInput) return;

    const cObj = typeof cInput === 'object'
      ? cInput
      : customers.find(item => String(item.id).trim() === String(cInput).trim() || String(item.dil).trim() === String(cInput).trim());

    if (!cObj) return;

    const targetId = cObj.id || cObj.dil;
    const targetName = cObj.name || 'Pelanggan';

    // Protect only if status is ALREADY VALIDATED
    if (cObj.status === 'sudah_divalidasi') {
      showToast(`⚠️ Pelanggan "${targetName}" SUDAH DIVALIDASI. Data survei tersimpan di Menu Validasi.`);
      return;
    }

    if (!confirm(`Yakin ingin menghapus TO "${targetName}" (IDPEL: ${cObj.dil || targetId}) dari database?`)) return;

    // 1. Blacklist ID locally to prevent background polling re-insertion
    const targetIdStr = String(targetId).trim();
    const targetDilStr = cObj.dil ? String(cObj.dil).trim() : targetIdStr;
    const newDeletedIds = [...new Set([...deletedIds, targetIdStr, targetDilStr])];
    setDeletedIds(newDeletedIds);
    secureStorage.setItem('pln_deleted_ids', newDeletedIds);

    // 2. Filter out from local state & storage
    const updated = customers.filter(item => {
      if (!item) return false;
      const iId = String(item.id || '').trim();
      const iDil = String(item.dil || '').trim();
      return iId !== targetIdStr && iDil !== targetIdStr && iId !== targetDilStr && iDil !== targetDilStr;
    });

    setCustomers(updated);
    secureStorage.setItem('pln_customers_db', updated);

    // 3. Send DELETE to backend candidates for both ID and DIL
    if (cObj.id) {
      await deleteFromApiCandidates(`/api/customers/${cObj.id}`);
    }
    if (cObj.dil && String(cObj.dil) !== String(cObj.id)) {
      await deleteFromApiCandidates(`/api/customers/${cObj.dil}`);
    }
    await fetchBackendData(true, true);

    showToast(`🗑️ TO "${targetName}" berhasil dihapus permanen.`);
  };

  const handleDeleteSelectedCustomers = async (targetIds = selectedValidationIds) => {
    if (!targetIds || targetIds.length === 0) return;

    if (confirm(`Yakin ingin menghapus ${targetIds.length} data survei/validasi terpilih dari database?`)) {
      const idsStr = targetIds.map(i => String(i));
      const updated = customers.filter(c => !idsStr.includes(String(c.id)) && !idsStr.includes(String(c.dil)));
      setCustomers(updated);
      secureStorage.setItem('pln_customers_db', updated);

      try {
        await postToApiCandidates('/api/customers/delete-batch', { ids: targetIds });
        await fetchBackendData(true, true);
        showToast(`🗑️ Berhasil menghapus ${targetIds.length} data validasi dari server.`);
      } catch (e) {
        showToast(`🗑️ ${targetIds.length} data validasi dihapus secara lokal.`);
      }
      setSelectedValidationIds([]);
    }
  };

  const handleToggleSelectValidation = (id) => {
    if (selectedValidationIds.includes(id)) {
      setSelectedValidationIds(selectedValidationIds.filter(i => i !== id));
    } else {
      setSelectedValidationIds([...selectedValidationIds, id]);
    }
  };

  const handleToggleSelectAllValidation = (e) => {
    if (e.target.checked) {
      const currentFiltered = getFilteredValidationCustomers();
      setSelectedValidationIds(currentFiltered.map(c => c.id));
    } else {
      setSelectedValidationIds([]);
    }
  };

  const startLiveCamera = async () => {
    setCapturedPreviewUrl(null);
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform();

    if (isNative) {
      try {
        const status = await CameraPreview.requestPermissions();
        if (status.camera !== 'granted') {
          showToast('Izin kamera diperlukan.');
          return;
        }
        document.body.classList.add('camera-active');
        setSurveyMode('camera');
        await CameraPreview.start({
          position: 'rear',
          x: 0,
          y: 0,
          width: window.screen.width,
          height: window.screen.height,
          toBack: true,
          displayPreview: true,
          disableAudio: true
        });
        setCameraStream(true);
        showToast('📷 Kamera real-time aktif.');
      } catch (err) {
        showToast('⚠️ Kamera emulator tidak terdeteksi, membuka file picker.');
        setSurveyMode('form');
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) {
            try {
              const compressed = await compressImage(file);
              const previewUrl = URL.createObjectURL(compressed);
              setCapturedPreviewUrl(previewUrl);
              setSurveyMode('preview');
              showToast('✅ Foto berhasil dipilih & siap ditinjau.');
            } catch (ex) {}
          }
        };
        input.click();
      }
      return;
    }

    // Web fallback
    showToast('📷 Membuka kamera...');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';

    input.onchange = async (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              setCurrentPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
              showToast(`📍 GPS Realtime Terkunci: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
            },
            (err) => {},
            { enableHighAccuracy: true, timeout: 5000 }
          );
        }

        try {
          const compressed = await compressImage(file);
          const previewUrl = URL.createObjectURL(compressed);
          setCapturedPreviewUrl(previewUrl);
          setSurveyMode('preview');
          showToast('✅ Foto berhasil diambil & siap ditinjau.');
        } catch (err) {
          showToast('Gagal memproses foto.');
        }
      } else {
        showToast('⚠️ Pengambilan foto dibatalkan.');
      }
    };

    input.click();
  };

  const stopLiveCamera = async (forceFormMode = true) => {
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform();
    if (isNative) {
      try { await CameraPreview.stop(); document.body.classList.remove('camera-active'); } catch (e) {}
      setCameraStream(null);
      if (forceFormMode) setSurveyMode('form');
      return;
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
      if (forceFormMode) setSurveyMode('form');
    }
  };

  const captureFrameFromVideo = async () => {
    setShowCameraFlash(true);
    setTimeout(() => setShowCameraFlash(false), 100);

    // Fetch real-time GPS at capture moment
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCurrentPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          showToast(`📍 GPS Realtime Terkunci: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        },
        (err) => {},
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }

    const isNative = window.Capacitor && window.Capacitor.isNativePlatform();
    if (isNative) {
      try {
        const result = await CameraPreview.capture({ quality: 80 });
        const rawBase64 = result.value.startsWith('data:') ? result.value : `data:image/jpeg;base64,${result.value}`;
        const compressedBase64 = await compressBase64(rawBase64, 1400, 0.80);
        setCapturedPreviewUrl(compressedBase64);
        setSurveyMode('preview');
        await CameraPreview.stop();
        document.body.classList.remove('camera-active');
        setCameraStream(null);
        return;
      } catch (err) {}
    }

    const videoElement = document.getElementById('live-camera-video');
    if (!videoElement) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth || 1280;
    canvas.height = videoElement.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        const file = new File([blob], "survey.jpg", { type: 'image/jpeg' });
        const compressed = await compressImage(file);
        setCapturedPreviewUrl(URL.createObjectURL(compressed));
        setSurveyMode('preview');
        stopLiveCamera(false);
      } catch (e) {}
    }, 'image/jpeg', 0.85);
  };

  const handleConfirmCapturedPhoto = () => {
    if (!capturedPreviewUrl) return;
    if (photoStep === 'kwh') {
      setKwhPhotoUri(capturedPreviewUrl);
      setPhotoStep('house');
      setCapturedPreviewUrl(null);
      setSurveyMode('form');
    } else {
      setHousePhotoUri(capturedPreviewUrl);
      setCapturedPreviewUrl(null);
      setSurveyMode('form');
      setCurrentView('survey_form');
    }
  };

  const handleTriggerPhotoUpload = (step) => {
    setPhotoStep(step);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const compressed = await compressImage(file);
          const previewUrl = URL.createObjectURL(compressed);
          setCapturedPreviewUrl(previewUrl);
          setSurveyMode('preview');
        } catch (err) {
          showToast('Gagal memproses foto.');
        }
      }
    };
    input.click();
  };

  const handleBatchDeleteCustomers = async () => {
    if (selectedCustomerIds.length === 0) return;

    const idsStr = selectedCustomerIds.map(i => String(i).trim());
    const selectedObj = customers.filter(c => idsStr.includes(String(c.id).trim()) || (c.dil && idsStr.includes(String(c.dil).trim())));

    const pendingItems = selectedObj.filter(c => !c.status || c.status === 'pending');
    const surveyedItems = selectedObj.filter(c => c.status && c.status !== 'pending');

    if (pendingItems.length === 0 && surveyedItems.length > 0) {
      showToast(`⚠️ Seluruh ${surveyedItems.length} pelanggan terpilih SUDAH DISURVEI / MENUNGGU VALIDASI. Data survei dilindungi & tidak dihapus.`);
      return;
    }

    const pendingCount = pendingItems.length;
    const surveyedCount = surveyedItems.length;

    const confirmMsg = surveyedCount > 0
      ? `Yakin ingin menghapus ${pendingCount} TO pending terpilih? (${surveyedCount} data yang sudah disurvei/divalidasi TETAP DIJAGA AMAN)`
      : `Yakin ingin menghapus ${pendingCount} TO pending terpilih dari database?`;

    if (confirm(confirmMsg)) {
      const targetIdsStr = [];
      pendingItems.forEach(c => {
        if (c.id) targetIdsStr.push(String(c.id).trim());
        if (c.dil) targetIdsStr.push(String(c.dil).trim());
      });

      // Blacklist deleted pending IDs locally
      const newDeletedIds = [...new Set([...deletedIds, ...targetIdsStr])];
      setDeletedIds(newDeletedIds);
      secureStorage.setItem('pln_deleted_ids', newDeletedIds);

      // Remove only pending items from local state & storage
      const updated = customers.filter(c => {
        const cId = String(c.id || '').trim();
        const cDil = String(c.dil || '').trim();
        return !targetIdsStr.includes(cId) && !targetIdsStr.includes(cDil);
      });

      setCustomers(updated);
      secureStorage.setItem('pln_customers_db', updated);

      try {
        await postToApiCandidates('/api/customers/delete-batch', { ids: targetIdsStr });
        await fetchBackendData(true, true);
        showToast(`🗑️ Berhasil menghapus ${pendingCount} TO pending. ${surveyedCount > 0 ? `(${surveyedCount} data tervalidasi dilindungi & tetap aman)` : ''}`);
      } catch (e) {
        showToast(`🗑️ ${pendingCount} TO pending dihapus secara lokal.`);
      }
      setSelectedCustomerIds([]);
    }
  };

  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCustomerIds(filteredImportCustomers.map(c => c.id));
    } else {
      setSelectedCustomerIds([]);
    }
  };

  const handleToggleSelectCustomer = (id) => {
    const idStr = String(id);
    if (selectedCustomerIds.map(String).includes(idStr)) {
      setSelectedCustomerIds(selectedCustomerIds.filter(i => String(i) !== idStr));
    } else {
      setSelectedCustomerIds([...selectedCustomerIds, id]);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const cleanUsername = username.trim().toLowerCase();
    const found = users.find(u => String(u.username || '').trim().toLowerCase() === cleanUsername && String(u.pass || '').trim() === password.trim());
    if (found) {
      if (found.status === 'nonaktif') {
        showToast('⚠️ Akun Anda telah dinonaktifkan oleh Admin!');
        return;
      }
      setCurrentUser(found);
      secureStorage.setItem('pln_current_user', found);

      // Reset all search and filter queries on login
      setSurveyorSearchQuery('');
      setSurveyorDesaFilter('ALL');
      setSurveyorTab('semua');
      setImportSearchQuery('');
      setImportFilterDesa('Semua Desa');
      setImportFilterDate('');
      setValidationSearchQuery('');
      setValFilterStatus('ALL');

      if (isUserAdmin(found)) {
        setCurrentView('admin_dashboard');
        if (isMobile) {
          setAdminTab('dashboard');
        }
      } else {
        setCurrentView('surveyor_dashboard');
        fetchBackendData(true, true); // Automatically download & save all TOs locally on login!
      }
      showToast(`Login berhasil sebagai ${found.name}`);
    } else {
      showToast('Username atau Password salah!');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    secureStorage.removeItem('pln_current_user');
    setCurrentView('login');
    showToast('Berhasil keluar.');
  };

  const handleSelectCustomerForRoute = (cust) => {
    setSelectedCustomer(cust);
    setKwhPhotoUri(null);
    setHousePhotoUri(null);
    setSurveyNote('');
    setPhotoStep('kwh');
    setCurrentView('route');
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSurvey = async () => {
    if (isSaving) return;
    setIsSaving(true);
    showToast('⏳ Membaca GPS Real-Time & Menyimpan...');

    let freshLat = currentPos?.lat || selectedCustomer.lat;
    let freshLng = currentPos?.lng || selectedCustomer.lng;

    // Fetch fresh real-time GPS position directly from device hardware
    try {
      if (navigator.geolocation) {
        const freshPos = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (p) => resolve(p),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
          );
        });
        if (freshPos?.coords) {
          freshLat = freshPos.coords.latitude;
          freshLng = freshPos.coords.longitude;
        }
      }
    } catch (e) {}

    const now = new Date();
    const fullTimestamp = now.toISOString().split('T')[0] + ' ' + now.toTimeString().split(' ')[0].substring(0, 5);

    const isWasRejected = selectedCustomer.status === 'rejected' || !!selectedCustomer.validationNote;

    const updatedCustomer = {
      ...selectedCustomer,
      surveyorUsername: currentUser?.username || selectedCustomer.assignedTo || 'petugas1',
      assignedTo: currentUser?.username || selectedCustomer.assignedTo, // Locks assignment to the surveyor who conducted the survey
      status: 'menunggu_validasi',
      tgl_survey: fullTimestamp,
      photoKwh: kwhPhotoUri,
      photoHouse: housePhotoUri,
      isResurvey: isWasRejected,
      previousValidationNote: selectedCustomer.validationNote || selectedCustomer.previousValidationNote || null,
      validationNote: null, // Clear rejection note so it automatically disappears from Ditolak list
      surveyData: {
        isMatch: surveyMatch,
        note: surveyNote,
        photoKwh: kwhPhotoUri,
        photoHouse: housePhotoUri,
        lat: freshLat,
        lng: freshLng,
        surveyorUsername: currentUser?.username || 'petugas1'
      }
    };

    const updated = customers.map(c => c.id === selectedCustomer.id ? updatedCustomer : c);
    setCustomers(updated);
    secureStorage.setItem('pln_customers_db', updated);

    try {
      const res = await postToApiCandidates('/api/customers/update', updatedCustomer);
      if (res && res.success) {
        showToast('✅ Survei BERHASIL dikirim & ter-upload ke Server!');
      } else {
        showToast('⚠️ GAGAL UPLOAD ke Server! Data tersimpan Lokal di HP.');
      }
    } catch (e) {
      showToast('⚠️ GAGAL UPLOAD ke Server! Data tersimpan di HP (Offline).');
    }

    await fetchBackendData(true, true);

    setSelectedCustomer(null);
    setKwhPhotoUri(null);
    setHousePhotoUri(null);
    setPhotoStep('kwh');
    setIsSaving(false);
    setCurrentView('surveyor_dashboard');
  };

  const handleValidateCustomer = async (id, newStatus, note = '') => {
    const found = customers.find(c => String(c.id) === String(id) || String(c.dil) === String(id));
    if (!found) return;

    const updatedCustomer = { ...found, status: newStatus, validationNote: note || null };
    const updated = customers.map(c => (String(c.id) === String(id) || String(c.dil) === String(id)) ? updatedCustomer : c);
    setCustomers(updated);
    secureStorage.setItem('pln_customers_db', updated);

    try {
      await postToApiCandidates('/api/customers/update', updatedCustomer);
      await fetchBackendData(true, true);
      if (newStatus === 'sudah_divalidasi') {
        showToast('✅ Survei BERHASIL DISETUJUI & DIVALIDASI!');
      } else if (newStatus === 'rejected') {
        showToast('❌ Survei DITOLAK & catatan dikirim ke HP petugas!');
      } else {
        showToast(`✅ Status pelanggan diubah menjadi ${newStatus}.`);
      }
    } catch (e) {
      showToast(`⚠️ Status diubah secara lokal.`);
    }
  };

  const handleRecoverAndSyncSurveys = async () => {
    showToast('🔍 Memindai & memulihkan data survei tersimpan di HP...');

    let foundSurveys = [];

    try {
      const currentList = customers || [];
      const inState = currentList.filter(c => c && (c.status !== 'pending' || c.surveyData || c.tgl_survey));
      foundSurveys.push(...inState);

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('pln') || key.includes('customer') || key.includes('db'))) {
          try {
            const val = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(val)) {
              const surveyed = val.filter(c => c && (c.status !== 'pending' || c.surveyData || c.tgl_survey));
              foundSurveys.push(...surveyed);
            }
          } catch (e) {}
        }
      }
    } catch (e) {}

    const uniqueSurveysMap = {};
    foundSurveys.forEach(s => {
      if (s && (s.id || s.dil)) {
        const k = s.dil || s.id;
        uniqueSurveysMap[k] = s;
      }
    });

    const uniqueSurveys = Object.values(uniqueSurveysMap);

    if (uniqueSurveys.length === 0) {
      showToast('ℹ️ Tidak ditemukan data survei lokal yang tertinggal.');
      return;
    }

    const currentCusts = customers || [];
    const mergedState = currentCusts.map(c => {
      const rec = uniqueSurveys.find(u => String(u.id) === String(c.id) || (u.dil && String(u.dil) === String(c.dil)));
      if (rec && rec.status && rec.status !== 'pending') {
        return rec;
      }
      return c;
    });

    uniqueSurveys.forEach(rec => {
      const exists = mergedState.some(c => String(c.id) === String(rec.id) || (c.dil && String(c.dil) === String(rec.dil)));
      if (!exists) {
        mergedState.push(rec);
      }
    });

    setCustomers(mergedState);
    secureStorage.setItem('pln_customers_db', mergedState);

    showToast(`⏳ Meng-upload ${uniqueSurveys.length} survei terpulihkan ke server...`);

    let successCount = 0;
    for (const cust of uniqueSurveys) {
      const res = await postToApiCandidates('/api/customers/update', cust);
      if (res && res.success) successCount++;
    }

    await fetchBackendData(true, true);

    if (successCount > 0) {
      showToast(`✅ BERHASIL MEMULIHKAN & UPLOAD ${successCount} DATA SURVEI KE SERVER!`);
    } else {
      showToast(`✅ ${uniqueSurveys.length} survei lokal terpulihkan & tampil di layar.`);
    }
  };

  const handleSyncData = async () => {
    setIsSyncing(true);
    showToast('⏳ Meng-upload survei lokal & mengecek TO baru di server...');

    setSurveyorDesaFilter('ALL');
    setSurveyorSearchQuery('');
    setImportFilterDate('');
    setImportFilterDesa('Semua Desa');
    setImportSearchQuery('');

    const localCusts = customers || secureStorage.getItem('pln_customers_db') || [];
    const surveyedLocally = localCusts.filter(c => c && c.status && c.status !== 'pending');

    let uploadedCount = 0;

    // 1. ONLY upload surveyed items (status !== 'pending') to server
    if (surveyedLocally.length > 0) {
      for (const cust of surveyedLocally) {
        const res = await postToApiCandidates('/api/customers/update', cust);
        if (res && res.success) {
          uploadedCount++;
        }
      }
    }

    // 2. Re-fetch from server with force = true and silent = false to notify about new TOs
    await fetchBackendData(false, true);

    setIsSyncing(false);
    showToast(`✅ Sinkronisasi Selesai! ${uploadedCount} data survei ter-upload ke server.`);
  };

  const handleExportExcel = () => {
    if (adminTab === 'laporan') {
      showToast('📥 Mengunduh Laporan Realisasi & Kinerja Surveyor (Excel/CSV)...');
      const todayStr = new Date().toISOString().split('T')[0];
      const headers = "No,Unit (ULP),Nama Petugas Surveyor,Total TO (Target),Realisasi Harian (Hari Ini),Realisasi Kumulatif,Persentase Capaian (%)\n";

      const rows = users.filter(u => u && u.role === 'surveyor').map((surv, index) => {
        const assigned = customers.filter(c => isCustomerAssignedToSurveyor(c, surv));
        const total = assigned.length;

        const harian = assigned.filter(c => {
          if (!c || (!c.status || c.status === 'pending')) return false;
          const tgl = String(c.tgl_survey || c.tgl_input || '');
          return tgl.startsWith(todayStr);
        }).length;

        const kumulatif = assigned.filter(c => c && (c.status === 'sudah_divalidasi' || c.status === 'menunggu_validasi')).length;
        const pct = total > 0 ? Math.round((kumulatif / total) * 100) : 0;

        return `"${index + 1}","${surv.ulp || 'Tual Kota'}","${surv.name}","${total}","${harian}","${kumulatif}","${pct}%"`;
      }).join("\n");

      const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Laporan_Realisasi_Kinerja_Surveyor_PLN_${todayStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    showToast('📥 Mengunduh laporan validasi resmi (Excel/CSV)...');

    // Only export customers that have been validated (Sudah Divalidasi)
    const validatedList = getFilteredValidationCustomers().filter(c => c.status === 'sudah_divalidasi');

    if (validatedList.length === 0) {
      showToast('⚠️ Belum ada data pelanggan yang Selesai Divalidasi untuk di-export.');
      return;
    }

    const headers = "No,IDPEL,Nama Pelanggan,No. kWh Meter,Daya,Jenis kWh,Kategori TO,Unit / Desa,Petugas Surveyor,Waktu Survey,GPS DIL Awal,GPS Hasil Survey,Selisih Jarak,Status Validasi\n";
    const rows = validatedList.map((c, index) => {
      const sLat = c.surveyData?.lat || c.lat;
      const sLng = c.surveyData?.lng || c.lng;
      const dist = calculateDistance(c.lat, c.lng, sLat, sLng);
      return `"${index + 1}","${c.dil || '-'}","${c.name || '-'}","${c.kwh_meter_awal || c.kwh_meter || '-'}","${c.daya || '-'} VA","${c.tarif || 'PRABAYAR'}","${c.jenis_to || 'pelanggan_baru'}","${c.ulp || 'Tual Kota'} / ${c.desa || '-'}","${c.assignedTo || c.surveyorUsername || '-'}","${c.tgl_survey || '-'}","${c.lat}, ${c.lng}","${sLat}, ${sLng}","${dist} Meter","TERVALIDASI"`;
    }).join("\n");

    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Laporan_Validasi_PLN_Resmi_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReport = (title, columns, data) => {
    showToast('🖨️ Menyiapkan dokumen PDF Korporat...');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.print();
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title} - PLN-MAPPING</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; margin: 0; padding: 25px; background: white; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #218DAE; padding-bottom: 15px; margin-bottom: 25px; }
          h1 { font-size: 18px; font-weight: 900; color: #0f172a; margin: 0; text-transform: uppercase; letter-spacing: 0.5px; }
          p { font-size: 11px; color: #218DAE; margin: 4px 0 0 0; font-weight: 800; text-transform: uppercase; }
          .meta { text-align: right; font-size: 10px; color: #475569; font-weight: bold; line-height: 1.4; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 10px; }
          th { background-color: #218DAE; color: white; padding: 10px 8px; text-align: center; font-weight: 900; text-transform: uppercase; border: 1px solid #1a728c; letter-spacing: 0.5px; }
          td { padding: 8px; border: 1px solid #cbd5e1; text-align: center; font-weight: 600; color: #334155; }
          tr:nth-child(even) { background-color: #f8fafc; }
          .footer { margin-top: 50px; display: flex; justify-content: space-between; font-size: 11px; font-weight: bold; page-break-inside: avoid; }
          .sign-box { text-align: center; width: 220px; }
          .sign-line { margin-top: 70px; border-bottom: 1px solid #334155; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>PT PLN (PERSERO) - UP3 TUAL</h1>
            <p>${title}</p>
          </div>
          <div class="meta">
            <div>Dicetak Oleh: ${currentUser?.name || 'Administrator UP3 Tual'}</div>
            <div>Waktu: ${new Date().toLocaleString()}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              ${columns.map(col => `<th>${col}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                ${row.map(cell => `<td>${cell}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <div class="sign-box">
            <div>Petugas Pelapor / Validator</div>
            <div class="sign-line"></div>
          </div>
          <div class="sign-box">
            <div>Mengetahui,<br>Manager UP3 Tual</div>
            <div class="sign-line"></div>
          </div>
        </div>

        <script>
          window.onload = function() { window.print(); window.close(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownloadDilTemplate = () => {
    showToast('📥 Mengunduh Template CSV DIL Resmi PLN (14 Kolom)...');

    const csvHeaders = "ID_Pelanggan,KWh_Meter,Nama_Pelanggan,Daya,Tarif,Jenis_KWh,Alamat,Desa,Kelurahan_Kecamatan,Kabupaten_Kota,ID_ULP,Latitude,Longitude,KATEGORI_TO\n";
    const sampleRow1 = '"512001234501","14099990001","PT Budi Makmur Jaya","6600","B1","PRABAYAR","Jl. Merdeka No. 12","Menteng","Tual","Maluku Tenggara","Tual Kota","-5.62410","132.74700","pelanggan_baru"\n';
    const sampleRow2 = '"512009876543","14088880002","CV Maluku Sentosa","1300","R1","PRABAYAR","Jl. Soekarno Hatta No. 5","Un","Tual","Maluku Tenggara","Tual Kota","-5.62800","132.74900","pelanggan_lama"\n';

    const csvContent = csvHeaders + sampleRow1 + sampleRow2;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Template_Format_DIL_PLN_14_Kolom.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast('✅ Template CSV DIL (14 Kolom) berhasil diunduh!');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    showToast('⏳ Memproses file CSV DIL...');
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(line => line && line.trim());
      if (lines.length < 2) {
        showToast('Format CSV kosong atau tidak valid.');
        return;
      }

      // Auto-detect CSV delimiter (; or , or \t)
      const firstLine = lines[0];
      let delimiter = ',';
      if (firstLine.includes(';') && firstLine.split(';').length >= firstLine.split(',').length) {
        delimiter = ';';
      } else if (firstLine.includes('\t') && firstLine.split('\t').length >= firstLine.split(',').length) {
        delimiter = '\t';
      }

      const parseCSVLine = (line) => {
        const result = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === delimiter && !inQuotes) {
            result.push(cur.trim().replace(/^"|"$/g, ''));
            cur = '';
          } else {
            cur += char;
          }
        }
        result.push(cur.trim().replace(/^"|"$/g, ''));
        return result;
      };

      const rawHeaders = parseCSVLine(lines[0]);
      const headers = rawHeaders.map(h => String(h || '').toUpperCase().trim().replace(/^\uFEFF/, '').replace(/[^A-Z0-9]/g, ''));

      const findCol = (keys) => {
        for (let i = 0; i < headers.length; i++) {
          const h = headers[i];
          if (!h) continue;
          for (const k of keys) {
            const cleanK = k.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (h === cleanK || h.includes(cleanK) || cleanK.includes(h)) {
              return i;
            }
          }
        }
        return -1;
      };

      const idxDil = findCol(['ID_PELANGGAN', 'IDPEL', 'ID_PEL', 'NO_PELANGGAN', 'NOPEL', 'DIL', 'ID']);
      const idxKwh = findCol(['KWH_METER', 'NO_KWH_METER', 'NO_KWH', 'KWH_METER_AWAL', 'NO_METER', 'METER']);
      const idxName = findCol(['NAMA_PELANGGAN', 'NAMA_PEL', 'NAMA']);
      const idxDaya = findCol(['DAYA', 'VA']);
      const idxTarif = findCol(['TARIF', 'GOL_TARIF']);
      const idxJenisKwh = findCol(['JENIS_KWH', 'JENIS_METER', 'METER_TYPE']);
      const idxAlamat = findCol(['ALAMAT', 'ALAMAT_PELANGGAN']);
      const idxDesa = findCol(['DESA', 'KELURAHAN', 'NAMA_DESA']);
      const idxKec = findCol(['KELURAHAN_KECAMATAN', 'KECAMATAN', 'KEC']);
      const idxKab = findCol(['KABUPATEN_KOTA', 'KABUPATEN', 'KAB']);
      const idxUlp = findCol(['ID_ULP', 'ULP', 'UNIT', 'ULP_NAME']);
      const idxLat = findCol(['LATITUDE', 'LAT', 'Y']);
      const idxLng = findCol(['LONGITUDE', 'LNG', 'LON', 'X']);
      const idxTo = findCol(['KATEGORI_TO', 'JENIS_TO', 'TO', 'KATEGORI']);

      // Smart positional fallback if headers are not recognized:
      let smartIdxDil = idxDil;
      if (smartIdxDil === -1) {
        if (headers[0] === 'NO' || headers[0] === 'NOMOR' || headers[0] === 'NUM' || headers[0] === 'NO_') {
          smartIdxDil = 1;
        } else {
          smartIdxDil = 0;
        }
      }

      let smartIdxName = idxName;
      if (smartIdxName === -1) {
        if (smartIdxDil === 1) {
          smartIdxName = 2;
        } else {
          smartIdxName = 1;
        }
      }

      const parsed = [];
      const nowStr = new Date().toISOString().split('T')[0];

      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        if (cols.length >= 2) {
          const dilVal = (smartIdxDil >= 0 && cols[smartIdxDil] ? cols[smartIdxDil] : cols[0]) || '';
          const nameVal = (smartIdxName >= 0 && cols[smartIdxName] ? cols[smartIdxName] : (cols[2] || cols[1])) || '';
          if (!dilVal && !nameVal) continue;

          const itemUlp = (idxUlp >= 0 && cols[idxUlp] ? cols[idxUlp] : cols[10]) || 'Tual Kota';
          const autoAssigned = getAssignedSurveyorByUlp(itemUlp, users);

          parsed.push({
            id: Date.now() + i + Math.floor(Math.random() * 10000),
            dil: String(dilVal).trim(),
            kwh_meter_awal: (idxKwh >= 0 ? cols[idxKwh] : cols[1]) || '',
            name: String(nameVal).trim(),
            daya: (idxDaya >= 0 ? cols[idxDaya] : cols[3]) || '',
            tarif: (idxTarif >= 0 ? cols[idxTarif] : cols[4]) || 'PRABAYAR',
            jenis_kwh: (idxJenisKwh >= 0 ? cols[idxJenisKwh] : 'PRABAYAR') || 'PRABAYAR',
            alamat: (idxAlamat >= 0 ? cols[idxAlamat] : cols[6]) || '',
            desa: (idxDesa >= 0 ? cols[idxDesa] : cols[7]) || 'KOLSER',
            kec: (idxKec >= 0 ? cols[idxKec] : cols[8]) || 'Tual',
            kab: (idxKab >= 0 ? cols[idxKab] : cols[9]) || 'Maluku Tenggara',
            ulp: itemUlp,
            lat: parseFloat(idxLat >= 0 ? cols[idxLat] : cols[11]) || -5.6241,
            lng: parseFloat(idxLng >= 0 ? cols[idxLng] : cols[12]) || 132.7470,
            jenis_to: (idxTo >= 0 ? cols[idxTo] : cols[13]) || 'pelanggan_baru',
            assignedTo: autoAssigned,
            status: 'pending',
            tgl_input: nowStr
          });
        }
      }

      setCsvData(parsed);
      showToast(`✅ ${parsed.length} baris data DIL berhasil dimuat! Siapkan distribusi.`);
    };
    reader.readAsText(file);
  };

  const handleDistributeData = async () => {
    try {
      if (!csvData || csvData.length === 0) return;

      showToast(`⏳ Meng-upload & mendistribusikan ${csvData.length} TO baru ke server...`);

      const nowStr = new Date().toISOString().split('T')[0];

      const currentCusts = customers || [];
      const newItems = [];
      const newDilsToUnblacklist = [];

      csvData.forEach((item, idx) => {
        const cleanDil = String(item.dil || '').trim();
        const cleanName = String(item.name || '').trim().toUpperCase();

        if (!cleanDil && !cleanName) return;

        const autoAssigned = getAssignedSurveyorByUlp(item.ulp, users);
        const newItem = {
          ...item,
          id: item.id || (Date.now() + idx + Math.floor(Math.random() * 10000)),
          dil: cleanDil || String(Date.now() + idx),
          name: cleanName || 'PELANGGAN BARU',
          status: 'pending',
          assignedTo: item.assignedTo || autoAssigned,
          ulp: item.ulp || 'Tual Kota',
          tgl_input: item.tgl_input || nowStr,
          isNewUpload: true
        };

        newItems.push(newItem);
        if (cleanDil) newDilsToUnblacklist.push(cleanDil);
        if (newItem.id) newDilsToUnblacklist.push(String(newItem.id));
      });

      if (newItems.length === 0) {
        showToast('⚠️ Data CSV tidak berisi IDPEL / Nama yang valid.');
        return;
      }

      // Un-blacklist uploaded DILs locally
      if (newDilsToUnblacklist.length > 0) {
        const currentDeleted = secureStorage.getItem('pln_deleted_ids') || deletedIds || [];
        const cleanDeleted = Array.isArray(currentDeleted)
          ? currentDeleted.filter(d => !newDilsToUnblacklist.includes(String(d).trim()))
          : [];
        setDeletedIds(cleanDeleted);
        secureStorage.setItem('pln_deleted_ids', cleanDeleted);
      }

      const existingMap = new Map();
      currentCusts.forEach(c => {
        if (c) {
          const k = String(c.dil || c.id || '').trim();
          if (k) existingMap.set(k, c);
        }
      });

      let addedCount = 0;
      newItems.forEach(item => {
        const k = String(item.dil || item.id || '').trim();
        if (!existingMap.has(k)) {
          existingMap.set(k, item);
          addedCount++;
        } else {
          const existing = existingMap.get(k);
          if (!existing.status || existing.status === 'pending') {
            existingMap.set(k, { ...existing, ...item });
          }
        }
      });

      const rawUpdated = Array.from(existingMap.values());
      const updated = distributeCustomersEvenlyByUlp(rawUpdated, users);

      setCustomers(updated);
      secureStorage.setItem('pln_customers_db', updated);

      // Send new TOs in small chunks (300 items per batch) for 100% reliable upload of large 1MB+ CSV files
      const CHUNK_SIZE = 300;
      let uploadSuccessCount = 0;

      for (let i = 0; i < newItems.length; i += CHUNK_SIZE) {
        const chunk = newItems.slice(i, i + CHUNK_SIZE);
        const chunkNum = Math.floor(i / CHUNK_SIZE) + 1;
        const totalChunks = Math.ceil(newItems.length / CHUNK_SIZE);

        showToast(`⏳ Meng-upload ke server (Batch ${chunkNum}/${totalChunks})...`);

        const uploadRes = await postToApiCandidates('/api/customers', chunk);
        if (uploadRes && uploadRes.success) {
          uploadSuccessCount += chunk.length;
        }
      }

      setCsvData([]);
      secureStorage.setItem('pln_last_action_time', Date.now());

      // Reset filters & switch directly to Sub Tab 3 on Page 1
      setImportFilterDate('');
      setImportFilterDesa('Semua Desa');
      setImportSearchQuery('');
      setImportPage(1);
      setImportDilSubTab('edit');

      if (uploadSuccessCount > 0) {
        showToast(`✅ BERHASIL UPLOAD & DISTRIBUSI ${newItems.length} TO BARU KE SERVER! Total database: ${updated.length} TO.`);
      } else {
        showToast(`✅ ${newItems.length} TO baru berhasil tersimpan secara lokal.`);
      }
    } catch (distErr) {
      console.error("handleDistributeData Error:", distErr);
      showToast(`⚠️ Terjadi kesalahan saat distribusi: ${distErr.message || 'Gagal memproses data CSV'}`);
    }
  };

  const handleAutoRedistributeToSurveyors = async () => {
    if (!confirm('Bagi rata seluruh TO pending ke petugas survei aktif sesuai ULP masing-masing?')) return;
    showToast('⏳ Mendistribusikan TO ke masing-masing petugas survei...');

    const distributed = distributeCustomersEvenlyByUlp(customers, users);
    setCustomers(distributed);
    secureStorage.setItem('pln_customers_db', distributed);

    await postToApiCandidates('/api/customers', distributed);
    await postToApiCandidates('/api/customers/auto-assign-ulp', {});
    await fetchBackendData(true, true);

    showToast('✅ Berhasil membagi rata seluruh TO ke petugas survei aktif sesuai ULP!');
  };

  const handleExportPDF = () => {
    if (adminTab === 'validasi') {
      const validatedList = getFilteredValidationCustomers().filter(c => c.status === 'sudah_divalidasi');

      if (validatedList.length === 0) {
        showToast('⚠️ Belum ada data pelanggan yang Selesai Divalidasi untuk di-export.');
        return;
      }

      const cols = ['No', 'IDPEL', 'Nama Pelanggan', 'No. kWh Meter', 'Daya', 'Jenis kWh', 'Unit / Desa', 'Petugas', 'Waktu Survey', 'GPS DIL', 'GPS Survey', 'Selisih Jarak', 'Status'];
      const rows = validatedList.map((c, idx) => {
        const sLat = c.surveyData?.lat || c.lat;
        const sLng = c.surveyData?.lng || c.lng;
        const dist = calculateDistance(c.lat, c.lng, sLat, sLng);
        return [
          idx + 1,
          c.dil || '-',
          c.name || '-',
          c.kwh_meter_awal || c.kwh_meter || '-',
          `${c.daya || '-'} VA`,
          c.tarif || 'PRABAYAR',
          `${c.ulp || 'Tual Kota'} / ${c.desa || '-'}`,
          c.assignedTo || c.surveyorUsername || '-',
          c.tgl_survey || '-',
          `${c.lat}, ${c.lng}`,
          `${sLat}, ${sLng}`,
          `${dist} m`,
          'TERVALIDASI'
        ];
      });
      handlePrintReport('Laporan Validasi Hasil Survei Lapangan Resmi', cols, rows);
    } else if (adminTab === 'laporan') {
      const cols = ['No', 'Unit (ULP)', 'Nama Petugas', 'Total TO (Target)', 'Realisasi Harian (Hari Ini)', 'Realisasi Kumulatif', 'Persentase Capaian (%)'];
      const todayStr = new Date().toISOString().split('T')[0];

      const rows = users.filter(u => u && u.role === 'surveyor').map((surv, idx) => {
        const assigned = customers.filter(c => isCustomerAssignedToSurveyor(c, surv));
        const total = assigned.length;

        const harian = assigned.filter(c => {
          if (!c || (!c.status || c.status === 'pending')) return false;
          const tgl = String(c.tgl_survey || c.tgl_input || '');
          return tgl.startsWith(todayStr);
        }).length;

        const kumulatif = assigned.filter(c => c && (c.status === 'sudah_divalidasi' || c.status === 'menunggu_validasi')).length;
        const pct = total > 0 ? Math.round((kumulatif / total) * 100) : 0;

        return [
          idx + 1,
          surv.ulp || 'Tual Kota',
          surv.name,
          `${total} TO`,
          `${harian} TO`,
          `${kumulatif} TO`,
          `${pct}%`
        ];
      });
      handlePrintReport('Laporan Realisasi & Kinerja Surveyor Resmi', cols, rows);
    } else {
      showToast('📥 Menyiapkan dokumen PDF...');
      window.print();
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const l1 = parseFloat(lat1);
    const n1 = parseFloat(lon1);
    const l2 = parseFloat(lat2);
    const n2 = parseFloat(lon2);

    if (isNaN(l1) || isNaN(n1) || isNaN(l2) || isNaN(n2)) return 999999;

    const R = 6371e3;
    const φ1 = (l1 * Math.PI) / 180;
    const φ2 = (l2 * Math.PI) / 180;
    const Δφ = ((l2 - l1) * Math.PI) / 180;
    const Δλ = ((n2 - n1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(Math.max(0, Math.min(1, a))), Math.sqrt(Math.max(0, Math.min(1, 1 - a))));
    const dist = Math.round(R * c);
    return isNaN(dist) ? 999999 : dist;
  };

  const getFilteredImportCustomers = () => {
    let list = customers.filter(c => c && c.name && !c.name.toUpperCase().includes('ADMINISTRATOR') && !c.role);

    if (importDilSubTab === 'edit') {
      // Sub Tab 3: HANYA TO PENDING TERKINI (Belum Disurvei)
      list = list.filter(c => !c.status || c.status === 'pending');
    } else if (importDilSubTab === 'riwayat') {
      // Sub Tab 2: RIWAYAT PELANGGAN YANG SUDAH DISURVEI / TERVALIDASI / MENUNGGU VALIDASI
      list = list.filter(c => (c.status && c.status !== 'pending') || c.surveyData || c.tgl_survey);
    }

    if (importFilterDate) {
      list = list.filter(c => c.tgl_input === importFilterDate || (c.tgl_survey && c.tgl_survey.startsWith(importFilterDate)));
    }
    if (importFilterDesa !== 'Semua Desa') {
      list = list.filter(c => c.desa === importFilterDesa);
    }
    if (deferredImportQuery.trim()) {
      const q = deferredImportQuery.toLowerCase().trim();
      list = list.filter(c =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.dil && c.dil.toLowerCase().includes(q)) ||
        (c.alamat && c.alamat.toLowerCase().includes(q))
      );
    }
    return list;
  };
  const filteredImportCustomers = useMemo(() => {
    return getFilteredImportCustomers();
  }, [customers, importDilSubTab, importFilterDate, importFilterDesa, importSearchQuery]);

  const filteredValidationCustomers = useMemo(() => {
    return getFilteredValidationCustomers();
  }, [customers, valFilterStatus, valFilterPetugas, valFilterStartDate, valFilterEndDate, valFilterDesa, valFilterUnit, validationSearchQuery]);

  const totalValPages = useMemo(() => Math.max(1, Math.ceil(filteredValidationCustomers.length / 50)), [filteredValidationCustomers]);
  const totalImportPages = useMemo(() => Math.max(1, Math.ceil(filteredImportCustomers.length / 50)), [filteredImportCustomers]);

  const safeValPage = useMemo(() => Math.min(Math.max(1, valPage), totalValPages), [valPage, totalValPages]);
  const safeImportPage = useMemo(() => Math.min(Math.max(1, importPage), totalImportPages), [importPage, totalImportPages]);

  const paginatedValidationCustomers = useMemo(() => {
    const start = (safeValPage - 1) * 50;
    return filteredValidationCustomers.slice(start, start + 50);
  }, [filteredValidationCustomers, safeValPage]);

  const paginatedImportCustomers = useMemo(() => {
    const start = (safeImportPage - 1) * 50;
    return filteredImportCustomers.slice(start, start + 50);
  }, [filteredImportCustomers, safeImportPage]);

  const sortedSurveyorList = useMemo(() => {
    try {
      if (!Array.isArray(customers) || !currentUser) return [];

      let list = customers.filter(c => c && c.name);

      if (surveyorTab === 'semua') {
        list = list.filter(c => isCustomerAssignedToSurveyor(c, currentUser) && (!c.status || c.status === 'pending'));
      } else {
        list = list.filter(c => isRejectedForCurrentUser(c, currentUser));
      }

      if (surveyorDesaFilter !== 'ALL') {
        list = list.filter(c => c.desa === surveyorDesaFilter);
      }

      if (surveyorSearchQuery && surveyorSearchQuery.trim()) {
        const query = surveyorSearchQuery.toLowerCase().trim();
        list = list.filter(c =>
          (c.name && c.name.toLowerCase().includes(query)) ||
          (c.dil && String(c.dil).toLowerCase().includes(query)) ||
          (c.kwh_meter_awal && String(c.kwh_meter_awal).toLowerCase().includes(query))
        );
      }

      list = list.map(c => ({
        ...c,
        calculatedDist: (currentPos && typeof currentPos.lat === 'number' && typeof currentPos.lng === 'number')
          ? calculateDistance(currentPos.lat, currentPos.lng, c.lat, c.lng)
          : 0
      }));

      if (isNearestFilter) {
        list.sort((a, b) => (a.calculatedDist || 0) - (b.calculatedDist || 0));
      }

      return list;
    } catch (e) {
      console.error("sortedSurveyorList error:", e);
      return [];
    }
  }, [customers, surveyorTab, surveyorDesaFilter, surveyorSearchQuery, isNearestFilter, currentPos, currentUser]);

  
  const surveyorStats = useMemo(() => {
    let pending = 0;
    let completed = 0;
    let rejected = 0;

    if (!currentUser || !Array.isArray(customers)) {
      return { pending: lastValidPendingCount.current, completed: 0, rejected: 0 };
    }

    for (let i = 0; i < customers.length; i++) {
      const c = customers[i];
      if (!c) continue;
      if (isCustomerAssignedToSurveyor(c, currentUser) && (!c.status || c.status === 'pending')) {
        pending++;
      }
      if (isCompletedByCurrentUser(c, currentUser)) {
        completed++;
      }
      if (isRejectedForCurrentUser(c, currentUser)) {
        rejected++;
      }
    }

    if (pending > 0) {
      lastValidPendingCount.current = pending;
    } else if (pending === 0 && lastValidPendingCount.current > 0) {
      // Smooth out split-second zero flashes during sync
      pending = lastValidPendingCount.current;
    }

    return { pending, completed, rejected };
  }, [customers, currentUser]);

  const visibleSurveyorCustomers = useMemo(() => {
    return sortedSurveyorList.slice(0, visibleSurveyorLimit);
  }, [sortedSurveyorList, visibleSurveyorLimit]);

  return (
    <div className={`min-h-screen w-screen flex flex-col overflow-x-hidden m-0 p-0 font-sans ${surveyMode === 'camera' ? 'bg-transparent' : (darkMode ? 'bg-slate-950 text-white' : 'bg-slate-100 text-slate-800')}`}>
      <div className="flex-1 w-full relative flex flex-col overflow-hidden">

        {toast && (
          <div className="absolute top-4 left-4 right-4 z-50">
            <div className="bg-[#218DAE] text-white px-4 py-3 rounded-xl shadow-xl text-xs font-bold flex items-center">
              <CheckCircle2 size={16} className="mr-2 shrink-0" /> {toast}
            </div>
          </div>
        )}

        {/* 1. LOGIN VIEW with Professional PLN Field Surveyor Aesthetic */}
        {currentView === 'login' && (
          <div className="relative w-screen h-screen flex items-center justify-center overflow-hidden bg-slate-950">
            <div
              className="absolute inset-0 bg-cover bg-center filter brightness-90 scale-105"
              style={{ backgroundImage: `url('https://images.unsplash.com/photo-1581092335397-9583fe92d232?q=80&w=1600&auto=format&fit=crop')` }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-900/80 to-[#218DAE]/40"></div>

            <div className="relative z-10 w-full h-full flex items-center justify-center px-6">
              <div className="w-full max-w-[440px] bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl space-y-6 text-slate-800 border border-white/20">
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center space-x-2.5">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/20/Logo_PLN.svg" alt="PLN Logo" className="h-10 w-auto" />
                    <div className="text-left">
                      <h2 className="text-lg font-black tracking-tight text-slate-900">PLN-MAPPING</h2>
                      <span className="text-[10px] font-bold text-[#218DAE] uppercase tracking-widest">Geospasial Asset v3.2</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 font-semibold pt-1">Portal Survei & Pemetaan Aset kWh Meter Lapangan (UP3 Tual)</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Username</label>
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border text-xs font-semibold outline-none bg-slate-50 border-slate-300 focus:border-[#218DAE]" placeholder="Masukkan Username" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Password</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border text-xs font-semibold outline-none bg-slate-50 border-slate-300 focus:border-[#218DAE]" placeholder="Masukkan Password" required />
                  </div>
                  <button type="submit" className="w-full py-4 bg-[#218DAE] text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-110 transition">
                    Masuk Aplikasi
                  </button>
                </form>

                <div className="pt-2 border-t border-slate-100 text-center text-[11px] text-slate-400 font-medium">
                  Dilengkapi GPS Geospasial & Dokumentasi Lapangan Verifikasi KWh Meter
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. ADMIN DASHBOARD VIEW (Exact Reference Color #218DAE) */}
        {isAdmin && currentView === 'admin_dashboard' && (
          <div className="flex h-screen w-full overflow-hidden bg-slate-100 text-slate-800">
            {/* Sidebar for Desktop (Hidden on Mobile) */}
            <aside className="hidden md:flex flex-col w-64 bg-[#218DAE] text-white shrink-0 select-none">
              <div className="p-6 flex items-center space-x-3">
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/20/Logo_PLN.svg" alt="PLN Logo" className="h-7 w-auto" />
                <div>
                  <h2 className="font-extrabold text-sm tracking-tight text-white leading-tight">PLN-MAPPING</h2>
                  <span className="text-[9px] font-bold text-cyan-100 uppercase tracking-widest opacity-90">GEOSPATIAL ASSET V3.2</span>
                </div>
              </div>

              <nav className="flex-1 px-4 py-2 space-y-1.5 overflow-y-auto">
                <button
                  onClick={() => setAdminTab('dashboard')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'dashboard' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <LayoutDashboard size={18} />
                  <span>Dashboard & Realisasi</span>
                </button>
                <button
                  onClick={() => setAdminTab('pengguna')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'pengguna' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <Users size={18} />
                  <span>Kelola Pengguna</span>
                </button>
                <button
                  onClick={() => setAdminTab('validasi')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'validasi' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <CheckSquare size={18} />
                  <span>Validasi Hasil Survei</span>
                </button>
                <button
                  onClick={() => setAdminTab('import_dil')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'import_dil' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <CloudUpload size={18} />
                  <span>Import DIL Pelanggan</span>
                </button>
                <button
                  onClick={() => setAdminTab('surat_tugas')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'surat_tugas' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <FileText size={18} />
                  <span>Surat Tugas</span>
                </button>
                <button
                  onClick={() => setAdminTab('laporan')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'laporan' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <BarChart3 size={18} />
                  <span>Laporan Realisasi</span>
                </button>
                <button
                  onClick={() => { setAdminTab('event_logs'); fetchEventLogs(); }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-2xl text-xs font-bold transition ${adminTab === 'event_logs' ? 'bg-white text-slate-900 shadow-sm' : 'text-white hover:bg-white/10'}`}
                >
                  <FileCode size={18} />
                  <span>Log Aktivitas Database</span>
                </button>
              </nav>

              <div className="p-4 space-y-2 border-t border-white/10">
                <div className="flex items-center justify-between px-3 py-2 text-xs font-bold">
                  <span>Mode Gelap</span>
                  <button onClick={() => setDarkMode(!darkMode)} className="p-1.5 bg-white/10 rounded-lg hover:bg-white/25 transition">
                    {darkMode ? <Sun size={15} /> : <Moon size={15} />}
                  </button>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 bg-white text-[#218DAE] rounded-2xl font-black text-xs hover:bg-slate-100 transition shadow-sm"
                >
                  <LogOut size={16} />
                  <span>Keluar Aplikasi</span>
                </button>
              </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col h-full overflow-y-auto bg-slate-50">
              {/* Top Bar for Admin */}
              <header className="px-5 sm:px-8 py-4 bg-white border-b border-slate-200 flex justify-between items-center sticky top-0 z-30 shadow-xs">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-[#218DAE] text-white flex items-center justify-center font-black text-base shrink-0 md:hidden">
                    {currentUser?.name?.charAt(0)}
                  </div>
                  <div>
                    <h1 className="text-sm font-black text-slate-900">{currentUser?.username || 'ren_tual'}</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">PORTAL WEB ADMIN UP3 TUAL</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={async () => {
                      if (confirm('Pulihkan Database Master TO ke Server? (Seluruh hasil survei/validasi TETAP AMAN & DILINDUNGI)')) {
                        showToast('⏳ Memulihkan Database Master TO...');
                        setDeletedIds([]);
                        secureStorage.setItem('pln_deleted_ids', []);
                        const res = await postToApiCandidates('/api/database/restore-master', {});
                        if (res && res.success && res.data && res.data.success) {
                          const pCount = res.data.pendingCount || 0;
                          const sCount = res.data.surveyedCount || 0;
                          const tCount = res.data.totalCount || (pCount + sCount);
                          showToast(`✅ DATABASE MASTER DIPULIHKAN! Menampilkan ${pCount} TO pending di Tab 3 (${sCount > 0 ? `dikurangi ${sCount} TO yang sudah disurvei di Tab 2` : 'utuh'}). Total: ${tCount} TO.`);
                          await fetchBackendData(true, true);
                        } else {
                          showToast('⚠️ Gagal terhubung ke server. Pastikan node server.js berjalan di Laptop Server.');
                        }
                      }
                    }}
                    className="px-3 sm:px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-xl text-xs font-bold flex items-center space-x-1.5 shadow-xs transition"
                    title="Pulihkan Database Master TO Pelanggan"
                  >
                    <RefreshCw size={14} className="text-blue-600" />
                    <span className="hidden sm:inline">Restore Database Master</span>
                  </button>
                  <button
                    onClick={async () => {
                      showToast('⏳ Memproses backup data harian...');
                      const res = await postToApiCandidates('/api/backup/trigger', {});
                      if (res && res.success && res.data && res.data.success) {
                        showToast(`✅ Backup berhasil disimpan di folder backup_pln-mapping! (${res.data.timestamp || 'Baru Saja'})`);
                      } else {
                        showToast('⚠️ Gagal terhubung ke server.');
                      }
                    }}
                    className="px-3 sm:px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-xl text-xs font-bold flex items-center space-x-1.5 shadow-xs transition"
                    title="Backup Data Ke Folder backup_pln-mapping"
                  >
                    <Save size={14} className="text-emerald-600" />
                    <span className="hidden sm:inline">Backup Server</span>
                  </button>
                  <button onClick={handleSyncData} className="px-3 sm:px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs font-bold flex items-center space-x-2 shadow-xs transition">
                    <RefreshCw size={14} className="text-[#218DAE]" />
                    <span className="hidden sm:inline">Sinkronisasi</span>
                  </button>
                </div>
              </header>

              {/* Tab Contents matching reference screenshots precisely */}
              <main className="p-4 sm:p-8 space-y-6 w-full pb-28 md:pb-8">
                {/* 1. DASHBOARD & REALISASI */}
                {adminTab === 'dashboard' && (
                  <div className="space-y-6">
                    {/* Top 4 Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90">TOTAL PELANGGAN</span>
                          <Users size={20} className="opacity-80" />
                        </div>
                        <div className="text-4xl font-black tracking-tight">{customers.length}</div>
                      </div>

                      <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90">MENUNGGU SURVEY</span>
                          <Clock size={20} className="opacity-80" />
                        </div>
                        <div className="text-4xl font-black tracking-tight">{customers.filter(c => !c.status || c.status === 'pending').length}</div>
                      </div>

                      <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90">MENUNGGU VALIDASI</span>
                          <RefreshCw size={20} className="opacity-80" />
                        </div>
                        <div className="text-4xl font-black tracking-tight">{customers.filter(c => c.status === 'menunggu_validasi' || c.status === 'surveyed' || c.status === 'synced').length}</div>
                      </div>

                      <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90">TERVALIDASI</span>
                          <CheckCircle2 size={20} className="opacity-80" />
                        </div>
                        <div className="text-4xl font-black tracking-tight">{customers.filter(c => c.status === 'sudah_divalidasi').length}</div>
                      </div>
                    </div>

                    {/* 3 ULP Progress Cards */}
                    {(() => {
                      const tualCusts = customers.filter(c => (c.ulp || '').toLowerCase().includes('tual') || !(c.ulp));
                      const tualTotal = tualCusts.length;
                      const tualSelesai = tualCusts.filter(c => c.status === 'sudah_divalidasi').length;
                      const tualPct = tualTotal > 0 ? Math.round((tualSelesai / tualTotal) * 100) : 0;

                      const elatCusts = customers.filter(c => (c.ulp || '').toLowerCase().includes('elat'));
                      const elatTotal = elatCusts.length;
                      const elatSelesai = elatCusts.filter(c => c.status === 'sudah_divalidasi').length;
                      const elatPct = elatTotal > 0 ? Math.round((elatSelesai / elatTotal) * 100) : 0;

                      const doboCusts = customers.filter(c => (c.ulp || '').toLowerCase().includes('dobo'));
                      const doboTotal = doboCusts.length;
                      const doboSelesai = doboCusts.filter(c => c.status === 'sudah_divalidasi').length;
                      const doboPct = doboTotal > 0 ? Math.round((doboSelesai / doboTotal) * 100) : 0;

                      return (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm space-y-4">
                            <div className="flex justify-between items-center text-xs font-black uppercase tracking-wider">
                              <span>ULP TUAL KOTA</span>
                              <span>{tualPct}%</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold opacity-90">
                              <span>TOTAL TO: {tualTotal}</span>
                              <span>REALISASI: {tualSelesai}</span>
                            </div>
                            <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                              <div className="bg-white h-full" style={{ width: `${tualPct}%` }}></div>
                            </div>
                          </div>

                          <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm space-y-4">
                            <div className="flex justify-between items-center text-xs font-black uppercase tracking-wider">
                              <span>ULP ELAT</span>
                              <span>{elatPct}%</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold opacity-90">
                              <span>TOTAL TO: {elatTotal}</span>
                              <span>REALISASI: {elatSelesai}</span>
                            </div>
                            <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                              <div className="bg-white h-full" style={{ width: `${elatPct}%` }}></div>
                            </div>
                          </div>

                          <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm space-y-4">
                            <div className="flex justify-between items-center text-xs font-black uppercase tracking-wider">
                              <span>ULP DOBO</span>
                              <span>{doboPct}%</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold opacity-90">
                              <span>TOTAL TO: {doboTotal}</span>
                              <span>REALISASI: {doboSelesai}</span>
                            </div>
                            <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                              <div className="bg-white h-full" style={{ width: `${doboPct}%` }}></div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Trend Line Section */}
                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">Trend Line Realisasi Survey Kumulatif Per Hari</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">Grafik Statistik Hasil Survei Harian Seluruh Petugas Lapangan</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-1 bg-cyan-50 text-[#218DAE] rounded-md text-[10px] font-black uppercase">
                            <TrendingUp size={12} className="mr-1" /> Real-time Chart
                          </span>
                        </div>
                      </div>

                      {(() => {
                        // Group surveyed customers by date (YYYY-MM-DD)
                        const surveyedCusts = customers.filter(c => c.tgl_survey && (c.status === 'sudah_divalidasi' || c.status === 'menunggu_validasi' || c.status === 'surveyed'));

                        const dateMap = {};
                        surveyedCusts.forEach(c => {
                          const d = c.tgl_survey.split(' ')[0];
                          if (d) dateMap[d] = (dateMap[d] || 0) + 1;
                        });

                        const sortedDates = Object.keys(dateMap).sort();

                        if (sortedDates.length === 0) {
                          return (
                            <div className="h-52 flex flex-col items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-6 space-y-2 text-center">
                              <BarChart3 size={32} className="text-slate-300" />
                              <h4 className="text-xs font-black text-slate-600 uppercase">Belum ada data grafik survei harian</h4>
                              <p className="text-[10px] text-slate-400 font-bold max-w-sm">Grafik garis trend harian akan otomatis terbentuk begitu petugas lapangan menyelesaikan survei pertamanya.</p>
                            </div>
                          );
                        }

                        const chartData = sortedDates.map(d => ({
                          date: d,
                          label: new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
                          count: dateMap[d]
                        }));

                        const maxCount = Math.max(...chartData.map(item => item.count), 5);

                        return (
                          <div className="space-y-3 pt-2">
                            {/* SVG Bar & Line Chart Container */}
                            <div className="h-52 w-full bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col justify-between">
                              <div className="flex-1 flex items-end justify-around space-x-2 pt-6">
                                {chartData.map((item, idx) => {
                                  const heightPct = Math.max(12, Math.round((item.count / maxCount) * 100));
                                  return (
                                    <div key={idx} className="flex-1 flex flex-col items-center group relative cursor-pointer">
                                      {/* Tooltip on Hover */}
                                      <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-[10px] font-black py-1 px-2.5 rounded-lg shadow-xl whitespace-nowrap z-20 pointer-events-none">
                                        {item.label}: <span className="text-cyan-300 font-mono">{item.count} Survei</span>
                                      </div>

                                      {/* Bar Column */}
                                      <div className="w-full max-w-[48px] bg-slate-200 rounded-t-xl overflow-hidden flex items-end transition-all duration-300 group-hover:bg-cyan-100">
                                        <div
                                          className="w-full bg-[#218DAE] group-hover:bg-cyan-500 rounded-t-xl transition-all duration-500 shadow-sm flex items-center justify-center text-[10px] font-black text-white"
                                          style={{ height: `${heightPct}%` }}
                                        >
                                          {item.count > 0 && <span className="text-[9px] font-mono">{item.count}</span>}
                                        </div>
                                      </div>

                                      {/* Date Label */}
                                      <span className="text-[10px] font-black text-slate-500 mt-2 uppercase tracking-wider">{item.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 px-1">
                              <span>Total Hari Terekam: <strong className="text-slate-800 font-black">{chartData.length} Hari</strong></span>
                              <span>Total Hasil Survei: <strong className="text-[#218DAE] font-black">{surveyedCusts.length} Survei</strong></span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Realisasi Petugas Lapangan Section */}
                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">Realisasi Petugas Lapangan</h3>
                      <div className="space-y-3">
                        {users.filter(u => u.role === 'surveyor').map((surv, idx) => {
                          const assignedCusts = customers.filter(c => isCustomerAssignedToSurveyor(c, surv));
                          const totalAssigned = assignedCusts.length;

                          // Realisasi spesifik yang fisik dikerjakan oleh petugas ini
                          const survDoneCusts = customers.filter(c =>
                            (c.surveyorUsername && c.surveyorUsername === surv.username) ||
                            (c.assignedTo === surv.username && c.status !== 'pending')
                          );

                          const tervalidasi = survDoneCusts.filter(c => c.status === 'sudah_divalidasi').length;
                          const menungguVal = survDoneCusts.filter(c => c.status === 'menunggu_validasi').length;
                          const totalDone = tervalidasi + menungguVal;

                          const todayStr = new Date().toISOString().split('T')[0];
                          const harianCount = survDoneCusts.filter(c => c.tgl_survey && c.tgl_survey.startsWith(todayStr)).length;

                          const pct = totalAssigned > 0 ? Math.round((tervalidasi / totalAssigned) * 100) : 0;

                          return (
                            <div key={surv.id} className="p-4 bg-white rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
                              {/* Left: Officer Info */}
                              <div className="flex items-center space-x-3 shrink-0">
                                <span className="text-xs font-black text-slate-400">#{idx + 1}</span>
                                <div className="w-12 h-12 rounded-2xl bg-[#218DAE] text-white flex items-center justify-center font-black text-sm shadow-xs">
                                  {surv.name.charAt(0)}
                                </div>
                                <div>
                                  <h4 className="text-xs font-black text-slate-900 uppercase">{surv.name}</h4>
                                  <p className="text-[10px] text-slate-500 font-bold mt-0.5 uppercase">
                                    {tervalidasi} TERVALIDASI ({totalDone} TOTAL SURVEI) DARI {totalAssigned} TARGET • {surv.ulp || 'TUAL KOTA'}
                                  </p>
                                </div>
                              </div>

                              {/* Middle: Stats Box with vertical dividing lines (Hidden on Mobile) */}
                              <div className="hidden md:flex items-center bg-slate-50 border border-slate-100 rounded-2xl px-6 py-2.5 mx-4 space-x-6 shrink-0 shadow-xs">
                                <div className="text-center px-3 border-r border-slate-200">
                                  <span className="text-[9px] text-slate-400 font-black uppercase tracking-wider block">SURVEI HARI INI</span>
                                  <span className="text-sm font-black text-cyan-600">{harianCount}</span>
                                </div>
                                <div className="text-center px-3 border-r border-slate-200">
                                  <span className="text-[9px] text-slate-400 font-black uppercase tracking-wider block">REVIEW ADMIN</span>
                                  <span className="text-sm font-black text-amber-600">{menungguVal}</span>
                                </div>
                                <div className="text-center px-3">
                                  <span className="text-[9px] text-slate-400 font-black uppercase tracking-wider block">TERVALIDASI</span>
                                  <span className="text-sm font-black text-emerald-600">{tervalidasi}</span>
                                </div>
                              </div>

                              {/* Right: Progress bar track + Percentage (Hidden on Mobile) */}
                              <div className="hidden md:flex items-center flex-1 space-x-4 pl-4">
                                <div className="flex-1 bg-slate-100 h-3 rounded-full overflow-hidden">
                                  <div className="bg-[#218DAE] h-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="text-xs font-black text-[#218DAE] w-10 text-right">{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Daftar Pelanggan Sudah Divalidasi */}
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-6">
                      <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">Daftar Pelanggan Sudah Divalidasi</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                              <th className="p-3 text-center">IDPEL</th>
                              <th className="p-3 text-center">NAMA PELANGGAN</th>
                              <th className="p-3 text-center">DAYA</th>
                              <th className="p-3 text-center">DESA / ALAMAT</th>
                              <th className="p-3 text-center">WAKTU SURVEY</th>
                              <th className="p-3 text-center">PETUGAS</th>
                              <th className="p-3 text-center">STATUS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {customers.filter(c => c.status === 'sudah_divalidasi').length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center py-12 text-slate-400 font-bold italic">
                                  Belum ada pelanggan yang tervalidasi.
                                </td>
                              </tr>
                            ) : (
                              customers.filter(c => c.status === 'sudah_divalidasi').map(c => (
                                <tr key={c.id} className="hover:bg-slate-50">
                                  <td className="p-3 text-center font-bold text-[#218DAE]">{c.dil}</td>
                                  <td className="p-3 text-center font-black uppercase">{c.name}</td>
                                  <td className="p-3 text-center">{c.daya} VA</td>
                                  <td className="p-3 text-center uppercase">{c.desa} / {c.alamat}</td>
                                  <td className="p-3 text-center">{c.tgl_survey || '-'}</td>
                                  <td className="p-3 text-center font-bold">{c.assignedTo || 'Petugas 1'}</td>
                                  <td className="p-3 text-center"><StatusBadge status={c.status} /></td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      {filteredValidationCustomers.length > 50 && (
                        <div className="flex flex-wrap items-center justify-between p-4 bg-slate-50 border-t border-slate-200 text-xs font-bold text-slate-700 gap-3">
                          <span>Menampilkan {(safeValPage - 1) * 50 + 1} - {Math.min(safeValPage * 50, filteredValidationCustomers.length)} dari {filteredValidationCustomers.length} data</span>
                          <div className="flex space-x-1.5 items-center">
                            <button disabled={safeValPage === 1} onClick={() => setValPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">◀ Sebelum</button>
                            {Array.from({ length: Math.min(5, totalValPages) }, (_, i) => {
                              let pageNum = safeValPage <= 3 ? i + 1 : safeValPage + i - 2;
                              if (pageNum > totalValPages) pageNum = totalValPages - (4 - i);
                              if (pageNum <= 0) pageNum = i + 1;
                              if (pageNum > totalValPages) return null;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setValPage(pageNum)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${safeValPage === pageNum ? 'bg-[#218DAE] text-white shadow-xs' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                            <button disabled={safeValPage >= totalValPages} onClick={() => setValPage(p => Math.min(totalValPages, p + 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">Lanjut ▶</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 2. KELOLA PENGGUNA */}
                {adminTab === 'pengguna' && (
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Kelola Pengguna Sistem</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">DAFTAR AKUN ADMINISTRATOR, SURVEYOR, DAN VALIDATOR</p>
                      </div>
                      <button onClick={handleOpenAddUser} className="px-4 py-2.5 bg-[#218DAE] hover:brightness-110 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                        <PlusCircle size={15} />
                        <span>TAMBAH USER</span>
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                            <th className="p-4 text-center">NAMA</th>
                            <th className="p-4 text-center">USERNAME</th>
                            <th className="p-4 text-center">ROLE</th>
                            <th className="p-4 text-center">ULP</th>
                            <th className="p-4 text-center">AKSI</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {users.map((u, idx) => (
                            <tr key={u.id} className={`hover:bg-slate-50 transition ${idx % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                              <td className="p-4 text-center font-extrabold text-slate-900 uppercase">{u.name}</td>
                              <td className="p-4 text-center font-bold text-slate-500">@{u.username}</td>
                              <td className="p-4 text-center font-extrabold text-[#218DAE] uppercase">{u.role}</td>
                              <td className="p-4 text-center font-bold text-slate-700">{u.ulp || 'Tual Kota'}</td>
                              <td className="p-4 text-center space-x-2">
                                <button onClick={() => handleOpenEditUser(u)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition">Edit</button>
                                <button onClick={() => handleDeleteUser(u.id)} className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg font-bold text-[11px] transition">Hapus</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 3. VALIDASI HASIL SURVEI */}
                {adminTab === 'validasi' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Validasi Hasil Survei Lapangan</h3>
                      <div className="flex space-x-2">
                        <button onClick={handleExportExcel} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                          <FileSpreadsheet size={15} />
                          <span>Export Excel</span>
                        </button>
                        <button onClick={handleExportPDF} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                          <Download size={15} />
                          <span>Export PDF</span>
                        </button>
                      </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[#218DAE] text-white p-6 rounded-3xl shadow-sm flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90 block">MENUNGGU VALIDASI</span>
                          <span className="text-4xl font-black mt-1 block">{customers.filter(c => c.status === 'menunggu_validasi').length}</span>
                        </div>
                        <RefreshCw size={28} className="opacity-80" />
                      </div>
                      <div className="bg-emerald-600 text-white p-6 rounded-3xl shadow-sm flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider opacity-90 block">SUDAH TERVALIDASI</span>
                          <span className="text-4xl font-black mt-1 block">{customers.filter(c => c.status === 'sudah_divalidasi').length}</span>
                        </div>
                        <CheckCircle2 size={28} className="opacity-80" />
                      </div>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-black uppercase tracking-wider text-slate-700">FILTER DATA VALIDASI</span>
                        <button onClick={handleResetValidationFilter} className="text-xs font-bold text-[#218DAE] hover:underline">Reset Filter</button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                        {/* Status */}
                        <select value={valFilterStatus} onChange={e => { setValFilterStatus(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]">
                          <option value="ALL">Semua Status</option>
                          <option value="menunggu_validasi">Menunggu Validasi</option>
                          <option value="sudah_divalidasi">Sudah Tervalidasi</option>
                          <option value="rejected">Ditolak Admin</option>
                        </select>

                        {/* Petugas */}
                        <select value={valFilterPetugas} onChange={e => { setValFilterPetugas(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]">
                          <option value="ALL">Semua Petugas</option>
                          {users.filter(u => u.role === 'surveyor').map(u => (
                            <option key={u.id} value={u.username}>{u.name}</option>
                          ))}
                        </select>

                        {/* Start Date */}
                        <input type="date" value={valFilterStartDate} onChange={e => { setValFilterStartDate(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]" placeholder="Tgl Mulai" />

                        {/* End Date */}
                        <input type="date" value={valFilterEndDate} onChange={e => { setValFilterEndDate(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]" placeholder="Tgl Sampai" />

                        {/* Desa */}
                        <select value={valFilterDesa} onChange={e => { setValFilterDesa(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]">
                          <option value="ALL">Semua Desa</option>
                          {[...new Set(customers.map(c => c.desa).filter(Boolean))].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>

                        {/* Unit */}
                        <select value={valFilterUnit} onChange={e => { setValFilterUnit(e.target.value); setValPage(1); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]">
                          <option value="ALL">Semua Unit</option>
                          <option value="Tual">ULP Tual Kota</option>
                          <option value="Dobo">ULP Dobo</option>
                          <option value="Elat">ULP Elat</option>
                        </select>
                      </div>

                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          value={validationSearchQuery}
                          onChange={e => { setValidationSearchQuery(e.target.value); setValPage(1); }}
                          placeholder="Cari IDPEL, Nama Pelanggan, atau Alamat..."
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-xs font-bold bg-slate-50 outline-none focus:border-[#218DAE]"
                        />
                      </div>
                    </div>

                    {/* Results Table */}
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-black uppercase tracking-wider text-slate-700">HASIL FILTER ({getFilteredValidationCustomers().length} DATA)</span>
                        {selectedValidationIds.length > 0 && (
                          <button
                            onClick={() => handleDeleteSelectedCustomers(selectedValidationIds)}
                            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs transition flex items-center space-x-1.5"
                          >
                            <Trash2 size={14} />
                            <span>Hapus Terpilih ({selectedValidationIds.length})</span>
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                              <th className="p-3 text-center">
                                <input type="checkbox" onChange={handleToggleSelectAllValidation} checked={getFilteredValidationCustomers().length > 0 && selectedValidationIds.length === getFilteredValidationCustomers().length} />
                              </th>
                              <th className="p-3 text-center">IDPEL</th>
                              <th className="p-3 text-center">NAMA PELANGGAN</th>
                              <th className="p-3 text-center">KATEGORI TO</th>
                              <th className="p-3 text-center">UNIT / DESA</th>
                              <th className="p-3 text-center">PETUGAS</th>
                              <th className="p-3 text-center">WAKTU SURVEY</th>
                              <th className="p-3 text-center">KOORDINAT GPS</th>
                              <th className="p-3 text-center">CATATAN / FOTO</th>
                              <th className="p-3 text-center">STATUS</th>
                              <th className="p-3 text-center">AKSI VALIDASI / HAPUS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {getFilteredValidationCustomers().length === 0 ? (
                              <tr>
                                <td colSpan="11" className="text-center py-12 text-slate-400 font-bold italic">
                                  Tidak ada data survei sesuai filter.
                                </td>
                              </tr>
                            ) : (
                              paginatedValidationCustomers.map(c => (
                                <tr key={c.id} className="hover:bg-slate-50">
                                  <td className="p-3 text-center">
                                    <input type="checkbox" checked={selectedValidationIds.includes(c.id)} onChange={() => handleToggleSelectValidation(c.id)} />
                                  </td>
                                  <td className="p-3 text-center font-bold text-[#218DAE]">{c.dil}</td>
                                  <td className="p-3 text-center font-black uppercase">
                                    {c.name}
                                    <div className="text-[10px] text-rose-700 font-bold mt-0.5">
                                      No. kWh: {c.kwh_meter_awal || c.kwh_meter || '-'}
                                    </div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <JenisToBadge jenis={c.jenis_to} />
                                    {(c.isResurvey || c.validationNote) && (
                                      <div className="mt-1">
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[9px] font-black uppercase tracking-wider shadow-2xs">
                                          🔄 HASIL PERBAIKAN
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-3 text-center uppercase">{c.ulp} / {c.desa}</td>
                                  <td className="p-3 text-center font-bold">{c.assignedTo || 'Petugas 1'}</td>
                                  <td className="p-3 text-center">{c.tgl_survey || '-'}</td>
                                  <td className="p-3 text-center">
                                    {(() => {
                                      const sLat = c.surveyData?.lat || c.lat;
                                      const sLng = c.surveyData?.lng || c.lng;
                                      const dist = calculateDistance(c.lat, c.lng, sLat, sLng);
                                      const isMatch = dist <= 50;
                                      return (
                                        <div className="space-y-1">
                                          <div className="text-[10px] font-mono text-slate-500">
                                            <div>DIL: {c.lat?.toFixed(4)}, {c.lng?.toFixed(4)}</div>
                                            <div>GPS: {sLat?.toFixed(4)}, {sLng?.toFixed(4)}</div>
                                          </div>
                                          <button
                                            onClick={() => setMapComparisonModal({ show: true, customer: c })}
                                            className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider transition ${isMatch ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-rose-100 text-rose-800 hover:bg-rose-200'}`}
                                          >
                                            🗺️ {isMatch ? `Sesuai (${dist}m)` : `Beda (${dist}m)`}
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="p-3 text-center space-y-1">
                                    <div className="text-[11px] font-bold text-slate-700 max-w-[160px] mx-auto truncate" title={c.surveyData?.note || 'Keterangan tidak diisi'}>
                                      {c.surveyData?.note ? `"${c.surveyData.note}"` : (c.surveyData?.isMatch === false ? 'Keterangan: Lokasi Tidak Sesuai' : 'Tanpa Catatan')}
                                    </div>
                                    <button
                                      onClick={() => setPhotoComparisonModal({ show: true, customer: c })}
                                      className="px-2.5 py-1 bg-cyan-50 border border-[#218DAE]/30 text-[#218DAE] hover:bg-cyan-100 rounded-lg text-[10px] font-black uppercase tracking-wider transition flex items-center justify-center space-x-1 mx-auto"
                                    >
                                      <Camera size={12} />
                                      <span>Lihat Foto & No. KWh</span>
                                    </button>
                                  </td>
                                  <td className="p-3 text-center"><StatusBadge status={c.status} /></td>
                                  <td className="p-3 text-center space-y-1">
                                    <div className="flex items-center justify-center space-x-1">
                                      <button onClick={() => handleValidateCustomer(c.id, 'sudah_divalidasi')} className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold transition">Setuju</button>
                                      <button onClick={() => { setRejectModal({ show: true, customerId: c.id, customerName: c.name }); setRejectReasonText(''); }} className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded font-bold transition">Tolak</button>
                                    </div>
                                    <button onClick={() => handleDeleteCustomer(c)} className="px-2 py-0.5 bg-slate-200 hover:bg-rose-100 hover:text-rose-700 text-slate-600 rounded text-[10px] font-bold transition flex items-center justify-center space-x-1 mx-auto">
                                      <Trash2 size={11} />
                                      <span>Hapus</span>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      {filteredValidationCustomers.length > 50 && (
                        <div className="flex flex-wrap items-center justify-between p-4 bg-slate-50 border-t border-slate-200 text-xs font-bold text-slate-700 gap-3">
                          <span>Menampilkan {(safeValPage - 1) * 50 + 1} - {Math.min(safeValPage * 50, filteredValidationCustomers.length)} dari {filteredValidationCustomers.length} data survei</span>
                          <div className="flex space-x-1.5 items-center">
                            <button disabled={safeValPage === 1} onClick={() => setValPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">◀ Sebelum</button>
                            {Array.from({ length: Math.min(5, totalValPages) }, (_, i) => {
                              let pageNum = safeValPage <= 3 ? i + 1 : safeValPage + i - 2;
                              if (pageNum > totalValPages) pageNum = totalValPages - (4 - i);
                              if (pageNum <= 0) pageNum = i + 1;
                              if (pageNum > totalValPages) return null;
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setValPage(pageNum)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${safeValPage === pageNum ? 'bg-[#218DAE] text-white shadow-xs' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
                                >
                                  Halaman {pageNum}
                                </button>
                              );
                            })}
                            <button disabled={safeValPage >= totalValPages} onClick={() => setValPage(p => Math.min(totalValPages, p + 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">Lanjut ▶</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 4. IMPORT DIL PELANGGAN */}
                {adminTab === 'import_dil' && (
                  <div className="space-y-6">
                    {/* Sub tabs */}
                    <div className="flex space-x-2 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm w-fit">
                      <button onClick={() => setImportDilSubTab('upload')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition ${importDilSubTab === 'upload' ? 'bg-[#218DAE] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'}`}>
                        1. Upload DIL Baru
                      </button>
                      <button onClick={() => setImportDilSubTab('riwayat')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition ${importDilSubTab === 'riwayat' ? 'bg-[#218DAE] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'}`}>
                        2. Riwayat Daftar TO Pelanggan
                      </button>
                      <button onClick={() => setImportDilSubTab('edit')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition ${importDilSubTab === 'edit' ? 'bg-[#218DAE] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'}`}>
                        3. Pelanggan TO Terkini (Edit)
                      </button>
                    </div>

                    {importDilSubTab === 'upload' && (
                      <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                          <div>
                            <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Import DIL Pelanggan (13 Kolom)</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">UNGGAH FILE CSV ATAU TAMBAH SATU PERSATU SECARA MANUAL</p>
                          </div>
                          <div className="flex space-x-2">
                            <button onClick={() => setAddCustomerModal(true)} className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                              <PlusCircle size={15} />
                              <span>+ Tambah 1 TO Manual</span>
                            </button>
                            <button onClick={handleDownloadDilTemplate} className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                              <Download size={15} />
                              <span>Download Template CSV</span>
                            </button>
                          </div>
                        </div>

                        {/* Dropzone Box */}
                        <label htmlFor="csv-upload" className="block border-2 border-dashed border-slate-300 rounded-3xl p-12 text-center space-y-3 hover:border-[#218DAE] transition bg-slate-50/50 cursor-pointer">
                          <input type="file" id="csv-upload" accept=".csv" className="hidden" onChange={handleFileUpload} />
                          <div className="w-14 h-14 bg-cyan-50 text-[#218DAE] rounded-2xl flex items-center justify-center mx-auto shadow-xs">
                            <FileText size={28} />
                          </div>
                          <div>
                            <h4 className="text-sm font-black text-slate-800">Pilih File CSV Pelanggan</h4>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">SESUAI FORMAT 14 KOLOM (ID_Pelanggan, KWh_Meter, Nama_Pelanggan, Daya, Tarif, Jenis_KWh, Alamat, Desa, Kelurahan_Kecamatan, Kabupaten_Kota, ID_ULP, Latitude, Longitude, KATEGORI_TO)</p>
                          </div>
                        </label>

                        {/* Preview section */}
                        <div className="space-y-3 pt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-black uppercase tracking-wider text-slate-700">Preview Data DIL ({csvData.length} Baris Dimuat)</span>
                            <button onClick={handleDistributeData} disabled={csvData.length === 0} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition ${csvData.length > 0 ? 'bg-[#218DAE] text-white hover:brightness-110 shadow-md' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
                              Upload & Distribusikan Tugas
                            </button>
                          </div>
                          {csvData.length === 0 ? (
                            <div className="p-12 text-center bg-slate-50 rounded-2xl border border-slate-200 text-xs font-bold text-slate-400 italic">
                              Belum ada file CSV yang dipilih. Silakan klik kotak di atas untuk memilih file CSV.
                            </div>
                          ) : (
                            <div className="overflow-x-auto bg-slate-50 rounded-2xl border border-slate-200">
                              <table className="w-full text-left text-xs">
                                <thead>
                                  <tr className="bg-slate-200 text-slate-600 font-bold uppercase">
                                    <th className="p-2">IDPEL</th>
                                    <th className="p-2">NAMA</th>
                                    <th className="p-2">DAYA</th>
                                    <th className="p-2">TARIF</th>
                                    <th className="p-2">DESA</th>
                                    <th className="p-2">PETUGAS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {csvData.slice(0, 5).map((row, idx) => (
                                    <tr key={idx} className="border-t border-slate-200">
                                      <td className="p-2">{row.dil}</td>
                                      <td className="p-2">{row.name}</td>
                                      <td className="p-2">{row.daya}</td>
                                      <td className="p-2">{row.tarif}</td>
                                      <td className="p-2">{row.desa}</td>
                                      <td className="p-2">{row.assignedTo}</td>
                                    </tr>
                                  ))}
                                  {csvData.length > 5 && (
                                    <tr><td colSpan="6" className="p-2 text-center text-slate-400 italic font-bold">...dan {csvData.length - 5} baris lainnya</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {importDilSubTab === 'riwayat' && (
                      <div className="space-y-6">
                        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-3">
                          <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Riwayat Daftar TO Pelanggan</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">MENAMPILKAN SELURUH DATA PELANGGAN YANG TELAH DIUNGGAH KE SISTEM BESERTA TANGGAL INPUTNYA</p>
                        </div>

                        {/* Filter Card */}
                        <div className="bg-[#218DAE] p-6 rounded-3xl text-white shadow-sm space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">TANGGAL UPLOAD</label>
                              <input type="date" value={importFilterDate} onChange={e => { setImportFilterDate(e.target.value); setImportPage(1); }} className="w-full px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none" />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">NAMA DESA</label>
                              <select value={importFilterDesa} onChange={e => { setImportFilterDesa(e.target.value); setImportPage(1); }} className="w-full px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none">
                                <option value="Semua Desa">Semua Desa</option>
                                {[...new Set(customers.map(c => c.desa).filter(Boolean))].map(d => (
                                  <option key={d} value={d}>{d}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">CARI NAMA / IDPEL</label>
                              <div className="flex space-x-2">
                                <input type="text" value={importSearchQuery} onChange={e => { setImportSearchQuery(e.target.value); setImportPage(1); }} placeholder="Ketik nama atau IDPEL..." className="flex-1 px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none placeholder:text-slate-400" />
                                <button onClick={() => { setImportFilterDate(''); setImportFilterDesa('Semua Desa'); setImportSearchQuery(''); }} className="px-4 py-2.5 bg-white text-slate-800 rounded-xl text-xs font-black uppercase hover:bg-slate-100 transition shadow-xs">RESET</button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Table */}
                        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                                  <th className="p-3 text-center">WAKTU SURVEY / INPUT</th>
                                  <th className="p-3 text-center">IDPEL</th>
                                  <th className="p-3 text-center">NAMA PELANGGAN</th>
                                  <th className="p-3 text-center">DAYA / TARIF</th>
                                  <th className="p-3 text-center">DESA / ULP</th>
                                  <th className="p-3 text-center">PETUGAS SURVEYOR</th>
                                  <th className="p-3 text-center">STATUS PROGRES</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {paginatedImportCustomers.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center py-12 text-slate-400 font-bold italic">
                                      Belum ada riwayat TO yang selesai disurvei.
                                    </td>
                                  </tr>
                                ) : (
                                  paginatedImportCustomers.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-50">
                                      <td className="p-3 text-center font-bold">{c.tgl_survey || c.tgl_input || '2026-06-01'}</td>
                                      <td className="p-3 text-center font-bold text-[#218DAE]">{c.dil}</td>
                                      <td className="p-3 text-center font-black uppercase">{c.name}</td>
                                      <td className="p-3 text-center font-bold">{c.daya} VA • {c.tarif || 'R1T'}</td>
                                      <td className="p-3 text-center uppercase">{c.desa || '-'} ({c.ulp || 'Tual Kota'})</td>
                                      <td className="p-3 text-center font-bold">{c.surveyorUsername || c.surveyData?.surveyorUsername || c.assignedTo || 'PETUGAS1'}</td>
                                      <td className="p-3 text-center"><StatusBadge status={c.status} /></td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                          {filteredImportCustomers.length > 50 && (
                            <div className="flex flex-wrap items-center justify-between p-4 bg-slate-50 border-t border-slate-200 text-xs font-bold text-slate-700 gap-3">
                              <span>Menampilkan {(safeImportPage - 1) * 50 + 1} - {Math.min(safeImportPage * 50, filteredImportCustomers.length)} dari {filteredImportCustomers.length} data</span>
                              <div className="flex space-x-1.5 items-center">
                                <button disabled={safeImportPage === 1} onClick={() => setImportPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">◀ Sebelum</button>
                                {Array.from({ length: Math.min(5, totalImportPages) }, (_, i) => {
                                  let pageNum = safeImportPage <= 3 ? i + 1 : safeImportPage + i - 2;
                                  if (pageNum > totalImportPages) pageNum = totalImportPages - (4 - i);
                                  if (pageNum <= 0) pageNum = i + 1;
                                  if (pageNum > totalImportPages) return null;
                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setImportPage(pageNum)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${safeImportPage === pageNum ? 'bg-[#218DAE] text-white shadow-xs' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                <button disabled={safeImportPage >= totalImportPages} onClick={() => setImportPage(p => Math.min(totalImportPages, p + 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">Lanjut ▶</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {importDilSubTab === 'edit' && (
                      <div className="space-y-6">
                        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                          <div>
                            <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Pelanggan TO Terkini & Manajemen Edit</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">KELOLA DAN UBAH DATA PELANGGAN TUGAS OPERASI (TO) YANG AKTIF</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2.5">
                            <button
                              onClick={() => setAddCustomerModal(true)}
                              className="px-4 py-2.5 bg-[#218DAE] hover:brightness-110 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition flex items-center space-x-1.5"
                            >
                              <Plus size={16} />
                              <span>Tambah 1 TO Manual</span>
                            </button>
                            <button
                              onClick={handleAutoRedistributeToSurveyors}
                              className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition flex items-center space-x-1.5"
                              title="Membagi rata seluruh TO pending ke petugas survei aktif berdasarkan ULP"
                            >
                              <RefreshCw size={15} />
                              <span>Bagi Rata TO Ke Petugas ULP</span>
                            </button>
                          </div>
                        </div>

                        {/* Filter Card */}
                        <div className="bg-[#218DAE] p-6 rounded-3xl text-white shadow-sm space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">TANGGAL UPLOAD</label>
                              <input type="date" value={importFilterDate} onChange={e => setImportFilterDate(e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none" />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">NAMA DESA</label>
                              <select value={importFilterDesa} onChange={e => setImportFilterDesa(e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none">
                                <option value="Semua Desa">Semua Desa</option>
                                {[...new Set(customers.map(c => c.desa).filter(Boolean))].map(d => (
                                  <option key={d} value={d}>{d}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider opacity-90">CARI NAMA / IDPEL</label>
                              <div className="flex space-x-2">
                                <input type="text" value={importSearchQuery} onChange={e => { setImportSearchQuery(e.target.value); setImportPage(1); }} placeholder="Ketik nama atau IDPEL..." className="flex-1 px-4 py-2.5 rounded-xl bg-white text-slate-800 text-xs font-bold outline-none border-none placeholder:text-slate-400" />
                                <button onClick={() => { setImportFilterDate(''); setImportFilterDesa('Semua Desa'); setImportSearchQuery(''); }} className="px-4 py-2.5 bg-white text-slate-800 rounded-xl text-xs font-black uppercase hover:bg-slate-100 transition shadow-xs">RESET</button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {selectedCustomerIds.length > 0 && (
                          <div className="flex items-center justify-between p-4 bg-rose-50 rounded-2xl border border-rose-200">
                            <span className="text-xs font-black text-rose-700">{selectedCustomerIds.length} pelanggan dipilih</span>
                            <button onClick={handleBatchDeleteCustomers} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs transition flex items-center space-x-1.5">
                              <Trash2 size={14} />
                              <span>Hapus Terpilih (Kolektif)</span>
                            </button>
                          </div>
                        )}

                        {/* Table */}
                        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                                  <th className="p-3 text-center w-12">
                                    <input type="checkbox" onChange={handleToggleSelectAll} checked={selectedCustomerIds.length === filteredImportCustomers.length && filteredImportCustomers.length > 0} />
                                  </th>
                                  <th className="p-3 text-center">IDPEL</th>
                                  <th className="p-3 text-center">NAMA PELANGGAN</th>
                                  <th className="p-3 text-center">DAYA / TARIF</th>
                                  <th className="p-3 text-center">ALAMAT / DESA</th>
                                  <th className="p-3 text-center">ULP</th>
                                  <th className="p-3 text-center">AKSI</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {paginatedImportCustomers.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="p-0">
                                      <div className="p-8 bg-amber-50/80 border-y border-amber-200 text-center space-y-3">
                                        <div className="w-12 h-12 bg-amber-500 text-white rounded-2xl flex items-center justify-center mx-auto shadow-xs font-black">
                                          <AlertTriangle size={24} />
                                        </div>
                                        <div className="space-y-1">
                                          <h4 className="text-xs font-black uppercase tracking-wider text-amber-900">
                                            DIAGNOSTIK: TIDAK ADA TO PENDING TERAMPIL (0 DATA)
                                          </h4>
                                          <p className="text-[11px] font-bold text-amber-800 max-w-xl mx-auto">
                                            {importSearchQuery || importFilterDesa !== 'Semua Desa' || importFilterDate ? (
                                              <span>Penyebab: Filter aktif (Desa: "{importFilterDesa}", Tanggal: "{importFilterDate || 'Semua'}", Cari: "{importSearchQuery || 'Kosong'}"). Silakan tekan tombol <strong>Reset Filter</strong> di bawah.</span>
                                            ) : customers.length === 0 ? (
                                              <span>Penyebab: Database pelanggan di server/lokal belum memuat data. Tekan tombol <strong>Restore 3.870 TO Master</strong> di bawah untuk memulihkan master data.</span>
                                            ) : (
                                              <span>Penyebab: Seluruh {customers.length} pelanggan di database telah selesai disurvei/divalidasi dan tersimpan aman di <strong>Sub Tab 2 (Riwayat)</strong>.</span>
                                            )}
                                          </p>
                                        </div>
                                        <div className="flex justify-center space-x-2 pt-1">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setImportFilterDate('');
                                              setImportFilterDesa('Semua Desa');
                                              setImportSearchQuery('');
                                              setImportPage(1);
                                              showToast('🔄 Filter di-reset! Menampilkan seluruh TO pending.');
                                            }}
                                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs transition"
                                          >
                                            Reset Filter Tampilan
                                          </button>
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              showToast('⏳ Memulihkan Database Master TO...');
                                              setDeletedIds([]);
                                              secureStorage.setItem('pln_deleted_ids', []);
                                              const res = await postToApiCandidates('/api/database/restore-master', {});
                                              if (res && res.success && res.data && res.data.success) {
                                                const pCount = res.data.pendingCount || 0;
                                                const sCount = res.data.surveyedCount || 0;
                                                const tCount = res.data.totalCount || (pCount + sCount);
                                                showToast(`✅ DATABASE MASTER DIPULIHKAN! Menampilkan ${pCount} TO pending di Tab 3 (${sCount > 0 ? `dikurangi ${sCount} TO yang sudah disurvei di Tab 2` : 'utuh'}). Total: ${tCount} TO.`);
                                                await fetchBackendData(true, true);
                                              }
                                            }}
                                            className="px-4 py-2 bg-[#218DAE] hover:brightness-110 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs transition"
                                          >
                                            Restore Database Master
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                ) : (
                                  paginatedImportCustomers.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-50">
                                      <td className="p-3 text-center">
                                        <input type="checkbox" checked={selectedCustomerIds.map(String).includes(String(c.id))} onChange={() => handleToggleSelectCustomer(c.id)} />
                                      </td>
                                      <td className="p-3 text-center font-bold text-[#218DAE]">{c.dil}</td>
                                      <td className="p-3 text-center font-black uppercase">{c.name}</td>
                                      <td className="p-3 text-center font-bold">{c.daya} VA • {c.tarif || 'R1T'}</td>
                                      <td className="p-3 text-center uppercase">{c.alamat || 'ALAMAT TIDAK TERSEDIA'} • {c.desa || '-'}</td>
                                      <td className="p-3 text-center font-bold">{c.ulp || 'Tual Kota'}</td>
                                      <td className="p-3 text-center space-x-2">
                                        <button onClick={() => handleOpenEditCustomer(c)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition">Edit</button>
                                        <button onClick={() => handleDeleteCustomer(c)} className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg font-bold text-[11px] transition">Hapus</button>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                          {filteredImportCustomers.length > 50 && (
                            <div className="flex flex-wrap items-center justify-between p-4 bg-slate-50 border-t border-slate-200 text-xs font-bold text-slate-700 gap-3">
                              <span>Menampilkan {(safeImportPage - 1) * 50 + 1} - {Math.min(safeImportPage * 50, filteredImportCustomers.length)} dari {filteredImportCustomers.length} data TO</span>
                              <div className="flex space-x-1.5 items-center">
                                <button disabled={safeImportPage === 1} onClick={() => setImportPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">◀ Sebelum</button>
                                {Array.from({ length: Math.min(5, totalImportPages) }, (_, i) => {
                                  let pageNum = safeImportPage <= 3 ? i + 1 : safeImportPage + i - 2;
                                  if (pageNum > totalImportPages) pageNum = totalImportPages - (4 - i);
                                  if (pageNum <= 0) pageNum = i + 1;
                                  if (pageNum > totalImportPages) return null;
                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setImportPage(pageNum)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${safeImportPage === pageNum ? 'bg-[#218DAE] text-white shadow-xs' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
                                    >
                                      Halaman {pageNum}
                                    </button>
                                  );
                                })}
                                <button disabled={safeImportPage >= totalImportPages} onClick={() => setImportPage(p => Math.min(totalImportPages, p + 1))} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg disabled:opacity-40 font-bold hover:bg-slate-100 transition shadow-2xs">Lanjut ▶</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. SURAT TUGAS */}
                {adminTab === 'surat_tugas' && (
                  <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Upload Surat Tugas Resmi</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">UNGGAH DOKUMEN/FOTO SURAT TUGAS YANG AKAN DITAMPILKAN KEPADA PELANGGAN SAAT SURVEI</p>
                    </div>

                    <label className="border-2 border-dashed border-slate-300 rounded-3xl p-12 text-center space-y-3 hover:border-[#218DAE] transition bg-slate-50/50 cursor-pointer block">
                      <input type="file" accept=".pdf,image/*" onChange={handleSuratTugasUpload} className="hidden" />
                      <div className="w-16 h-16 bg-cyan-50 text-[#218DAE] rounded-2xl flex items-center justify-center mx-auto shadow-xs">
                        <FileText size={32} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-800">Klik / Seret File Surat Tugas (Gambar / PDF) Ke Sini</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">DOKUMEN RESMI BERSTEMPEL PLN UP3 TUAL</p>
                      </div>
                    </label>

                    {suratTugasDoc && (
                      <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex flex-wrap items-center justify-between gap-3 text-xs font-bold text-emerald-800">
                        <div className="flex items-center space-x-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                          <span>📄 File Aktif: <strong>{suratTugasDoc.name}</strong> (Diunggah: {suratTugasDoc.date})</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="px-3 py-1 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-wider shadow-2xs">
                            ✅ Ter-Upload
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowSuratTugasMobileModal(true)}
                            className="px-4 py-1.5 bg-[#218DAE] hover:brightness-110 text-white rounded-xl text-[11px] font-black uppercase tracking-wider shadow-xs transition flex items-center space-x-1"
                          >
                            <Eye size={14} />
                            <span>Preview Dokumen</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 6. LAPORAN REALISASI */}
                {adminTab === 'laporan' && (
                  <div className="space-y-6">
                    <div className="flex flex-wrap justify-between items-center gap-3">
                      <div>
                        <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Laporan Realisasi & Kinerja Surveyor</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">REKAPITULASI REALISASI HARIAN DAN KUMULATIF PER MASING-MASING PETUGAS SURVEYOR</p>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={handleExportExcel} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                          <FileSpreadsheet size={15} />
                          <span>Export Excel</span>
                        </button>
                        <button onClick={handleExportPDF} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition">
                          <Download size={15} />
                          <span>Export PDF</span>
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                              <th className="p-3 text-center">UNIT (ULP)</th>
                              <th className="p-3 text-center">NAMA PETUGAS SURVEYOR</th>
                              <th className="p-3 text-center">TOTAL TO (TARGET)</th>
                              <th className="p-3 text-center">REALISASI HARIAN (HARI INI)</th>
                              <th className="p-3 text-center">REALISASI KUMULATIF</th>
                              <th className="p-3 text-center">PERSENTASE CAPAIAN (%)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(() => {
                              const todayStr = new Date().toISOString().split('T')[0];
                              const surveyors = users.filter(u => u && u.role === 'surveyor');

                              if (surveyors.length === 0) {
                                return (
                                  <tr>
                                    <td colSpan="6" className="text-center py-12 text-slate-400 font-bold italic">
                                      Belum ada data petugas surveyor aktif.
                                    </td>
                                  </tr>
                                );
                              }

                              return surveyors.map((surv) => {
                                const assigned = customers.filter(c => isCustomerAssignedToSurveyor(c, surv));
                                const total = assigned.length;

                                const harian = assigned.filter(c => {
                                  if (!c || (!c.status || c.status === 'pending')) return false;
                                  const tgl = String(c.tgl_survey || c.tgl_input || '');
                                  return tgl.startsWith(todayStr);
                                }).length;

                                const kumulatif = assigned.filter(c => c && (c.status === 'sudah_divalidasi' || c.status === 'menunggu_validasi')).length;
                                const pct = total > 0 ? Math.round((kumulatif / total) * 100) : 0;

                                return (
                                  <tr key={surv.id} className="hover:bg-slate-50">
                                    <td className="p-3 text-center font-bold text-[#218DAE] uppercase">{surv.ulp || 'Tual Kota'}</td>
                                    <td className="p-3 text-center font-black uppercase">{surv.name}</td>
                                    <td className="p-3 text-center font-bold">{total} TO</td>
                                    <td className="p-3 text-center font-bold text-blue-600">{harian} TO</td>
                                    <td className="p-3 text-center font-bold text-emerald-600">{kumulatif} TO</td>
                                    <td className="p-3 text-center font-black text-slate-900">{pct}%</td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* 7. AUDIT EVENT LOGS & RESET REASON */}
                {adminTab === 'event_logs' && (
                  <div className="space-y-6">
                    <div className="flex flex-wrap justify-between items-center gap-3">
                      <div>
                        <h3 className="text-sm font-black uppercase tracking-tight text-slate-900">Log Aktivitas & Audit Database TO</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">CATATAN TRANSAKSI REAL-TIME RIWAYAT EDIT, HAPUS, IMPOR, RESTORE & PENYEBAB PERUBAHAN DATABASE</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={fetchEventLogs}
                          className="px-4 py-2 bg-[#218DAE] hover:brightness-110 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition"
                        >
                          <RefreshCw size={14} />
                          <span>Refresh Log</span>
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm('Yakin ingin membersihkan seluruh log aktivitas database?')) {
                              await deleteFromApiCandidates('/api/event-logs');
                              setEventLogs([]);
                              showToast('🗑️ Log aktivitas berhasil dibersihkan.');
                            }
                          }}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-xs flex items-center space-x-1.5 transition"
                        >
                          <Trash2 size={14} />
                          <span>Bersihkan Log</span>
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-[#218DAE] text-white font-black uppercase tracking-wider">
                              <th className="p-3 text-center">WAKTU EVENT</th>
                              <th className="p-3 text-center">JENIS AKSI</th>
                              <th className="p-3 text-center">AKUN PELAKU</th>
                              <th className="p-3 text-center">DESKRIPSI & PENYEBAB PERUBAHAN</th>
                              <th className="p-3 text-center">RINGKASAN TOTAL TO</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {eventLogs.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center py-12 text-slate-400 font-bold italic">
                                  Belum ada catatan log aktivitas database. Setiap perubahan TO (impor, hapus, restore) akan tersimpan otomatis di sini.
                                </td>
                              </tr>
                            ) : (
                              eventLogs.map((log) => (
                                <tr key={log.id} className="hover:bg-slate-50">
                                  <td className="p-3 text-center font-bold text-slate-600">{log.timestamp}</td>
                                  <td className="p-3 text-center font-black">
                                    <span className={`px-2.5 py-1 rounded text-[10px] uppercase font-black tracking-wider ${
                                      log.actionType.includes('RESTORE') ? 'bg-blue-100 text-blue-800' :
                                      log.actionType.includes('DELETE') ? 'bg-rose-100 text-rose-800' :
                                      log.actionType.includes('IMPORT') ? 'bg-emerald-100 text-emerald-800' :
                                      'bg-amber-100 text-amber-800'
                                    }`}>
                                      {log.actionType}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center font-bold text-slate-800 uppercase">{log.actorName}</td>
                                  <td className="p-3 text-left font-semibold text-slate-700 max-w-md">{log.description}</td>
                                  <td className="p-3 text-center font-mono font-bold">
                                    <span className="text-slate-500">{log.beforeCount}</span>
                                    <span className="mx-1.5 text-slate-400">➔</span>
                                    <span className={log.afterCount >= log.beforeCount ? 'text-emerald-600 font-black' : 'text-rose-600 font-black'}>
                                      {log.afterCount} TO
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </main>
            </div>

            {/* MOBILE ADMIN BOTTOM NAV */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-between px-2 py-2 pb-[env(safe-area-inset-bottom,16px)] z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] overflow-x-auto hide-scrollbar">
              <div className="flex w-max space-x-1 mx-auto">
                <button onClick={() => setAdminTab('dashboard')} className={`flex flex-col items-center px-4 py-2 rounded-xl transition ${adminTab === 'dashboard' ? 'text-[#218DAE] bg-cyan-50' : 'text-slate-400 hover:text-slate-600'}`}>
                  <LayoutDashboard size={20} />
                  <span className="text-[9px] font-bold mt-1">Dashboard</span>
                </button>
                <button onClick={() => setAdminTab('pengguna')} className={`flex flex-col items-center px-4 py-2 rounded-xl transition ${adminTab === 'pengguna' ? 'text-[#218DAE] bg-cyan-50' : 'text-slate-400 hover:text-slate-600'}`}>
                  <Users size={20} />
                  <span className="text-[9px] font-bold mt-1">Pengguna</span>
                </button>
                <button onClick={() => setAdminTab('validasi')} className={`flex flex-col items-center px-4 py-2 rounded-xl transition ${adminTab === 'validasi' ? 'text-[#218DAE] bg-cyan-50' : 'text-slate-400 hover:text-slate-600'}`}>
                  <CheckSquare size={20} />
                  <span className="text-[9px] font-bold mt-1">Validasi</span>
                </button>
                <button onClick={() => setAdminTab('import_dil')} className={`flex flex-col items-center px-4 py-2 rounded-xl transition ${adminTab === 'import_dil' ? 'text-[#218DAE] bg-cyan-50' : 'text-slate-400 hover:text-slate-600'}`}>
                  <CloudUpload size={20} />
                  <span className="text-[9px] font-bold mt-1">Import DIL</span>
                </button>
                <button onClick={() => setAdminTab('laporan')} className={`flex flex-col items-center px-4 py-2 rounded-xl transition ${adminTab === 'laporan' ? 'text-[#218DAE] bg-cyan-50' : 'text-slate-400 hover:text-slate-600'}`}>
                  <BarChart3 size={20} />
                  <span className="text-[9px] font-bold mt-1">Laporan</span>
                </button>
                <button onClick={handleLogout} className="flex flex-col items-center px-4 py-2 rounded-xl text-rose-500 hover:bg-rose-50 transition">
                  <LogOut size={20} />
                  <span className="text-[9px] font-bold mt-1">Keluar</span>
                </button>
              </div>
            </nav>
          </div>
        )}

        {/* 3. SURVEYOR DASHBOARD VIEW */}
        {!isAdmin && currentView === 'surveyor_dashboard' && (
          <div className="min-h-screen w-full flex flex-col bg-slate-100 text-slate-800 overflow-y-auto">
            <div className="flex-1 flex flex-col w-full pb-24">
              <header className="px-5 pt-5 pb-8 bg-[#218DAE] text-white shadow-md rounded-b-[2.5rem] space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <button onClick={handleSyncData} className="p-2.5 bg-white/20 rounded-xl hover:bg-white/35 transition" title="Sinkronisasi Data">
                      <RefreshCw size={18} />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center font-black text-base">{currentUser?.name?.charAt(0)}</div>
                    <div>
                      <h1 className="text-sm font-black">{currentUser?.name}</h1>
                      <p className="text-[10px] opacity-85 uppercase font-bold tracking-wider">SURVEYOR LAPANGAN</p>
                    </div>
                  </div>
                  <button onClick={handleLogout} className="p-2.5 bg-white/20 rounded-xl hover:bg-white/35 transition">
                    <LogOut size={18} />
                  </button>
                </div>

                <button
                  onClick={() => setShowSuratTugasMobileModal(true)}
                  className="w-full py-3.5 bg-white text-[#218DAE] hover:bg-slate-50 rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg flex items-center justify-center space-x-2 transition active:scale-95"
                >
                  <FileText size={18} className="text-[#218DAE]" />
                  <span>📄 LIHAT SURAT TUGAS RESMI PLN</span>
                </button>

                <div className="relative w-full">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={16} />
                  <input
                    type="text"
                    value={surveyorSearchQuery}
                    onChange={e => setSurveyorSearchQuery(e.target.value)}
                    placeholder="Cari DIL atau Nama Pelanggan..."
                    className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/20 border border-white/25 text-xs font-bold text-white placeholder-white/70 outline-none backdrop-blur-sm"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3 pt-1">
                  <div className="bg-white/15 backdrop-blur-sm p-3 rounded-2xl border border-white/20 text-center">
                    <span className="text-[9px] font-black uppercase tracking-widest text-blue-100 block">TUGAS ANDA</span>
                    <span className="text-lg font-black text-white mt-0.5 block">{surveyorStats.pending}</span>
                  </div>
                  <div onClick={() => setShowSurveyedModal(true)} className="bg-white/15 backdrop-blur-sm p-3 rounded-2xl border border-white/20 text-center cursor-pointer hover:bg-white/25 transition active:scale-95">
                    <span className="text-[9px] font-black uppercase tracking-widest text-blue-100 block">SELESAI</span>
                    <span className="text-lg font-black text-white mt-0.5 block">{surveyorStats.completed}</span>
                  </div>
                  <div className="bg-emerald-500/40 backdrop-blur-sm p-3 rounded-2xl border border-emerald-400/40 text-center flex flex-col justify-center items-center">
                    <span className="text-[9px] font-black uppercase tracking-widest text-emerald-100 block">ONLINE</span>
                    <span className="text-xs font-black text-emerald-200 mt-0.5 flex items-center justify-center"><span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse mr-1.5" />Aktif</span>
                  </div>
                </div>
              </header>

              <main className="p-4 sm:p-6 w-full space-y-4">
                {/* Rejection Alert Banner for Surveyor */}
                {(() => {
                  const rejectedList = customers.filter(c => isRejectedForCurrentUser(c, currentUser));
                  if (rejectedList.length === 0) return null;
                  return (
                    <div className="p-4 bg-rose-50 border-2 border-rose-300 rounded-3xl flex items-center justify-between shadow-md space-x-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-2xl bg-rose-600 text-white flex items-center justify-center font-black shrink-0">
                          <AlertTriangle size={22} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black uppercase text-rose-900 tracking-tight">ADA {rejectedList.length} SURVEI DITOLAK ADMIN!</h4>
                          <p className="text-[10px] font-bold text-rose-700 uppercase mt-0.5">Periksa tab "Ditolak" untuk melihat alasan & survei ulang.</p>
                        </div>
                      </div>
                      <button onClick={() => setSurveyorTab('ditolak')} className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shrink-0 shadow-sm transition">
                        Lihat
                      </button>
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">Daftar Survey</h3>
                  <div
                    onClick={() => {
                      const next = !isNearestFilter;
                      setIsNearestFilter(next);
                      showToast(next ? '📍 Filter Terdekat GPS Aktif' : '📋 Menampilkan Semua TO');
                    }}
                    className={`flex items-center space-x-1 text-xs font-black cursor-pointer px-3 py-1.5 rounded-xl transition ${isNearestFilter ? 'bg-[#218DAE] text-white shadow-xs' : 'text-slate-500 hover:bg-slate-200'}`}
                  >
                    <ArrowUpDown size={13} />
                    <span>{isNearestFilter ? 'TERDEKAT (AKTIF)' : 'TERDEKAT / SEMUA'}</span>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button onClick={() => setSurveyorTab('semua')} className={`flex-1 py-3 rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition ${surveyorTab === 'semua' ? 'bg-[#218DAE] text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200'}`}>
                    Semua Tugas ({surveyorStats.pending})
                  </button>
                  <button onClick={() => setSurveyorTab('ditolak')} className={`flex-1 py-3 rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition flex items-center justify-center space-x-1.5 ${surveyorTab === 'ditolak' ? 'bg-rose-500 text-white shadow-md' : 'bg-white text-rose-600 border border-rose-200'}`}>
                    <AlertTriangle size={14} />
                    <span>Ditolak ({surveyorStats.rejected})</span>
                  </button>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                  <span className="text-xs font-black text-slate-500">FILTER DESA:</span>
                  <select value={surveyorDesaFilter} onChange={e => setSurveyorDesaFilter(e.target.value)} className="px-4 py-2 rounded-xl border text-xs font-bold outline-none bg-slate-50 text-slate-800 border-slate-200">
                    <option value="ALL">Semua Desa</option>
                    {[...new Set(customers.map(c => c.desa).filter(Boolean))].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  {sortedSurveyorList.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-2">
                      <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
                      <h4 className="text-sm font-black text-slate-800">Tidak ada penugasan survei.</h4>
                    </div>
                  ) : (
                    visibleSurveyorCustomers.map(c => (
                      <div key={c.id} className="p-5 bg-white rounded-3xl border border-slate-200 shadow-sm space-y-3.5 hover:shadow-md transition">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <h4 className="text-base font-black text-slate-900 uppercase tracking-tight">{c.name}</h4>
                            <div className="flex flex-wrap items-center gap-2 text-xs font-black">
                              <span className="text-[#218DAE]">{c.dil}</span>
                              <span className="text-slate-300">•</span>
                              <span className="text-slate-700">{c.daya} VA</span>
                              <span className="text-slate-300">•</span>
                              <span className="text-rose-600 uppercase">{c.tarif || c.jenis_kwh || 'PRABAYAR'}</span>
                            </div>
                            <div className="text-xs font-black text-rose-700 uppercase tracking-wide">
                              NO. KWH METER: {c.kwh_meter_awal || c.kwh_meter || '-'}
                            </div>
                          </div>
                          <span className="text-xs font-black text-slate-500 bg-slate-100 px-3 py-1.5 rounded-xl">
                            {c.calculatedDist > 1000 ? (c.calculatedDist / 1000).toFixed(1) + ' km' : c.calculatedDist + ' m'}
                          </span>
                        </div>

                        {c.validationNote && (
                          <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl space-y-1">
                            <span className="text-[10px] font-black text-rose-700 uppercase tracking-wider flex items-center">
                              <AlertTriangle size={12} className="mr-1" /> ALASAN PENOLAKAN ADMIN:
                            </span>
                            <p className="text-xs font-bold text-rose-900 italic">"{c.validationNote}"</p>
                          </div>
                        )}

                        <div
                          onClick={() => {
                            const url = `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`;
                            window.open(url, '_blank');
                          }}
                          className="flex items-center space-x-2 text-xs font-bold text-slate-600 bg-slate-50 p-3 rounded-2xl border border-slate-100 cursor-pointer hover:bg-cyan-50/50 hover:border-[#218DAE]/30 transition"
                          title="Buka di Google Maps"
                        >
                          <MapPin size={15} className="text-[#218DAE] shrink-0" />
                          <span className="uppercase truncate">DESA {c.desa || '-'} • {c.alamat || 'ALAMAT TIDAK TERSEDIA'}</span>
                          <ExternalLink size={13} className="ml-auto text-[#218DAE] shrink-0" />
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <div className="text-xs font-black text-slate-400 flex items-center">
                            <Clock size={13} className="mr-1.5 text-amber-500" />
                            <span className="uppercase">{c.validationNote ? 'DITOLAK ADMIN' : 'MENUNGGU SURVEY'}</span>
                          </div>
                          <button
                            onClick={() => handleSelectCustomerForRoute(c)}
                            className={`px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider shadow-md transition flex items-center space-x-1.5 ${c.validationNote ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-[#218DAE] text-white hover:brightness-110'}`}
                          >
                            <span>{c.validationNote ? 'Survei Ulang' : 'Mulai Navigasi'}</span>
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  {sortedSurveyorList.length > visibleSurveyorLimit && (
                    <button
                      onClick={() => setVisibleSurveyorLimit(prev => prev + 50)}
                      className="w-full py-4 bg-white border border-slate-200 text-slate-800 font-black rounded-2xl text-xs uppercase tracking-wider transition shadow-sm hover:bg-slate-50 active:scale-95 text-center flex items-center justify-center space-x-2 mt-3"
                    >
                      <span>Tampilkan Lebih Banyak (+50 TO)</span>
                      <span className="text-[10px] bg-cyan-100 text-[#218DAE] px-2.5 py-1 rounded-full font-bold">Tersisa {sortedSurveyorList.length - visibleSurveyorLimit} TO</span>
                    </button>
                  )}
                </div>
              </main>
            </div>
          </div>
        )}

        {/* 4. ROUTE / NAVIGATION VIEW */}
        {currentView === 'route' && selectedCustomer && (
          <div className="flex h-screen w-full flex-col bg-slate-900 text-white relative">
            <div className="absolute top-4 left-4 z-[999]">
              <button
                onClick={() => setCurrentView('surveyor_dashboard')}
                className="w-12 h-12 bg-white text-slate-900 rounded-2xl shadow-2xl flex items-center justify-center hover:bg-slate-100 active:scale-95 transition"
              >
                <ArrowLeft size={24} />
              </button>
            </div>

            <div className="flex-1 relative">
              <MapContainer center={[currentPos.lat, currentPos.lng]} zoom={17} style={{ width: '100%', height: '100%' }} zoomControl={false}>
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Tiles &copy; Esri"
                />
                <Marker position={[selectedCustomer.lat, selectedCustomer.lng]} />
                <Circle center={[selectedCustomer.lat, selectedCustomer.lng]} radius={GEOFENCE_RADIUS_METERS} pathOptions={{ color: '#218DAE', fillColor: '#218DAE', fillOpacity: 0.2, weight: 1 }} />
                <Marker position={[currentPos.lat, currentPos.lng]} />
                <Polyline positions={[[currentPos.lat, currentPos.lng], [selectedCustomer.lat, selectedCustomer.lng]]} color="#218DAE" weight={6} />
                <MapUpdater center={[currentPos.lat, currentPos.lng]} />
              </MapContainer>

              <div className="absolute bottom-6 left-6 right-6 z-[400] bg-white text-slate-800 p-6 rounded-[2.5rem] shadow-2xl space-y-5 border border-slate-100">
                <div className="space-y-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">TUJUAN ANDA</span>
                  <h3 className="text-xl font-black text-slate-900 uppercase">{selectedCustomer.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-black">
                    <span className="text-rose-700">NO. KWH METER: {selectedCustomer.kwh_meter_awal || selectedCustomer.kwh_meter || '-'}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-[#218DAE]">{selectedCustomer.daya} VA</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-rose-600 uppercase">
                      {selectedCustomer.tarif || selectedCustomer.jenis_kwh || 'PRABAYAR'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center text-xs font-bold text-slate-600 pr-2 overflow-hidden">
                      <MapPin size={14} className="text-[#218DAE] mr-1 shrink-0" />
                      <span className="uppercase truncate">
                        DESA {selectedCustomer.desa || '-'} {selectedCustomer.alamat ? `• ${selectedCustomer.alamat}` : ''}
                      </span>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-2xl font-black text-[#218DAE]">1</span>
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">MENIT</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedCustomer.lat},${selectedCustomer.lng}`;
                    window.open(url, '_blank');
                  }}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg flex items-center justify-center space-x-2 hover:brightness-110 transition"
                >
                  <Navigation size={18} />
                  <span>START NAVIGASI KE LOKASI</span>
                </button>

                {(() => {
                  const dist = calculateDistance(currentPos.lat, currentPos.lng, selectedCustomer.lat, selectedCustomer.lng);
                  return (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <button onClick={() => {
                        const url = `https://www.google.com/maps/search/?api=1&query=${selectedCustomer.lat},${selectedCustomer.lng}`;
                        window.open(url, '_blank');
                      }} className="py-3.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-2xl text-xs font-black uppercase flex items-center justify-center space-x-1.5 transition">
                        <MapPin size={15} className="text-[#218DAE]" />
                        <span>Titik Lokasi</span>
                      </button>
                      <button
                        onClick={() => {
                          const latestCust = customers.find(c => String(c.id) === String(selectedCustomer?.id) || (c.dil && String(c.dil) === String(selectedCustomer?.dil)));
                          if (latestCust && latestCust.status && latestCust.status !== 'pending' && latestCust.status !== 'rejected') {
                            const surveyorName = latestCust.surveyorUsername || latestCust.assignedTo || 'petugas lain';
                            showToast(`⚠️ Pelanggan ini sudah disurvei oleh ${surveyorName}! TO telah otomatis diperbarui.`);
                            setCurrentView('surveyor_dashboard');
                            return;
                          }
                          setSurveyMode('form');
                          setCurrentView('survey_form');
                        }}
                        className="py-3.5 bg-[#218DAE] text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-md flex items-center justify-center space-x-1.5 hover:brightness-110 transition relative overflow-hidden"
                      >
                        <Camera size={15} />
                        <span>Mulai Survey</span>
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* FULL SCREEN PHOTO PREVIEW OVERLAY */}
        {surveyMode === 'preview' && capturedPreviewUrl && (
          <div className="fixed inset-0 z-[999] bg-black flex flex-col justify-between p-6">
            <div className="flex justify-between items-center text-white z-10 pt-4">
              <div className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider text-cyan-300">
                Tinjau Hasil Foto ({photoStep === 'kwh' ? 'KWh Meter' : 'Depan Rumah'})
              </div>
            </div>

            <div className="flex-1 relative flex items-center justify-center overflow-hidden my-4 rounded-[2.5rem] bg-black border border-white/10 shadow-2xl">
              <img src={capturedPreviewUrl} alt="Preview" className="absolute inset-0 w-full h-full object-contain" />
            </div>

            <div className="flex space-x-3 pb-6 z-10 max-w-xl mx-auto w-full">
              <button onClick={() => { setCapturedPreviewUrl(null); startLiveCamera(); }} className="flex-1 py-4 bg-white/20 hover:bg-white/30 text-white rounded-2xl text-xs font-black uppercase tracking-wider backdrop-blur-md transition">
                Foto Ulang
              </button>
              <button onClick={handleConfirmCapturedPhoto} className="flex-1 py-4 bg-[#218DAE] hover:brightness-110 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-xl transition">
                Gunakan Foto
              </button>
            </div>
          </div>
        )}

        {/* 5. CAMERA VIEW FOR NATIVE CAMERA PREVIEW */}
        {surveyMode === 'camera' && (
          <div className="fixed inset-0 z-[999] camera-container flex flex-col justify-between p-4 pointer-events-none">
            <div className="flex justify-between items-center text-white z-30 pt-6 px-2 pointer-events-auto">
              <button onClick={() => { stopLiveCamera(true); }} className="p-3 bg-black/40 rounded-full backdrop-blur-md text-white"><X size={24} /></button>
              <div className="bg-black/60 px-5 py-2.5 rounded-full backdrop-blur-md text-xs font-black uppercase tracking-wider text-cyan-300 shadow-lg">
                {photoStep === 'kwh' ? 'Langkah 1: Foto KWh Meter' : 'Langkah 2: Foto Rumah'}
              </div>
              <div className="w-10"></div>
            </div>

            <div className="flex-1 relative flex items-center justify-center bg-transparent">
              {showCameraFlash && <div className="absolute inset-0 bg-white z-50 animate-pulse"></div>}
            </div>

            <div className="flex flex-col items-center pb-12 z-30 pointer-events-auto space-y-4">
              <button
                onClick={captureFrameFromVideo}
                className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center bg-white/20 active:scale-95 transition shadow-2xl"
              >
                <div className="w-16 h-16 bg-white rounded-full"></div>
              </button>
            </div>
          </div>
        )}

        {/* 6. SURVEY FORM VIEW */}
        {currentView === 'survey_form' && (
          <div className={`flex h-screen w-full flex-col overflow-y-auto ${surveyMode === 'camera' ? 'bg-transparent' : 'bg-slate-50 text-slate-800'}`}>
            {surveyMode !== 'camera' && (
              <>
                <header className="px-6 py-4 bg-[#218DAE] text-white flex justify-between items-center sticky top-0 z-30 shadow-md">
                  <button onClick={() => { setSurveyMode('form'); setCurrentView('route'); }} className="p-2 bg-white/20 rounded-xl"><ArrowLeft size={20} /></button>
                  <div className="text-center">
                    <h1 className="text-sm font-black">Formulir Survei</h1>
                    <p className="text-[10px] text-cyan-100 font-bold uppercase">{selectedCustomer?.name}</p>
                  </div>
                  <div className="w-10"></div>
                </header>

                <main className="p-6 max-w-xl mx-auto w-full space-y-6">
                  <div className="space-y-6 bg-white p-6 sm:p-8 rounded-[2.5rem] border shadow-sm">
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">1. BUKTI FOTO (WAJIB)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div onClick={() => { setPhotoStep('kwh'); startLiveCamera(); }} className="cursor-pointer border-2 border-dashed border-slate-300 rounded-3xl p-5 text-center space-y-2 hover:border-[#218DAE] transition bg-slate-50/50">
                      {kwhPhotoUri ? (
                        <img src={kwhPhotoUri} alt="KWh" className="w-full h-24 object-cover rounded-2xl" />
                      ) : (
                        <div className="w-14 h-14 bg-cyan-50 text-[#218DAE] rounded-2xl flex items-center justify-center mx-auto shadow-sm"><Camera size={26} /></div>
                      )}
                      <span className="text-[10px] font-black uppercase tracking-wider block text-slate-600">FOTO KWH METER</span>
                    </div>

                    <div onClick={() => { setPhotoStep('house'); startLiveCamera(); }} className="cursor-pointer border-2 border-dashed border-slate-300 rounded-3xl p-5 text-center space-y-2 hover:border-[#218DAE] transition bg-slate-50/50">
                      {housePhotoUri ? (
                        <img src={housePhotoUri} alt="House" className="w-full h-24 object-cover rounded-2xl" />
                      ) : (
                        <div className="w-14 h-14 bg-cyan-50 text-[#218DAE] rounded-2xl flex items-center justify-center mx-auto"><Building2 size={26} /></div>
                      )}
                      <span className="text-[10px] font-black uppercase tracking-wider block text-slate-600">FOTO DEPAN RUMAH</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">2. VERIFIKASI LAPANGAN</h3>
                  <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-black text-slate-800 uppercase">Kesesuaian Lokasi</h4>
                        <p className="text-[10px] text-slate-400 font-bold mt-0.5">TITIK KORDINAT SUDAH SESUAI?</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSurveyMatch(!surveyMatch)}
                        className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors ${surveyMatch ? 'bg-emerald-500 justify-end' : 'bg-slate-300 justify-start'}`}
                      >
                        <div className="bg-white w-6 h-6 rounded-full shadow-md transform transition-transform"></div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">CATATAN TAMBAHAN</label>
                  <textarea
                    rows="3"
                    value={surveyNote}
                    onChange={e => setSurveyNote(e.target.value)}
                    placeholder="Tuliskan keterangan jika ada kendala di lapangan..."
                    className="w-full px-5 py-4 rounded-3xl border text-xs font-black outline-none bg-slate-50 border-slate-200 placeholder:text-slate-400"
                  ></textarea>
                </div>

                <button
                  disabled={!kwhPhotoUri || !housePhotoUri || isSaving}
                  onClick={handleSaveSurvey}
                  className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg transition-all ${kwhPhotoUri && housePhotoUri && !isSaving ? 'bg-[#218DAE] text-white hover:brightness-110' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                >
                  {isSaving ? 'Menyimpan...' : 'Kirim Hasil Survei'}
                </button>
              </div>
            </main>
              </>
            )}
          </div>
        )}

      </div>

      {/* USER MODAL (ADD / EDIT) */}
      {userModal.show && (
        <div className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl space-y-6 text-slate-800">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-black uppercase">{userModal.mode === 'add' ? 'Tambah Pengguna Baru' : 'Edit Pengguna'}</h3>
              <button onClick={() => setUserModal({ show: false, mode: 'add', user: null })} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveUserModal} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Nama Lengkap</label>
                <input type="text" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" placeholder="Nama Pengguna" required />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Username</label>
                <input type="text" value={userForm.username} onChange={e => setUserForm({...userForm, username: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" placeholder="username" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Role</label>
                  <select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]">
                    <option value="surveyor">Surveyor</option>
                    <option value="admin">Admin</option>
                    <option value="validator">Validator</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">ULP / Unit</label>
                  <select value={userForm.ulp} onChange={e => setUserForm({...userForm, ulp: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]">
                    <option value="UP3 Tual">UP3 Tual</option>
                    <option value="Tual Kota">Tual Kota</option>
                    <option value="ULP Elat">ULP Elat</option>
                    <option value="ULP Dobo">ULP Dobo</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Password</label>
                <input type="text" value={userForm.pass} onChange={e => setUserForm({...userForm, pass: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" placeholder="Password" required />
              </div>
              <button type="submit" className="w-full py-4 bg-[#218DAE] text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-110 transition">
                {userModal.mode === 'add' ? 'Simpan Pengguna' : 'Perbarui Pengguna'}
              </button>
            </form>
          </div>
        </div>
      )}

      
      {/* ADD SINGLE CUSTOMER MODAL */}
      {addCustomerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-2xl p-8 space-y-6 my-8 border border-slate-100">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-black uppercase tracking-tight text-slate-900">Tambah 1 Target Operasi (TO) Manual</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">MASUKKAN DETAIL PELANGGAN BARU</p>
              </div>
              <button onClick={() => setAddCustomerModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveNewCustomer} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">IDPEL / DIL *</label>
                  <input type="text" value={addCustomerForm.dil} onChange={e => setAddCustomerForm({ ...addCustomerForm, dil: e.target.value })} required placeholder="Contoh: 5120012345" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Nama Pelanggan *</label>
                  <input type="text" value={addCustomerForm.name} onChange={e => setAddCustomerForm({ ...addCustomerForm, name: e.target.value })} required placeholder="Nama lengkap pelanggan" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">No. KWh Meter</label>
                  <input type="text" value={addCustomerForm.kwh_meter_awal} onChange={e => setAddCustomerForm({ ...addCustomerForm, kwh_meter_awal: e.target.value })} placeholder="No KWh" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Daya (VA)</label>
                  <input type="text" value={addCustomerForm.daya} onChange={e => setAddCustomerForm({ ...addCustomerForm, daya: e.target.value })} placeholder="900" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Tarif / Jenis</label>
                  <input type="text" value={addCustomerForm.tarif} onChange={e => setAddCustomerForm({ ...addCustomerForm, tarif: e.target.value })} placeholder="R1MT" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Alamat Lengkap</label>
                <input type="text" value={addCustomerForm.alamat} onChange={e => setAddCustomerForm({ ...addCustomerForm, alamat: e.target.value })} placeholder="Jl. Merdeka No. 1" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Desa / Kelurahan</label>
                  <input type="text" value={addCustomerForm.desa} onChange={e => setAddCustomerForm({ ...addCustomerForm, desa: e.target.value })} placeholder="KOLSER" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Unit / ULP</label>
                  <select value={addCustomerForm.ulp} onChange={e => setAddCustomerForm({ ...addCustomerForm, ulp: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold bg-white outline-none focus:border-[#218DAE]">
                    <option value="Tual Kota">ULP Tual Kota</option>
                    <option value="ULP Dobo">ULP Dobo</option>
                    <option value="ULP Elat">ULP Elat</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Latitude GPS</label>
                  <input type="text" value={addCustomerForm.lat} onChange={e => setAddCustomerForm({ ...addCustomerForm, lat: e.target.value })} placeholder="-5.6241" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Longitude GPS</label>
                  <input type="text" value={addCustomerForm.lng} onChange={e => setAddCustomerForm({ ...addCustomerForm, lng: e.target.value })} placeholder="132.7470" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs font-bold outline-none focus:border-[#218DAE]" />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setAddCustomerModal(false)} className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider transition">Batal</button>
                <button type="submit" className="flex-1 py-3.5 bg-[#218DAE] hover:brightness-110 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-md transition">Simpan & Assign TO</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT CUSTOMER MODAL */}
      {editCustomerModal.show && (
        <div className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl space-y-6 text-slate-800">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-black uppercase">Edit Data Pelanggan DIL</h3>
              <button onClick={() => setEditCustomerModal({ show: false, customer: null })} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveCustomerEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">No. DIL / IDPEL</label>
                  <input type="text" value={editCustomerForm.dil} onChange={e => setEditCustomerForm({...editCustomerForm, dil: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Nama Pelanggan</label>
                  <input type="text" value={editCustomerForm.name} onChange={e => setEditCustomerForm({...editCustomerForm, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Daya (VA)</label>
                  <input type="text" value={editCustomerForm.daya} onChange={e => setEditCustomerForm({...editCustomerForm, daya: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Tarif / Jenis</label>
                  <input type="text" value={editCustomerForm.tarif} onChange={e => setEditCustomerForm({...editCustomerForm, tarif: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Alamat</label>
                <input type="text" value={editCustomerForm.alamat} onChange={e => setEditCustomerForm({...editCustomerForm, alamat: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Desa</label>
                  <input type="text" value={editCustomerForm.desa} onChange={e => setEditCustomerForm({...editCustomerForm, desa: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]" required />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">ULP / Unit</label>
                  <select value={editCustomerForm.ulp} onChange={e => setEditCustomerForm({...editCustomerForm, ulp: e.target.value})} className="w-full px-4 py-3 rounded-xl border text-xs font-semibold outline-none bg-slate-50 focus:border-[#218DAE]">
                    <option value="UP3 Tual">UP3 Tual</option>
                    <option value="Tual Kota">Tual Kota</option>
                    <option value="ULP Elat">ULP Elat</option>
                    <option value="ULP Dobo">ULP Dobo</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="w-full py-4 bg-[#218DAE] text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-lg hover:brightness-110 transition">
                Simpan Perubahan
              </button>
            </form>
          </div>
        </div>
      )}

      {/* SURVEYED CUSTOMERS LIST MODAL */}
      {showSurveyedModal && (
        <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[85vh] shadow-2xl flex flex-col space-y-4 text-slate-800 overflow-hidden">
            <div className="flex justify-between items-center border-b pb-3 shrink-0">
              <div>
                <h3 className="text-sm font-black uppercase text-slate-900">Daftar Pelanggan Selesai / Menunggu Validasi</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase">Petugas: {currentUser?.name}</p>
              </div>
              <button onClick={() => setShowSurveyedModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {customers.filter(c => isCompletedByCurrentUser(c, currentUser)).length === 0 ? (
                <div className="text-center py-12 text-slate-400 font-bold italic">
                  Belum ada tugas survei yang diselesaikan.
                </div>
              ) : (
                customers
                  .filter(c => isCompletedByCurrentUser(c, currentUser))
                  .map(c => (
                    <div key={c.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-xs font-black text-slate-900 uppercase">{c.name}</h4>
                          <p className="text-[10px] font-bold text-[#218DAE]">DIL: {c.dil} • {c.daya} VA</p>
                        </div>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="text-[11px] font-bold text-slate-600">
                        Desa: <span className="uppercase">{c.desa || '-'}</span> • Waktu: {c.tgl_survey || '-'}
                      </div>
                    </div>
                  ))
              )}
            </div>

            <button onClick={() => setShowSurveyedModal(false)} className="w-full py-3.5 bg-[#218DAE] text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-md hover:brightness-110 transition shrink-0">
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* FULL SURAT TUGAS VIEWER MODAL */}
      {showSuratTugasMobileModal && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] shadow-2xl flex flex-col space-y-4 text-slate-800 overflow-hidden my-auto border border-slate-200">
            <div className="flex justify-between items-center border-b pb-3 shrink-0">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-xl bg-cyan-50 text-[#218DAE] flex items-center justify-center font-black">
                  <FileText size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase text-slate-900">Dokumen Surat Tugas Resmi PLN</h3>
                  <p className="text-[10px] text-slate-500 font-bold uppercase">{suratTugasDoc.name || 'Surat_Tugas_Resmi_UP3_Tual_2026.pdf'}</p>
                </div>
              </div>
              <button onClick={() => setShowSuratTugasMobileModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-100 rounded-2xl border border-slate-200 p-2 flex items-center justify-center min-h-[450px]">
              {suratTugasDoc.url ? (
                suratTugasDoc.type === 'pdf' || getPhotoUrl(suratTugasDoc.url).toLowerCase().includes('.pdf') ? (
                  <div className="w-full h-full min-h-[420px] flex flex-col space-y-2">
                    <iframe
                      src={getPhotoUrl(suratTugasDoc.url)}
                      title="Dokumen Surat Tugas Resmi PDF"
                      className="w-full h-[58vh] rounded-xl border border-slate-300 bg-white shadow-inner"
                    />
                    <div className="flex justify-center pt-1">
                      <button
                        type="button"
                        onClick={() => window.open(getPhotoUrl(suratTugasDoc.url), '_system')}
                        className="px-4 py-2 bg-[#218DAE] text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-sm hover:brightness-110 transition flex items-center space-x-1.5"
                      >
                        <Download size={14} />
                        <span>Buka PDF di Tab / Aplikasi Eksternal</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <img
                    src={getPhotoUrl(suratTugasDoc.url)}
                    alt="Surat Tugas Resmi"
                    className="max-w-full max-h-[65vh] object-contain rounded-xl shadow-lg border border-slate-300"
                  />
                )
              ) : (
                /* OFFICIAL PLN LETTERHEAD DEFAULT PREVIEW */
                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md border border-slate-200 max-w-lg w-full space-y-5 text-left relative overflow-hidden">
                  {/* Official Stamp Watermark Badge */}
                  <div className="absolute top-4 right-4 bg-emerald-50 border-2 border-emerald-500 text-emerald-700 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-xs flex items-center space-x-1 rotate-3">
                    <ShieldCheck size={14} />
                    <span>STEMPEL RESMI UP3 TUAL</span>
                  </div>

                  {/* Header */}
                  <div className="border-b-2 border-[#218DAE] pb-4 space-y-1">
                    <div className="flex items-center space-x-2 text-[#218DAE] font-black text-sm uppercase">
                      <Building2 size={20} />
                      <span>PT PLN (PERSERO) UP3 TUAL</span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">UNIT PELAKSANA PELAYANAN PELANGGAN TUAL</p>
                    <p className="text-[9px] text-slate-400 font-mono">Jl. Soekarno Hatta No. 42, Kota Tual, Maluku Tenggara</p>
                  </div>

                  {/* Title */}
                  <div className="text-center space-y-1 py-1">
                    <h4 className="text-xs font-black uppercase tracking-wide text-slate-900 underline">SURAT TUGAS RESMI PEMETAAN GEOSPASIAL</h4>
                    <p className="text-[10px] font-mono font-bold text-slate-500">Nomor: 0042/ST/UP3-TUAL/2026</p>
                  </div>

                  {/* Body Content */}
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs space-y-2.5 text-slate-700 font-medium">
                    <p className="text-[11px] font-bold text-slate-800">
                      Menugaskan kepada seluruh Petugas Surveyor Lapangan terdaftar untuk melaksanakan:
                    </p>
                    <ul className="list-disc pl-5 text-[11px] space-y-1 text-slate-600 font-semibold">
                      <li>Pemetaan Geospasial Titik Lokasi Pelanggan (GPS Coordinate).</li>
                      <li>Verifikasi Fisik Nomor KWh Meter & Dokumentasi Foto Rumah.</li>
                      <li>Pembaruan Data DIL Pelanggan ULP Tual Kota, Dobo, & Elat.</li>
                    </ul>
                    <div className="pt-2 border-t border-slate-200 grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <span className="text-slate-400 font-bold uppercase block">Masa Berlaku:</span>
                        <strong className="text-slate-800 font-black">1 Juni 2026 - 31 Des 2026</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 font-bold uppercase block">Status Dokumen:</span>
                        <strong className="text-emerald-600 font-black uppercase">AKTIF & RESMI</strong>
                      </div>
                    </div>
                  </div>

                  <p className="text-[10px] text-slate-400 italic text-center">
                    Dokumen ini diterbitkan resmi oleh Manager UP3 Tual untuk Operasi Lapangan 2026.
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3 shrink-0 pt-2">
              {suratTugasDoc.url && (
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = getPhotoUrl(suratTugasDoc.url);
                    link.download = suratTugasDoc.name;
                    link.click();
                  }}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-md hover:bg-emerald-700 transition flex items-center justify-center space-x-2"
                >
                  <Download size={16} />
                  <span>Download Dokumen</span>
                </button>
              )}
              <button
                onClick={() => setShowSuratTugasMobileModal(false)}
                className="flex-1 py-3 bg-[#218DAE] text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-md hover:brightness-110 transition"
              >
                Tutup Viewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING SURAT TUGAS BUTTON FOR MOBILE SURVEYOR */}
      {!isAdmin && currentView === 'surveyor_dashboard' && (
        <div className="fixed bottom-6 right-6 z-[950]">
          <button
            onClick={() => setShowSuratTugasMobileModal(true)}
            className="px-5 py-3.5 bg-[#218DAE] text-white rounded-full font-black text-xs uppercase tracking-wider shadow-2xl flex items-center space-x-2 border-2 border-white hover:scale-105 active:scale-95 transition"
          >
            <FileText size={18} />
            <span>Surat Tugas</span>
          </button>
        </div>
      )}

      {/* MAP COMPARISON MODAL */}
      {mapComparisonModal.show && mapComparisonModal.customer && (() => {
        const c = mapComparisonModal.customer;
        const dilLat = c.lat || -5.6241;
        const dilLng = c.lng || 132.7470;
        const gpsLat = c.surveyData?.lat || dilLat;
        const gpsLng = c.surveyData?.lng || dilLng;
        const dist = calculateDistance(dilLat, dilLng, gpsLat, gpsLng);
        const isMatch = dist <= 50;

        return (
          <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-6 max-w-2xl w-full shadow-2xl space-y-4 text-slate-800 border border-slate-200">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 text-[#218DAE] flex items-center justify-center font-black">
                    <MapPin size={22} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase text-slate-900">Perbandingan Koordinat Peta</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">{c.name} • IDPEL: {c.dil}</p>
                  </div>
                </div>
                <button onClick={() => setMapComparisonModal({ show: false, customer: null })} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition">
                  <X size={18} />
                </button>
              </div>

              {/* Map Display */}
              <div className="h-72 w-full rounded-2xl overflow-hidden border border-slate-200 relative shadow-inner">
                <MapContainer center={[dilLat, dilLng]} zoom={17} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                  <Marker position={[dilLat, dilLng]}>
                    <Popup><strong>📍 Titik DIL Awal</strong><br />{c.name}<br />{dilLat}, {dilLng}</Popup>
                  </Marker>
                  {c.surveyData?.lat && (
                    <Marker position={[gpsLat, gpsLng]}>
                      <Popup><strong>🛰️ Hasil Survei Petugas</strong><br />{gpsLat}, {gpsLng}</Popup>
                    </Marker>
                  )}
                  <Polyline positions={[[dilLat, dilLng], [gpsLat, gpsLng]]} color={isMatch ? '#10b981' : '#f43f5e'} weight={4} dashArray="6, 6" />
                  <Circle center={[dilLat, dilLng]} radius={50} pathOptions={{ color: '#218DAE', fillColor: '#218DAE', fillOpacity: 0.15 }} />
                </MapContainer>
              </div>

              {/* Info Comparison Bar */}
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-200 text-xs">
                <div className="space-y-1 border-r border-slate-200 pr-3">
                  <span className="text-[10px] font-black uppercase text-slate-400 block">1. TITIK KOORDINAT DIL AWAL</span>
                  <div className="font-mono font-bold text-slate-800">{dilLat}, {dilLng}</div>
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Desa {c.desa || '-'} • ULP {c.ulp || 'Tual Kota'}</div>
                </div>
                <div className="space-y-1 pl-1">
                  <span className="text-[10px] font-black uppercase text-slate-400 block">2. HASIL SURVEY GPS PETUGAS</span>
                  <div className="font-mono font-bold text-[#218DAE]">{gpsLat}, {gpsLng}</div>
                  <div className="flex items-center space-x-2 pt-0.5">
                    <span className={`px-2.5 py-0.5 rounded text-[10px] font-black uppercase ${isMatch ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                      {isMatch ? `Sesuai (${dist}m)` : `Beda (${dist}m)`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={() => setMapComparisonModal({ show: false, customer: null })} className="px-5 py-2.5 bg-slate-800 text-white rounded-xl text-xs font-black uppercase hover:bg-slate-900 transition">
                  Tutup Peta
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* PHOTO COMPARISON MODAL */}
      {photoComparisonModal.show && photoComparisonModal.customer && (() => {
        const c = photoComparisonModal.customer;
        const kwhNum = c.kwh_meter_awal || c.kwh_meter || '-';

        return (
          <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl p-6 max-w-3xl w-full shadow-2xl space-y-5 text-slate-800 border border-slate-200 my-8">
              {/* Header */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="px-2.5 py-0.5 bg-[#218DAE]/10 text-[#218DAE] rounded-md text-[10px] font-black uppercase">
                      IDPEL: {c.dil}
                    </span>
                    <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[10px] font-black uppercase">
                      {c.daya} VA • {c.tarif || 'PRABAYAR'}
                    </span>
                  </div>
                  <h3 className="text-lg font-black uppercase text-slate-900">{c.name}</h3>
                  <p className="text-xs font-bold text-slate-500 uppercase">
                    Desa {c.desa || '-'} • ULP {c.ulp || 'Tual Kota'} • Surveyor: <strong className="text-slate-800">{c.assignedTo || 'Petugas'}</strong>
                  </p>
                </div>
                <button onClick={() => setPhotoComparisonModal({ show: false, customer: null })} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition shrink-0">
                  <X size={18} />
                </button>
              </div>

              {/* Highlight No. KWh Meter Comparison Card */}
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex flex-wrap items-center justify-between gap-2 shadow-xs">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-rose-600 block">TARGET NOMOR KWH METER DATABASE</span>
                  <span className="text-xl font-black text-rose-700 font-mono tracking-wider">{kwhNum}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">CATATAN PETUGAS LAPANGAN</span>
                  <span className="text-xs font-bold text-slate-800 italic">"{c.surveyData?.note || 'Keterangan tidak diisi'}"</span>
                </div>
              </div>

              {/* Photos Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Photo 1: kWh Meter */}
                <div className="bg-slate-50 rounded-2xl p-3 border border-slate-200 space-y-2 text-center">
                  <div className="text-xs font-black uppercase text-slate-700 flex items-center justify-center space-x-1.5">
                    <Camera size={14} className="text-[#218DAE]" />
                    <span>1. Foto Fisik kWh Meter</span>
                  </div>
                  <ImageZoomMagnifier
                    src={c.surveyData?.photoKwh || c.photoKwh || c.kwhPhotoUri}
                    alt="Foto Fisik kWh Meter"
                    kwhNum={kwhNum}
                    onApprove={() => {
                      handleValidateCustomer(c.id, 'sudah_divalidasi');
                      setPhotoComparisonModal({ show: false, customer: null });
                    }}
                    onReject={() => {
                      setRejectModal({ show: true, customerId: c.id, customerName: c.name });
                      setRejectReasonText('');
                      setPhotoComparisonModal({ show: false, customer: null });
                    }}
                  />
                  <p className="text-[10px] text-slate-500 font-bold">Klik foto untuk Pop-Up Kaca Pembesar HD dengan Nomor KWh Meter</p>
                </div>

                {/* Photo 2: House */}
                <div className="bg-slate-50 rounded-2xl p-3 border border-slate-200 space-y-2 text-center">
                  <div className="text-xs font-black uppercase text-slate-700 flex items-center justify-center space-x-1.5">
                    <Camera size={14} className="text-amber-600" />
                    <span>2. Foto Tampak Depan Bangunan</span>
                  </div>
                  <ImageZoomMagnifier src={c.surveyData?.photoHouse || c.photoHouse || c.housePhotoUri} alt="Foto Tampak Depan Bangunan" />
                  <p className="text-[10px] text-slate-500 font-bold">Dokumentasi fisik tampak depan rumah/bangunan pelanggan</p>
                </div>
              </div>

              {/* Validation Actions */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
                <span className="text-xs font-bold text-slate-500">Aksi Validasi Langsung:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setRejectModal({ show: true, customerId: c.id, customerName: c.name });
                      setRejectReasonText('');
                      setPhotoComparisonModal({ show: false, customer: null });
                    }}
                    className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase shadow-sm transition"
                  >
                    Tolak
                  </button>
                  <button
                    onClick={() => {
                      handleValidateCustomer(c.id, 'sudah_divalidasi');
                      setPhotoComparisonModal({ show: false, customer: null });
                    }}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase shadow-md transition"
                  >
                    Setuju & Validasi
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* SERVER IP & DIAGNOSTIC MODAL */}
      {serverIpModal && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-4 text-slate-800 border border-slate-200 max-h-[90vh] flex flex-col my-auto overflow-hidden">
            <div className="flex justify-between items-center border-b pb-3 shrink-0">
              <div className="flex items-center space-x-2.5">
                <div className="w-10 h-10 rounded-2xl bg-cyan-50 text-[#218DAE] flex items-center justify-center font-black">
                  <Zap size={22} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase text-slate-900">Diagnostik Server PLN-MAPPING</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Status Jalur Koneksi & Pengambilan Data</p>
                </div>
              </div>
              <button onClick={() => setServerIpModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Active Connection Status */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">JALUR KONEKSI AKTIF SAAT INI</span>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-black text-[#218DAE] font-mono truncate">{activeWorkingUrl || 'Menggunakan Data Lokal / Offline'}</span>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase shrink-0 ${activeWorkingUrl ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {activeWorkingUrl ? '🟢 Connected' : '🟠 Offline Cache'}
                  </span>
                </div>
              </div>

              {/* Form Input Custom Server */}
              <form onSubmit={handleTestAndSaveServerIp} className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">Set Custom Server IP / URL Laptop</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={inputServerIp}
                    onChange={e => setInputServerIp(e.target.value)}
                    placeholder="Misal: 10.23.131.39 atau https://api.mapping-pel.com"
                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-xs font-bold outline-none bg-white focus:border-[#218DAE]"
                  />
                  <button
                    type="button"
                    onClick={handleTestAndSaveServerIp}
                    className="px-4 py-2.5 bg-[#218DAE] text-white hover:brightness-110 rounded-xl text-xs font-black uppercase tracking-wider shadow-xs transition"
                  >
                    Simpan
                  </button>
                </div>
              </form>

              {/* Diagnostic Test Section */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-700">Uji Respon Semua Kandidat Server</span>
                  <button
                    onClick={runServerDiagnostics}
                    disabled={isTestingServers}
                    className="px-3 py-1.5 bg-cyan-50 border border-cyan-200 text-[#218DAE] hover:bg-cyan-100 rounded-xl text-[10px] font-black uppercase tracking-wider transition flex items-center space-x-1"
                  >
                    <RefreshCw size={12} className={isTestingServers ? 'animate-spin' : ''} />
                    <span>{isTestingServers ? 'Pengujian...' : 'Uji Semua Server'}</span>
                  </button>
                </div>

                <div className="space-y-2 pt-1">
                  {serverDiagnosticList.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 font-bold text-xs italic bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-4">
                      Tekan tombol <strong className="text-[#218DAE]">"Uji Semua Server"</strong> di atas untuk melihat status respon dan jumlah TO dari tiap server.
                    </div>
                  ) : (
                    serverDiagnosticList.map((item, idx) => (
                      <div key={idx} className={`p-3 rounded-2xl border text-xs flex justify-between items-center space-x-2 ${item.ok ? 'bg-emerald-50/60 border-emerald-200' : 'bg-rose-50/60 border-rose-200'}`}>
                        <div className="space-y-0.5 truncate">
                          <div className="font-mono font-black text-slate-800 text-[11px] truncate">{item.displayUrl}</div>
                          <div className="text-[10px] font-bold text-slate-500 flex items-center space-x-2">
                            <span>Status: <strong className={item.ok ? 'text-emerald-700' : 'text-rose-700'}>{item.status}</strong></span>
                            <span>•</span>
                            <span>{item.latency}ms</span>
                          </div>
                        </div>
                        {item.ok && (
                          <button
                            onClick={() => {
                              setActiveWorkingUrl(item.base);
                              secureStorage.setItem('pln_working_server_url', item.base);
                              showToast(`✅ Menggunakan server: ${item.displayUrl}`);
                              fetchBackendData(true, true);
                            }}
                            className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[10px] font-black uppercase tracking-wider shrink-0 transition shadow-xs"
                          >
                            Gunakan
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <button onClick={() => setServerIpModal(false)} className="w-full py-3.5 bg-slate-100 text-slate-700 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-slate-200 transition shrink-0">
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* REJECT REASON MODAL FOR ADMIN */}
      {rejectModal.show && (
        <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-slate-800 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-black">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase text-slate-900">Penolakan Hasil Survei</h3>
                  <p className="text-[10px] font-bold text-rose-600 uppercase">{rejectModal.customerName}</p>
                </div>
              </div>
              <button onClick={() => setRejectModal({ show: false, customerId: null, customerName: '' })} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleConfirmReject} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                  Tuliskan Alasan Penolakan / Catatan Perbaikan untuk Petugas
                </label>
                <textarea
                  rows="3"
                  value={rejectReasonText}
                  onChange={e => setRejectReasonText(e.target.value)}
                  placeholder="Contoh: Foto KWh meter buram / Nomor KWh meter tidak sesuai fisik / Titik lokasi berbeda terlalu jauh..."
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 text-xs font-bold outline-none bg-slate-50 focus:border-rose-500"
                  required
                ></textarea>
              </div>

              {/* Quick Template Chips */}
              <div className="space-y-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Opsi Cepat Alasan Penolakan:</span>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setRejectReasonText('Nomor kWh meter pada foto tidak sesuai dengan database')}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-rose-100 hover:text-rose-800 rounded-lg text-[10px] font-bold text-slate-600 transition"
                  >
                    Nomor KWh tidak sesuai
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejectReasonText('Foto kWh meter atau foto rumah gelap / buram / tidak jelas')}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-rose-100 hover:text-rose-800 rounded-lg text-[10px] font-bold text-slate-600 transition"
                  >
                    Foto buram/gelap
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejectReasonText('Titik lokasi GPS jauh berbeda dari lokasi DIL')}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-rose-100 hover:text-rose-800 rounded-lg text-[10px] font-bold text-slate-600 transition"
                  >
                    Lokasi GPS berbeda
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setRejectModal({ show: false, customerId: null, customerName: '' })}
                  className="py-3 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md transition"
                >
                  Kirim Penolakan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}